import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AUTH_EXPIRED_EVENT } from './services/qbaseClient';
import buildInfo from './build-info.json';
import Header from './components/Header';
import LoginPage from './components/LoginPage';
import SQLEditor from './components/SQLEditor';
import ResultGrid from './components/ResultGrid';
import SQLHistory from './components/SQLHistory';
import SchemaExplorer from './components/SchemaExplorer';
import ExportDataModal from './components/ExportDataModal';
import ImportDataModal from './components/ImportDataModal';
import SessionPicker from './components/SessionPicker';
import VerifySessionPopover from './components/VerifySessionPopover';
import AssistantPanel from './components/assistant/AssistantPanel';
import TuningCaseModal from './components/assistant/TuningCaseModal';
import TableAnalysisModal from './components/assistant/TableAnalysisModal';
import DesignGuideModal from './components/assistant/DesignGuideModal';
import { AI_ENABLED, IS_STANDALONE } from './config/features';
import { syncLocalConnections, startLocalSync } from './services/localConnections';
import { clearColumnCache } from './services/columnCache';
import OpsLogModal from './components/assistant/OpsLogModal';
import ExperienceModal from './components/experience/ExperienceModal';
import ExecMonitorModal from './components/ExecMonitorModal';
import SqlMonitorReportModal from './components/SqlMonitorReportModal';
import { lastActualPlan, lastSqlMonitorReport } from './services/execMonitor';
import { parseStatements } from './utils/sqlBinds';
import { statementAtCursor, stripSqlComments, statementLabel, dmlVerb } from './utils/statementAtCursor';
import { parseBookmarks } from './utils/sqlBookmarks';
import BookmarkExplorer from './components/BookmarkExplorer';
import ExplorerRail from './components/ExplorerRail';
import { getZoom, setZoom as setAppZoom, subscribe as subscribeZoom, initAppZoom, toCssPx } from './utils/appZoom';
import FileTabs, { ProjectPicker } from './components/FileTabs';
import GlobalSearchModal from './components/GlobalSearchModal';
// 편집기/결과 분할 — react-split-pane 은 웹의 화면 확대(CSS zoom)에서 끌기가 틀어져 자체 구현으로 바꿨다(utils/appZoom)
import VerticalSplit from './components/common/VerticalSplit';
import MainDisplayTabs from './components/MainDisplayTabs';
import StatusLine from './components/StatusLine';
import './App.css';
// 팝업 창 공통 모양(제목줄·닫기·버튼) — 창마다의 CSS 보다 뒤에 와도 이기도록 선택자를 세게 썼다(styles/appModal.css)
import './styles/appModal.css';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSchemasForConnection, refreshSchemasForConnection, setActiveConnectionIdForSchema } from './redux/actions/schemaActions';
import { loadUserCommentDefs, loadUserCommentAreas } from './redux/actions/userCommentActions';
import { initializeAuth, handleSessionExpired } from './redux/actions/authActions';
import SessionExpiredGate from './components/SessionExpiredGate';
import QbaseMaintenanceBanner from './components/QbaseMaintenanceBanner';
import { detectMaintenance, startMaintenanceWatch } from './services/qbaseMaintenance';
import { loadDbmsManifests } from './redux/actions/dbmsActions';
import { addToHistory, clearHistory, deleteHistoryItem } from './redux/reducers/historyReducer';
import { loadChallengeList, loadInvitedChallenges, removeInvitedChallenges } from './redux/actions/challengeActions';
import { createSession, closeSession, cancelSession, verifySession, fetchSessions } from './redux/actions/sessionActions';
import useSessionPolling from './hooks/useSessionPolling';
import useMonitoringAccess, { usesExpertMenus } from './hooks/useMonitoringAccess';
import dbQueries from './config/dbQueries'; // dbQueries 임포트 (이미 되어있을 수 있음)
import { toast, ToastContainer } from 'react-toastify'; // Toast 알림을 위해 추가
import 'react-toastify/dist/ReactToastify.css'; // Toast 알림 CSS 추가
// import 'react-split-pane/dist/themes/default.css'; // 이전 주석 유지
//import { выброшена_фича_disable_autoflush_cache as unstable_batchedUpdates } from 'react-dom'; // 경로 확인 필요, 또는 그냥 React.unstable_batchedUpdates 사용
import BindVariablesModal from './components/BindVariablesModal'; // 모달 컴포넌트 임포트
import QueryChallenge from './components/QueryChallenge';
import HistoryAndChallengeTabs from './components/HistoryAndChallengeTabs';
import ChallengeWindow from './components/quiz/ChallengeWindow';
import { FiExternalLink, FiSearch, FiChevronDown, FiChevronUp, FiChevronLeft, FiMoreHorizontal, FiX, FiRotateCcw, FiRotateCw, FiCopy, FiScissors, FiClipboard, FiAlignLeft, FiHash, FiPlay, FiPlayCircle, FiClock, FiBarChart2, FiDatabase, FiDownload, FiUpload, FiCheckCircle, FiXCircle, FiMessageSquare, FiAward, FiPieChart, FiZap } from 'react-icons/fi';
import useAutoSave from './hooks/useAutoSave'; // Auto-save hook 추가
import PasswordChangeModal from './components/PasswordChangeModal';
import ERDModal from './components/ERDModal';
import ConnectionModal from './components/ConnectionModal';
import BuiltinConnectionDialog from './components/BuiltinConnectionDialog';
import DetachablePanel from './components/DetachablePanel';
import SplitView from './components/SplitView';
import { useConfirm } from './components/ConfirmDialog';
import { usePrompt } from './components/PromptDialog';
import { parseMultiSQLWithBindVariables, flattenEditorBinds } from './utils/sqlTuningParser';
import { parseXplanText, buildPlanToSqlMapping } from './utils/xplanParser';
import SettingsModal from './components/SettingsModal';
import { loadSection, saveSection, cursorGapPx } from './utils/appSettings';
import { loadLayout, saveLayout } from './utils/workspaceLayout';
import { isFixedAction, loadActionGroup, loadHiddenFixed, saveFavorites, loadActionGroupOpen, saveActionGroupOpen } from './utils/actionFavorites';
import { openReplacePanel } from './utils/editorSearch';
import { useMenuActions, runMenuAction } from './utils/menuActionBus';
import EditorToolbarRight, { EditorOptionChecks, TxnChip, ResultLayoutChecks } from './components/EditorToolbarRight';
import { nextTabNumber, freeTabNumber, uniqueTabId, repairTabIds } from './utils/tabIds';
import { planObjectsOf } from './utils/planObjects';
import { serializeProject as serializeProjectFile, parseProjectFile as parseProjectFileText, PROJECT_EXT, PROJECT_EXTS, isProjectFileName } from './utils/projectFile';
import { saveTextFile, canWriteDisk, baseNameOf, rememberOpenedFile } from './utils/desktopFile';
import { analyzeStatements } from './utils/sqlSafety';
import { isProduction, getConnType } from './utils/connectionTypes';
import { formatApiError } from './utils/formatters';
import { sessionDisplayName, isSessionBusy } from './utils/sessionDisplay';
import { projectSessionOf, bindSessionToProject } from './utils/projectSession';
import { reorderTabs } from './utils/tabOrder';
import { qualifiedNameAtCursor } from './utils/sqlIdentifier';
import { pushPlanTabs, planTabToItems } from './utils/planTabs';
import { serializePlanArchive, planArchiveToMarkdown } from './utils/planArchiveFile';
import { savePlanArchive } from './services/planArchive';
import PlanArchiveModal from './components/PlanArchiveModal';
import SqlTuningModal from './components/sqlTuning/SqlTuningModal';
import * as sqlTuningApi from './services/sqlTuning';
import SqlReviewModal from './components/sqlReview/SqlReviewModal';
import SqlBuilderModal from './components/sqlBuilder/SqlBuilderModal';
import DataAnalysisModal from './components/dataAnalysis/DataAnalysisModal';
import SqlAuthorModal from './components/sqlAuthor/SqlAuthorModal';
import SqlQualityModal from './components/sqlQuality/SqlQualityModal';
import SchemaCompareModal from './components/schemaCompare/SchemaCompareModal';
import SqlCollectModal from './components/sqlCollect/SqlCollectModal';
import GuardrailHost from './components/guardrail/GuardrailHost';
import { useWorkspaceDock, WorkspaceTabs, WorkspaceDockArea, SQL_TAB } from './components/workspace/WorkspaceDock';
import { useAgentBridge } from './utils/agentBridge';
import { observe, installToastObserver, useObservations } from './utils/agentObserver';
import { planArchiveCaseDraft } from './services/assistantClient';

// DML 의 처리 건수 — 「3건 UPDATE 되었습니다」(신고 2026-11-03: 몇 건이 반영됐는지 안 보였다). DML 이 아니거나 건수를 모르면 null
const affectedText = (sql, n, t) => {
    const verb = dmlVerb(sql);
    return verb && typeof n === 'number' ? t('common:run.rowsAffected', { count: n.toLocaleString('en-US'), verb }) : null;
};

/** 다시 열 수 있게 기억해 두는 닫은 탭 묶음 수(메모리에만) */
const MAX_CLOSED_TABS = 20;

/**
 * 바인드 값에 사람이 고른 형식을 입힌다(2026-10-26) — 고르지 않은 값은 글자(VARCHAR2) 그대로다. 자동으로 숫자로 바꾸지 않는다.
 * types 는 탭의 bindTypes({ statement_N: { 변수: 'NUMBER' } }) — 같은 변수는 어느 문장에서 골랐든 따른다.
 */
function typedBinds(flat, types) {
    const out = { ...(flat || {}) };
    for (const stmtTypes of Object.values(types || {})) {
        for (const [name, ty] of Object.entries(stmtTypes || {})) {
            const v = out[name];
            if (ty === 'NUMBER' && typeof v === 'string' && /^\s*-?\d+(\.\d+)?\s*$/.test(v)) out[name] = Number(v);
        }
    }
    return out;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/sql-developer';

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',  // 캐시 비활성화
        'Pragma': 'no-cache',  // HTTP/1.0 캐시 비활성화
        'Expires': '0'  // 만료 시간 0
    },
    // 기본 30초 — 목록·메타데이터처럼 **금방 끝나야 정상인** 요청용이다.
    // 쿼리 실행은 여기 해당하지 않는다. 아래 NO_TIMEOUT 참조.
    timeout: 30000,
    validateStatus: function (status) {
        return status < 500; // 500 미만은 정상으로 처리 (4xx도 처리 가능하도록)
    }
});

/**
 * 쿼리 실행·조회 요청에 붙인다 — **시간 제한을 두지 않는다** (사용자 결정 2026-09-18).
 *
 * 오래 걸리는 쿼리는 잘못된 것이 아니라 그냥 오래 걸리는 것이다. 30초에 끊으면 화면만 포기하고
 * **DB 에서는 계속 돌기 때문에**, 사용자는 결과도 못 받고 부하는 부하대로 남는다(게다가 다시 누른다).
 * 대신 **중지 버튼**이 진짜로 끊는다 — `handleStopQuery` 가 세션에 `break` 를 보낸다.
 * 끊을 수단이 없으면 제한시간을 없애면 안 된다. 그 둘은 한 벌이다.
 */
const NO_TIMEOUT = { timeout: 0 };

// 에이전트 상시 관찰의 기준(utils/agentObserver) — 느린 쿼리 10초 · 오래 도는 쿼리 30초 · 미커밋 5분
const OBS_SLOW_MS = 10000;
const OBS_LONG_RUNNING_MS = 30000;
const OBS_UNCOMMITTED_MS = 5 * 60 * 1000;
// 앱 전체의 오류·경고 알림을 관찰로도 남긴다(한 번만 감싼다)
installToastObserver(toast);

const App = () => {
    const { t } = useTranslation();
    // 작업 탭 — 편집기 자리를 함께 쓰는 화면들(components/workspace/WorkspaceDock). 앞에 온 것이 SQL 편집일 때만 편집기·툴바·패널을 보인다
    const workspace = useWorkspaceDock();
    const sqlTabActive = !workspace || workspace.activeId === SQL_TAB;
    // SQL 을 실행하는 화면(SQL 작성)은 아래에 공용 결과 영역을 함께 보인다(2026-10-19) — 결과 탭은 SQL 편집의 것과 같은 것이다
    const activeDockView = !sqlTabActive ? workspace.views.find(v => v.id === workspace.activeId) : null;
    const [dockSplitSize, setDockSplitSize] = useState(null);
    // 편집기 자리를 대신하던 화면을 새 창으로 분리했는가(2026-10-19) — 분리한 동안에는 그 자리에 원래 화면(편집기·결과)이 돌아온다
    const [poppedPane] = useState({});   // 편집기 자리를 대신하는 화면이 없어졌다(챌린지 → 작업 탭, 2026-11-04) — 읽는 곳만 남았다
    // 편집기 자리를 대신하는 화면(쿼리 챌린지)을 **닫지 않고** 잠시 걷어 둔 상태(2026-10-26 신고 —
    // 「SQL 편집」 을 누르면 화면이 통째로 닫혀 사라졌다). 그 화면의 단추를 다시 누르거나 다시 열면 돌아온다
    const [coverHidden, setCoverHidden] = useState(false);
    // 작업 화면 배치 — 새로고침 전의 화면으로 이어 간다(utils/workspaceLayout 이 무엇을 복원하는지 정한다, 2026-10-05)
    const [savedLayout] = useState(() => loadLayout());
    const [queryResultTabs, setQueryResultTabs] = useState([]);
    const [activeQueryResultTabId, setActiveQueryResultTabId] = useState(null);
    const [nextQueryResultTabNum, setNextQueryResultTabNum] = useState(1);
    const [executionPlanTabs, setExecutionPlanTabs] = useState([]);
    const [activeExecutionPlanTabId, setActiveExecutionPlanTabId] = useState(null);
    const [nextExecutionPlanTabNum, setNextExecutionPlanTabNum] = useState(1);
    // Redux에서 history 가져오기
    const history = useSelector(state => state.history.queries);
    const [isSidePanelOpen, setIsSidePanelOpen] = useState(() => savedLayout.sidePanelOpen);
    const [activeConnectionName, setActiveConnectionName] = useState('');
    const [activeConnectionId, setActiveConnectionId] = useState(null);
    const activeConnectionIdRef = useRef(null);
    const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
    // 편집 버튼으로 모달을 열 때 자동으로 선택될 connection id (null = 자동 선택 없음)
    const [editConnectionId, setEditConnectionId] = useState(null);
    const [builtinEdit, setBuiltinEdit] = useState(null);   // 내장 저장소 연결 편집(이름·설명·읽기 전용) — 전체 연결 창 대신
    const [editorHeight, setEditorHeight] = useState('60%');
    // SplitPane 영역 접기/펼치기 — 'none' 양쪽 표시, 'top' 에디터 접기, 'bottom' 결과 접기.
    const [splitCollapseMode, setSplitCollapseMode] = useState(() => savedLayout.collapseMode);
    // 사용자가 드래그한 splitSize (px). 접기 모드 해제 시 이 값 복원.
    const [splitSize, setSplitSize] = useState(() => savedLayout.splitSize);
    const [activeMainTab, setActiveMainTab] = useState('queryResults');
    const [isEditorDetached, setIsEditorDetached] = useState(false);
    const [isResultDetached, setIsResultDetached] = useState(false);
    const [isEditorSplit, setIsEditorSplit] = useState(() => savedLayout.editorSplit);
    const [isResultSplit, setIsResultSplit] = useState(() => savedLayout.resultSplit);
    const [activePlanNodeId, setActivePlanNodeId] = useState(null);
    const [editorHighlightRanges, setEditorHighlightRanges] = useState([]);
    const [cloneHighlightRanges, setCloneHighlightRanges] = useState([]);
    /** 실행계획 ↔ SQL 연결 표시를 걷는다. 지우는 곳이 여럿이라 한 함수로 둔다. */
    const clearPlanHighlight = useCallback(() => {
        setActivePlanNodeId(null);
        setEditorHighlightRanges([]);
        setCloneHighlightRanges([]);
    }, []);
    // 화면 확대/축소(%) — 앱 전체 한 배율. 값·적용·Ctrl+휠·단축키는 utils/appZoom.js 한 곳, 여기는 헤더 표시용 거울
    const [screenZoom, setScreenZoom] = useState(() => getZoom());
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('livesql_theme') !== 'light');
    const [zoneZoom, setZoneZoom] = useState(() => loadSection('zoneZoom'));
    const [editorSearch, setEditorSearch] = useState({ open: true, text: '', matchIndex: -1, matchCount: 0 });
    const editorSearchInputRef = useRef(null); // 툴바 검색 창 — 이미 열려 있어도 Ctrl+F 로 다시 포커스한다
    const [editorOptions, setEditorOptions] = useState(() => loadSection('editorOptions'));
    // 통합 탭 헤더에서 종류별 표시 필터 (모두 기본 ON).
    const [tabKindFilter, setTabKindFilter] = useState(() => loadSection('tabKindFilter'));
    // 결과 그리드 표시 포맷 / 실행 안전장치 (Phase 2)
    const [gridFormat, setGridFormat] = useState(() => loadSection('gridFormat'));
    const [execSettings, setExecSettings] = useState(() => loadSection('execution'));
    // 실행계획 요청이 읽는 최신 값 — 콜백 의존성에 넣으면 설정을 바꿀 때마다 계획 핸들러가 다시 만들어진다
    const execSettingsRef = useRef(execSettings);
    execSettingsRef.current = execSettings;
    const [sqlFormat, setSqlFormat] = useState(() => loadSection('sqlFormat'));
    const [assistantSettings, setAssistantSettings] = useState(() => loadSection('assistant'));
    // 설정 모달
    const [showSettings, setShowSettings] = useState(false);
    // 설정을 **어느 항목으로** 열지. 부르는 쪽이 문자열을 주면 그 항목으로 연다 —
    // onClick 핸들러로 직접 물리면 이벤트 객체가 들어오므로 문자열만 받는다.
    const [settingsCategory, setSettingsCategory] = useState(null);
    const openSettings = useCallback((cat) => {
        setSettingsCategory(typeof cat === 'string' ? cat : null);
        setShowSettings(true);
        // 설정은 작업 탭이다(2026-11-04) — 이미 열려 있으면 open 이 바뀌지 않으니 탭을 앞으로 부른다(제목 = 메뉴 문구)
        if (workspace) setTimeout(() => workspace.activateByTitle(t('menu:file.appSettings')), 0);
    }, [workspace, t]);

    // 에디터 툴바 액션 그룹에 고정될 액션 key 목록 (localStorage 영속화)
    // 'separator'는 구분선. 등록된 action만 표시됨 (ACTION_REGISTRY 기준)
    const DEFAULT_PINNED_ACTIONS = [
        // 컬럼 선택 모드는 오른편 체크박스와 겹쳐 기본값에서 뺐다(docs/ui-command-placement.md) — 설정 › 도구 모음에서 다시 넣을 수 있다
        'undo', 'redo', 'separator',
        'copy', 'cut', 'paste', 'separator',
        'executecurrent', 'executeall', 'runplan', 'separator',
        'format', 'uppercase', 'lowercase', 'toggleComment'
    ];
    // 메뉴 즐겨찾기 — 저장·이관은 utils/actionFavorites.js 한 곳. (옛 livesql_pinnedActions 를 한 번 읽어 옮긴다)
    const [pinnedActions, setPinnedActions] = useState(() => loadActionGroup(DEFAULT_PINNED_ACTIONS));
    // 원래 화면에 있는 버튼 중 사용자가 끈 것 (탭 바 +, 탐색기 「연결」)
    const [hiddenFixedActions, setHiddenFixedActions] = useState(() => loadHiddenFixed());
    // 툴바 아이콘 묶음은 기본 접힘(2026-11-04 사용자 요청) — 펼친 것만 기억한다
    const [actionGroupOpen, setActionGroupOpen] = useState(() => loadActionGroupOpen());
    const toggleActionGroupOpen = useCallback(() => setActionGroupOpen(p => { saveActionGroupOpen(!p); return !p; }), []);
    useEffect(() => {
        saveFavorites(pinnedActions, hiddenFixedActions);
    }, [pinnedActions, hiddenFixedActions]);
    // 메뉴에만 있는 기능의 표시명·아이콘 — Header 가 게시한다(utils/menuActionBus.js)
    const menuActions = useMenuActions();

    // 메뉴 아이콘 클릭 시 액션 그룹 토글 (핀/해제)
    // DEFAULT_PINNED_ACTIONS 의 순서(= canonical order)를 기준으로 재구성 →
    // 아이콘이 항상 자연스러운 위치에 삽입/정렬되고 그룹 구분선도 자동 정돈
    const handleToggleActionPin = useCallback((actionKey) => {
        if (!actionKey || actionKey === 'separator') return;
        setPinnedActions(prev => {
            const existing = new Set(prev.filter(x => x !== 'separator'));
            if (existing.has(actionKey)) existing.delete(actionKey);
            else existing.add(actionKey);

            // canonical 순서로 재구성. 그룹 경계에서만 separator 추가
            const result = [];
            let lastWasSep = true; // 앞에 연속 separator 방지
            for (const item of DEFAULT_PINNED_ACTIONS) {
                if (item === 'separator') {
                    if (!lastWasSep && result.length > 0) {
                        result.push('separator');
                        lastWasSep = true;
                    }
                } else if (existing.has(item)) {
                    result.push(item);
                    existing.delete(item);
                    lastWasSep = false;
                }
            }
            if (result[result.length - 1] === 'separator') result.pop();

            // canonical 에 없는 액션은 끝에 별도 그룹으로 추가
            if (existing.size > 0) {
                if (result.length > 0) result.push('separator');
                result.push(...existing);
            }
            return result;
        });
    // DEFAULT_PINNED_ACTIONS 는 컴포넌트 렌더마다 재생성되지만 내용 불변이라 deps 생략
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 메뉴 아이콘 = 즐겨찾기 토글. 화면에 원래 버튼이 있으면 보이기/숨기기, 없으면 툴바에 붙이기/떼기.
    const handleToggleFavorite = useCallback((actionKey) => {
        if (!actionKey || actionKey === 'separator') return;
        if (isFixedAction(actionKey)) {
            setHiddenFixedActions(prev => (prev.includes(actionKey)
                ? prev.filter(x => x !== actionKey)
                : [...prev, actionKey]));
        } else {
            handleToggleActionPin(actionKey);
        }
    }, [handleToggleActionPin]);
    const isFavoriteAction = useCallback((actionKey) => (isFixedAction(actionKey)
        ? !hiddenFixedActions.includes(actionKey)
        : pinnedActions.includes(actionKey)), [hiddenFixedActions, pinnedActions]);
    // 설정 › 기본값 복원 — 즐겨찾기도 함께 되돌린다
    const handleResetFavorites = useCallback(() => {
        setPinnedActions(DEFAULT_PINNED_ACTIONS);
        setHiddenFixedActions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    // Auto-save hook 사용
    const { restoreFromStorage, clearSavedData } = useAutoSave(null, 'sql_editor_files', 2000);
    // **프로젝트도 함께 복원한다.** 탭만 살리고 프로젝트 목록을 비워 두면 `projectId` 가 붙은
    // 탭이 **어느 목록에도 안 보이는 미아**가 된다 — 새로고침하면 프로젝트가 사라진 것처럼
    // 보였다(실제 신고 2026-09-21, docs/schema-compat.md 가 미적용으로 적어 둔 항목).
    const { restoreFromStorage: restoreProjectState } = useAutoSave(null, 'sql_editor_projects', 2000);
    // **처음 한 번만 읽는다**(2026-10-26 — 탭을 누를 때 멈칫하던 원인). 렌더마다 부르면 저장소의 JSON 을 매번 풀고
    // 콘솔에 한 줄씩 남겼다(오래 켜 둔 세션에서 20만 번). 쓰는 곳이 전부 초깃값이라 그 뒤로 다시 읽을 까닭이 없다
    const [savedProjectState] = useState(() => restoreProjectState());
    const savedProjects = (savedProjectState && Array.isArray(savedProjectState.projects))
        ? savedProjectState.projects : [];
    
    // Tab-based file management state - 초기값으로 기본 파일 설정
    const [openFiles, setOpenFiles] = useState(() => {
        // 앱 시작 시 저장된 파일들 복원 시도
        const savedFiles = restoreFromStorage();
        if (savedFiles && savedFiles.length > 0) {
            console.log('Restored editor files from localStorage');
            // sessionId 는 backend 휘발 자원 — 페이지 reload 후 무효 가능성 높음.
            // localStorage 에 stale 값이 남아 SessionPicker 와 실제 binding 이 어긋나는
            // 케이스를 막기 위해 복원 시 항상 null 로 초기화. 사용자는 다시 SessionPicker
            // 로 명시 바인딩해야 함 (의도적 UX).
            // 속할 프로젝트가 사라진 탭은 **기본 작업공간으로 돌려보낸다.** 그대로 두면
            // 어느 목록에도 안 그려져 사용자가 잃은 줄 안다 — 탭을 잃는 쪽으로 틀리면 안 된다.
            const liveIds = new Set(savedProjects.map(p => p.id));
            // 같은 id 의 탭이 저장돼 있으면 고친다 — 하나를 닫을 때 둘 다 닫히고 탭 줄에 지워지지 않는 탭이 남는다(utils/tabIds.js)
            return repairTabIds(savedFiles.map(f => ({
                ...f,
                sessionId: null,
                projectId: (f.projectId && liveIds.has(f.projectId)) ? f.projectId : undefined,
            })));
        }
        // 저장된 것이 없으면 기본 파일 생성
        return [{
            id: 'new_1',
            name: t('common:file.defaultFirst'),
            content: '',
            isDirty: false,
            path: null,
            bindVariables: {}, // 각 파일별 바인드 변수 저장
            sessionId: null,
        }];
    });
    const [activeFileId, setActiveFileId] = useState(() => {
        const savedFiles = restoreFromStorage();
        if (!(savedFiles && savedFiles.length > 0)) return 'new_1';
        // 새로고침 전에 보던 탭 — 있고 **지금 프로젝트의 탭**일 때만. 예전엔 늘 첫 탭으로 돌아가, 복원된 프로젝트와
        // 다른 프로젝트의 탭이 활성으로 잡힐 수도 있었다
        const pid = (savedProjectState && savedProjectState.activeProjectId) || null;
        const inProject = (f) => (f.projectId || null) === pid;
        const prev = savedFiles.find(f => f.id === savedLayout.activeFileId && inProject(f));
        return (prev || savedFiles.find(inProject) || savedFiles[0]).id;
    });
    // 새 탭 번호 — **복원된 탭 다음부터** 센다. 늘 2 부터 세면 앱을 다시 켠 뒤의 새 탭이 복원된 탭(다른 프로젝트의 것 포함)과
    // 같은 id 를 받는다(스탠드얼론 신고 2026-10-12: 탭 하나를 닫으면 프로젝트의 탭이 함께 닫혔다)
    const [nextFileId, setNextFileId] = useState(() => nextTabNumber(openFiles));
    // 콜백이 **지금의** 탭 목록에서 빈 번호를 고르게 한다(의존성으로 받으면 탭이 바뀔 때마다 콜백이 새로 만들어진다)
    const openFilesRef = useRef(openFiles);
    openFilesRef.current = openFiles;
    
    // Auto-save hook으로 파일 상태 자동 저장
    useAutoSave(openFiles, 'sql_editor_files', 2000);

    const nextQueryResultTabIdCounterRef = useRef(1);
    const nextExecutionPlanTabIdCounterRef = useRef(1);

    // 파일 입력을 위한 ref
    const fileInputRef = useRef(null);

    // activeFile을 ref로 관리하여 콜백 안정성 확보
    const activeFileRef = useRef(null);
    const nextFileIdRef = useRef(nextFileId);

    // 프로젝트(스크립트 그룹) 다중 관리 — Orange 의 .ogp 개념 확장
    // projects: [{id, name}] — 현재 열린 프로젝트 목록
    // activeProjectId: string | null   (null → "기본" 작업공간, projectId 가 없는 탭 집합)
    // openFiles 의 각 항목은 optional `projectId` 로 어느 프로젝트에 속하는지 태깅됨.
    //   projectId 미지정(undefined/null) → 기본 작업공간
    const [projects, setProjects] = useState(savedProjects);
    const [activeProjectId, setActiveProjectId] = useState(() => {
        const id = savedProjectState && savedProjectState.activeProjectId;
        // 저장된 활성 프로젝트가 목록에 없으면 기본 작업공간으로 — 빈 화면을 보여 주지 않는다
        return (id && savedProjects.some(p => p.id === id)) ? id : null;
    });
    // 한 객체로 묶어 저장한다. `activeProjectId` 만 따로 두면 null 일 때 저장을 건너뛴다
    // (useAutoSave 가 falsy 를 거른다) — 기본 작업공간으로 돌아간 것이 기억되지 않는다.
    const projectState = useMemo(() => ({ projects, activeProjectId }), [projects, activeProjectId]);
    const activeProjectIdRef = useRef(activeProjectId);
    activeProjectIdRef.current = activeProjectId;
    useAutoSave(projectState, 'sql_editor_projects', 2000);

    // 전역 검색 모달
    const [showGlobalSearch, setShowGlobalSearch] = useState(false);

    const dispatch = useDispatch();
    const confirm = useConfirm();
    const prompt = usePrompt();
    // 닫은 탭(다시 열기용, 2026-10-26) — 메모리에만. 한 번 닫은 묶음이 한 항목이다
    const closedTabsRef = useRef([]);
    // 머리줄에 넘기는 동작 키 목록 — 내용이 같으면 같은 배열(Header 가 memoWindow 다)
    const actionKeysRef = useRef([]);
    const [closedTabCount, setClosedTabCount] = useState(0);
    const reopenClosedRef = useRef(null);
    const connectionsFromStore = useSelector(state => state.schema.connections);
    const tableComments = useSelector(state => state.schema.tableComments) || {};
    const columnComments = useSelector(state => state.schema.columnComments) || {};
    const { isAuthenticated, user, isTemporaryPassword, sessionExpired } = useSelector(state => state.auth);
    // 사용자 유형(2026-10-29) — beginner 는 DB 전문가 기능(튜닝 사례·테이블 분석 단추 · 실행계획의 보관함 저장·진단 리포트·AI 튜닝)을 감춘다.
    // 서버도 같은 판정으로 막는다(backend modules/userGrade). 메뉴는 Header 가 같은 훅으로 감춘다
    const userAccess = useMonitoringAccess(IS_STANDALONE || isAuthenticated, IS_STANDALONE);
    const expertMenus = usesExpertMenus(userAccess);

    // Phase 2: 세션 폴링 — 인증 후에만 실행
    // 스탠드얼론은 로그인을 건너뛰어 isAuthenticated 가 영원히 false 다 — 이 줄이
    // IS_STANDALONE 을 빠뜨리면 세션 폴링이 아예 돌지 않아, 세션이 서버에 생성돼도
    // 트리가 영영 Sessions(0) 이다 (= "스탠드얼론에서 세션 추가 안 됨" 의 정체).
    useSessionPolling(isAuthenticated || IS_STANDALONE);
    const sessionsList = useSelector(state => (state.sessions && state.sessions.list) || []);
    // Phase A: DBMS manifest (capability flag 분기용). 시작 시 1회 fetch (loadDbmsManifests).
    const dbmsByType = useSelector(state => (state.dbms && state.dbms.byType) || {});

    // 탐색기 트리 데이터 — connections + sessions 합성. 각 연결 노드 children 을
    // [Sessions 폴더, Schemas 폴더] 의 2개 가상 폴더로 감싼다.
    const explorerTreeData = useMemo(() => {
        const conns = Array.isArray(connectionsFromStore) ? connectionsFromStore : [];
        return conns.map(conn => {
            const sessions = sessionsList.filter(s => s.connectionId === conn.id);
            const sessionNodes = sessions.map(s => ({
                id: `session:${s.sessionId}`,
                type: 'session',
                name: sessionDisplayName(s, t),
                ...s,
            }));
            return {
                ...conn,
                children: [
                    {
                        id: `sessions-folder:${conn.id}`,
                        type: 'sessions-folder',
                        name: 'Sessions',
                        connectionId: conn.id,
                        // 세션 추가는 폴더 라벨 옆 + 아이콘으로 이동 — 자식엔 실제 세션만.
                        // 빈 배열로 두면 react-arborist 가 expandable folder 로 표시 (펼쳐도 비어있음).
                        children: sessionNodes,
                    },
                    {
                        id: `schemas-folder:${conn.id}`,
                        type: 'schemas-folder',
                        name: 'Schemas',
                        connectionId: conn.id,
                        children: conn.children || null,
                        isLoadingSchemas: conn.isLoadingSchemas,
                        schemasError: conn.schemasError,
                    },
                ],
            };
        });
    }, [connectionsFromStore, sessionsList]);

    // Phase 2: 세션 액션 핸들러
    // createSession thunk 는 try/catch 가 없어 실패 시 그대로 reject 된다 — 호출부인
    // SchemaExplorer 의 "+" 아이콘 클릭 핸들러도 catch 를 안 걸어서, 실패해도 아무 표시
    // 없이 조용히 사라지는 문제(unhandled rejection)가 있었다. 여기서 명시적으로 잡아
    // 토스트로 알린다 — 특히 ORA-12637(Packet receive failed) 같은 네트워크 순단은
    // 재시도하면 되는 경우가 많은데, 실패가 안 보이면 사용자는 "기능이 안 된다"로 오인한다.
    const handleCreateSession = useCallback(async (connectionId) => {
        try {
            return await dispatch(createSession(connectionId, 'user'));
        } catch (err) {
            console.warn(`[App] 세션 생성 실패 (conn=${connectionId}):`, err.message);
            toast.error(t('common:session.createFailed', { message: formatApiError(err, t) }));
            return null;
        }
    }, [dispatch, t]);
    const handleCancelSession = useCallback(async (session) => {
        if (!session?.sessionId) return;
        try {
            const result = await dispatch(cancelSession(session.sessionId));
            if (result && result.supported === false) {
                console.warn(`[App] cancel 미지원: ${result.reason}`);
                toast.info(t('common:session.cancelUnsupported', { reason: result.reason }));
            } else if (result && result.forced) {
                // break() 가 timeout — 세션을 강제 종료. DB 측엔 잔존 가능 (DBA KILL SESSION 권장).
                console.warn(`[App] 세션 강제 종료: ${result.reason}`);
                toast.warning(t('common:session.forceClosed'));
            }
        } catch (err) {
            console.warn(`[App] cancel 실패: ${err.message}`);
            toast.error(t('common:session.cancelFailed', { message: err.message }));
        }
    }, [dispatch]);
    const handleCloseSession = useCallback(async (session) => {
        if (!session?.sessionId) return;
        // 미커밋 변경이 있는 세션을 닫으면 롤백된다 — 묻지 않고 닫으면 "커밋한 줄 알았는데 사라졌다" 가 된다
        if (session.uncommitted) {
            const ok = await confirm({
                title: t('common:sess.closeUncommittedTitle'),
                message: t('common:sess.closeUncommittedMessage'),
                confirmText: t('common:sess.closeAndRollback'),
                cancelText: t('common:ui.cancel'),
                variant: 'danger',
            });
            if (!ok) return;
        }
        try {
            await dispatch(closeSession(session.sessionId));
        } catch (err) {
            console.warn(`[App] close 실패: ${err.message}`);
        }
    }, [dispatch, confirm, t]);

    // 데이터 분석 모드 — 에디터+결과 영역 우측 패널 토글
    const [analysisMode, setAnalysisMode] = useState(() => savedLayout.analysisMode);
    // AI 에이전트 — 같은 우측 레일의 대화 패널. 데이터 분석 패널과 **함께 열린다** — 에이전트가 늘 맨 오른쪽이고
    // 분석 패널은 닫히지 않고 그 왼쪽으로 밀린다(사용자 요청 2026-09-13).
    const [assistantOpen, setAssistantOpen] = useState(() => AI_ENABLED && savedLayout.assistantOpen);
    // 메뉴·도움말 창에서 넘어온 질문 — 패널을 열고 입력란에 채우기만 한다 (nonce 로 같은 글도 다시 채운다)
    const [assistantPrefill, setAssistantPrefill] = useState(null);
    /**
     * AI 에이전트의 **도구 창 3종** — 튜닝 사례 · 사용·에러 로그 · 테이블 분석.
     * 튜닝 사례·테이블 분석은 편집기 툴바(레이블 포함)와 메뉴 도구 ›, 사용·에러 로그는 메뉴 도움말 › 에서 연다(2026-09-13).
     *
     * 버튼은 편집기 도구모음(「AI 에이전트」 토글 옆)에 있고 창은 여기서 그린다. 패널 안에
     * 두면 **패널이 닫힐 때 창도 같이 사라진다**(AssistantPanel 은 닫히면 null 을 낸다) —
     * 세 화면 다 대화 없이 혼자 쓰는 것이라 패널에 매여 있을 이유가 없다.
     */
    // 튜닝 사례·SQL 튜닝·SQL 진단 리포트 창은 새로고침해도 열려 있던 대로, 보던 사례·작업·단계로 연다(utils/workspaceLayout 의 windows)
    const [caseListOpen, setCaseListOpen] = useState(() => AI_ENABLED && savedLayout.windows.cases.open);
    const [winView, setWinView] = useState(() => savedLayout.windows);
    const reportWinView = useCallback((key, view) => setWinView((prev) => {
        const cur = prev[key] || {};
        const next = { ...cur, ...view };
        return (cur.id === next.id && cur.step === next.step) ? prev : { ...prev, [key]: next };
    }), []);
    const onCasesView = useCallback(v => reportWinView('cases', v), [reportWinView]);
    const onSqlTuningView = useCallback(v => reportWinView('sqlTuning', v), [reportWinView]);
    const onSqlReviewView = useCallback(v => reportWinView('sqlReview', v), [reportWinView]);
    const onSqlAuthorView = useCallback(v => reportWinView('sqlAuthor', v), [reportWinView]);
    const onSqlQualityView = useCallback(v => reportWinView('sqlQuality', v), [reportWinView]);
    const onSchemaCompareView = useCallback(v => reportWinView('schemaCompare', v), [reportWinView]);
    const onSqlCollectView = useCallback(v => reportWinView('sqlCollect', v), [reportWinView]);
    const [planArchiveOpen, setPlanArchiveOpen] = useState(false);
    // SQL 튜닝 창 — AI 무관하게 열린다(AI 버튼만 AI_ENABLED). docs/features/20-sql-tuning.md
    const [sqlTuningOpen, setSqlTuningOpen] = useState(() => savedLayout.windows.sqlTuning.open);
    // 채팅 에이전트가 가리킨 SQL 튜닝 작업(SSE sql_tuning) — open 이면 창을 그 작업으로 열고, 아니면 열린 창만 새로 읽는다
    const [sqlTuningFocus, setSqlTuningFocus] = useState(null);
    // SQL 진단 리포트 — DB 연결 없이 쿼리·실행계획 글만으로(docs/features/22-sql-review.md). seed = 실행계획 탭에서 넘긴 자료
    const [sqlReviewOpen, setSqlReviewOpen] = useState(() => savedLayout.windows.sqlReview.open);
    const [sqlReviewSeed, setSqlReviewSeed] = useState(null);
    // SQL 작성 — 요구 → 유사 쿼리 → 작성 → 실행(docs/features/26-sql-author.md). AI 무관하게 열린다(AI 단추만 AI_ENABLED)
    const [sqlAuthorOpen, setSqlAuthorOpen] = useState(() => savedLayout.windows.sqlAuthor.open);
    // SQL 품질 검사 — 기준 → 대상 → 기계 검사 → 결과·검토 → 리포트(docs/features/27-sql-quality.md). AI 무관하게 열린다(AI 판정만 AI_ENABLED)
    const [sqlQualityOpen, setSqlQualityOpen] = useState(() => savedLayout.windows.sqlQuality.open);
    // 스키마 비교 — 비교 대상 → 오브젝트 → 결과 → 동기화 스크립트(docs/features/28-schema-compare.md). AI 무관 · DB 를 바꾸지 않는다
    const [schemaCompareOpen, setSchemaCompareOpen] = useState(() => savedLayout.windows.schemaCompare.open);
    // SQL 수집 — 수집 → 클린징 → 변조 → 검증 → 저장·머지(docs/features/29-sql-collect.md). AI 무관. 세트는 품질 검사 등이 대상으로 쓴다
    const [sqlCollectOpen, setSqlCollectOpen] = useState(() => savedLayout.windows.sqlCollect.open);
    // 채팅 에이전트가 가리킨 작업(SSE open_screen, 2026-10-20 — sql_author_open · sql_quality_open · sql_review_create/open) — { id, step, itemId, nonce }
    const [sqlAuthorFocus, setSqlAuthorFocus] = useState(null);
    const [sqlQualityFocus, setSqlQualityFocus] = useState(null);
    const [sqlReviewFocus, setSqlReviewFocus] = useState(null);
    // SQL 빌더(2026-10-19) — 연 자리(편집기의 문장·선택 영역)를 기억해 두었다가 「편집기에 반영」 때 그 자리를 바꾼다
    const [sqlBuilderOpen, setSqlBuilderOpen] = useState(() => savedLayout.windows.sqlBuilder.open);
    const [sqlBuilderSeed, setSqlBuilderSeed] = useState(null);
    const sqlBuilderOrigin = useRef(null);
    // 실행계획 탭의 「AI 튜닝」(2026-10-26 — SQL 실행계획 분석 패널을 편집기 실행계획에 합쳤다) → 이 SQL 로 SQL 튜닝 작업을 만들고 창을 열어 「AI 튜닝 자동 진행」 확인 창까지 띄운다.
    // 연결은 편집기 연결(실행계획을 뜬 연결과 같다). 설정(범위·검증)은 확인 창에서 고르고, 실행은 사용자가 「시작」 을 눌러야 한다
    const editorConnRef = useRef(null);
    const startSqlTuningAuto = useCallback(async ({ sql, binds }) => {
        const conn = editorConnRef.current;
        if (!conn) { toast.warning(t('common:tun.autoTuneNoConn')); return; }
        if (conn.dbType && conn.dbType !== 'oracle') { toast.warning(t('common:tun.autoTuneOracleOnly')); return; }
        try {
            const r = await sqlTuningApi.createJob({ connectionId: conn.id, title: '', target: { sql, binds, source: 'manual', sqlId: null } });
            setSqlTuningOpen(true);
            setSqlTuningFocus({ jobId: String(r.job.id), step: 'target', open: true, autoRun: true, nonce: Date.now() });
        } catch (err) { toast.error(formatApiError(err, t)); }
    }, [t]);
    // useCallback 으로 고정 — 매 렌더 새 함수면 패널의 이벤트 탭이 다시 붙어 스트리밍 도중의 이벤트를 놓친다
    const handleSqlTuningEvent = useCallback((d) => {
        if (!d || !d.jobId) return;
        if (d.open) setSqlTuningOpen(true);
        setSqlTuningFocus({ jobId: String(d.jobId), step: d.step || null, open: !!d.open, nonce: Date.now() });
    }, []);
    // 테이블 분석 창 — 닫힘이면 null, 열리면 모드('analysis' | 'index' | 'partition', 2026-10-06 설계 두 갈래)
    // 새로고침 전에 열려 있었으면 그 탭으로 다시 연다 — 수집 결과는 저장하지 않는 것이라 비어 있다(다시 수집)
    const [tableAnalysisMode, setTableAnalysisMode] = useState(() => {
        const w = savedLayout.windows.tableAnalysis;
        // 옛 저장본의 'analysis'(분석 메뉴가 따로 있던 때)는 인덱스 설계 창으로 연다
        return AI_ENABLED && w.open ? (w.step === 'partition' ? 'partition' : 'index') : null;
    });
    const [designGuidesOpen, setDesignGuidesOpen] = useState(() => AI_ENABLED && savedLayout.windows.guides.open);
    // 가이드 창이 열릴 탭 — 메뉴는 설계(인덱스), 튜닝 사례·SQL 튜닝·SQL 진단 리포트의 「가이드 관리」 는 튜닝
    const [designGuideKind, setDesignGuideKind] = useState(() => (['index', 'partition', 'tuning', 'sqlStandard'].includes(savedLayout.windows.guides.step) ? savedLayout.windows.guides.step : 'index'));
    const onGuidesView = useCallback(v => reportWinView('guides', v), [reportWinView]);
    const openTuningGuides = useCallback(() => { setDesignGuideKind('tuning'); setDesignGuidesOpen(true); }, []);
    // SQL 품질 검사의 「가이드 관리」 — SQL 표준 가이드 탭으로 연다
    const openStandardGuides = useCallback(() => { setDesignGuideKind('sqlStandard'); setDesignGuidesOpen(true); }, []);
    /** 채팅이 테이블 분석을 열며 건넨 표 — { tables: ['OWNER.NAME'], nonce } */
    const [tableAnalysisPreset, setTableAnalysisPreset] = useState(null);
    const openTableAnalysis = useCallback((mode) => {
        setTableAnalysisMode(mode === 'partition' ? 'partition' : 'index');
    }, []);
    /**
     * 채팅 에이전트가 작업 창을 연다(SSE open_screen — backend assistant/tools/screenTools.js). 창의 동작은 그 창이 한다(가이드라인 원칙 6) —
     * 여기는 창을 열고 가리킨 작업으로 옮길 뿐이다. 이미 열린 창이면 그 작업 탭을 앞으로 부른다(열림 상태가 안 바뀌어 창이 스스로 앞으로 오지 못한다).
     * 고정 함수여야 한다 — 패널의 이벤트 탭이 다시 붙으면 스트리밍 도중에 온 이벤트를 놓친다(onAgentPlanCapture 와 같은 함정).
     */
    const handleOpenScreen = useCallback((d) => {
        if (!d || !d.screen) return;
        const nonce = Date.now();
        const front = (titleKey) => setTimeout(() => { if (workspace) { workspace.activate(SQL_TAB); workspace.activateByTitle(t(titleKey)); } }, 0);
        if (d.screen === 'sqlAuthor') { setSqlAuthorFocus({ id: d.jobId || null, step: d.step || undefined, nonce }); setSqlAuthorOpen(true); front('common:sau.windowTitle'); }
        else if (d.screen === 'sqlQuality') { setSqlQualityFocus({ id: d.jobId || null, itemId: d.itemId || undefined, nonce }); setSqlQualityOpen(true); front('common:sqq.windowTitle'); }
        else if (d.screen === 'sqlReview') { setSqlReviewFocus({ id: d.jobId || null, step: d.step || undefined, nonce }); setSqlReviewOpen(true); front('common:srv.windowTitle'); }
        else if (d.screen === 'tableAnalysis') {
            // 표 이름은 창이 목록에서 찾아 고른다(목록에 없는 이름은 고르지 않는다) — 여기서는 건네기만
            if (Array.isArray(d.tables) && d.tables.length) setTableAnalysisPreset({ tables: d.tables.map(String), nonce });
            openTableAnalysis(d.tab === 'partition' ? 'partition' : 'index');
            front(d.tab === 'partition' ? 'menu:tools.partitionDesign' : 'menu:tools.indexDesign');
        }
        // 메뉴 화면(2026-10-29, open_app_screen) — 메뉴를 누른 것과 같은 길(Header 의 handleMenuClick). 메뉴에 없는 항목(사용자 유형이 감춘 것)은 열리지 않는다
        else if (d.screen === 'menu' && d.action) runMenuAction(String(d.action));
        // 설정 창의 그 항목 — 사용자 주석 정의를 에이전트가 저장했으면 정의를 다시 받는다(user_comment_save)
        else if (d.screen === 'settings') {
            if (d.reloadUserComments) dispatch(loadUserCommentDefs());
            openSettings(typeof d.category === 'string' ? d.category : undefined);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- workspace 는 활성화 함수만 쓴다(고정 함수로 둔다)
    }, [openTableAnalysis, openSettings, dispatch, t]);
    const [opsLogOpen, setOpsLogOpen] = useState(false);
    const [experienceOpen, setExperienceOpen] = useState(false);
    // 오른쪽 패널 토글 — 툴바 버튼과 메뉴 보기 › 가 같은 함수를 쓴다. 둘은 **서로 닫지 않는다**:
    // 에이전트를 열어도 분석 패널은 왼쪽으로 밀릴 뿐이다(맨 오른쪽 자리는 AssistantPanel.module.css 의 order).
    /**
     * 데이터 분석 — SQL 빌더처럼 편집기 자리의 **작업 탭**이다(2026-10-28 사용자 요청 — 예전에는 오른쪽 패널이라 실행이력과 자리를 다퉜다).
     * 분석 SQL 을 만들어 편집기에 넣을 뿐 실행하지 않는다(components/dataAnalysis/DataAnalysisModal).
     */
    const toggleAnalysisMode = useCallback(() => { setAnalysisMode(prev => !prev); }, []);
    const openDataAnalysis = useCallback(() => { setAnalysisMode(true); }, []);
    const toggleAssistantPanel = useCallback(() => { setAssistantOpen(p => !p); }, []);
    // 접힌 에이전트의 상태 — 에이전트는 접혀도 작동하므로(2026-10-20) 단추가 「일하는 중」·「새 답」 을 말해 준다(utils/agentBridge)
    const agentLive = useAgentBridge();
    // 상시 관찰의 「새 관찰」 수 — 접힌 에이전트 단추에 숫자로(utils/agentObserver)
    const observations = useObservations();
    const [agentUnread, setAgentUnread] = useState(false);
    const busyWhileClosedRef = useRef(false);
    useEffect(() => {
        if (assistantOpen) { busyWhileClosedRef.current = false; setAgentUnread(false); return; }
        if (agentLive.busy) busyWhileClosedRef.current = true;
        else if (busyWhileClosedRef.current) { busyWhileClosedRef.current = false; setAgentUnread(true); }
    }, [assistantOpen, agentLive.busy]);
    // 접힌 동안 에이전트가 사람의 답(되묻기·실행 승인·연결 확인)을 기다리면 펼친다 — 안 보이면 시간 초과로 「실행 안 함」 이 된다
    useEffect(() => { if (agentLive.waiting && !assistantOpen) setAssistantOpen(true); }, [agentLive.waiting, assistantOpen]);

    /**
     * 왼쪽 탐색기 접기(2026-10-10) — 오른쪽 패널이 열리면 공간을 **탐색기에서 먼저** 얻는다. 규칙: utils/explorerLayout.js
     *  · explorerCollapsed — 사용자가 **직접** 접었는가(저장한다)
     *  · 자동 접힘 — 없앴다(2026-10-19, 아래). 그 설정(`layout.explorerAutoCollapse`)도 2026-10-29 에 걷어 냈다
     *  · explorerPeek — 자동으로 접힌 동안 사용자가 다시 펼쳤다. 패널을 새로 열거나 닫으면 풀린다
     */
    const [explorerCollapsed, setExplorerCollapsed] = useState(() => savedLayout.explorerCollapsed);
    const [explorerPeek, setExplorerPeek] = useState(false);
    const contentAreaRef = useRef(null);
    // 탐색기 자동 접기는 **AI 에이전트를 펼칠 때만**(2026-11-04 사용자 요청 — 10-19 의 「스스로 접히지 않는다」 를 이 경우에만 바꿨다).
    // 펼치는 순간(닫힘 → 열림)에만 접고, 앱을 열 때 이미 펼쳐져 있던 것은 건드리지 않는다. 에이전트를 닫으면 저절로 다시 펴진다
    // (자동 접힘은 저장하지 않는다 — 직접 접은 explorerCollapsed 와 다르다). 접힌 동안 사람이 펴면 explorerPeek 로 그대로 둔다.
    // 다른 오른쪽 패널(실행이력 등)에는 걸지 않는다. 예전의 설정 「패널을 열 때 탐색기 접기」 는 되살리지 않는다
    const [agentAutoFold, setAgentAutoFold] = useState(false);
    const prevAssistantOpenRef = useRef(assistantOpen);
    useEffect(() => {
        if (assistantOpen && !prevAssistantOpenRef.current) setAgentAutoFold(true);
        if (!assistantOpen) setAgentAutoFold(false);
        prevAssistantOpenRef.current = assistantOpen;
    }, [assistantOpen]);
    const explorerAutoCollapsed = agentAutoFold;
    const isExplorerCollapsed = explorerCollapsed || (explorerAutoCollapsed && !explorerPeek);
    // 패널을 열거나 닫을 때마다 「잠깐 펼침」 을 푼다 — 새로 연 패널은 다시 공간을 얻어야 한다
    useEffect(() => { setExplorerPeek(false); }, [isSidePanelOpen, analysisMode, assistantOpen]);
    const expandExplorer = useCallback(() => {
        setExplorerCollapsed(false);
        if (explorerAutoCollapsed) setExplorerPeek(true);
    }, [explorerAutoCollapsed]);
    const collapseExplorer = useCallback(() => {
        setExplorerPeek(false);
        // 에이전트가 접어 둔 동안 잠깐 편 것을 다시 접으면 자동 접힘으로 되돌릴 뿐이다 — 직접 접기로 저장하면
        // 에이전트를 닫아도 탐색기가 펴지지 않는다(신고 2026-11-04)
        if (explorerAutoCollapsed) return;
        setExplorerCollapsed(true);
    }, [explorerAutoCollapsed]);
    // 메뉴 보기 › 의 체크형 항목 — 화면 컨트롤과 같은 상태·같은 함수를 본다(두 갈래로 두면 어긋난다)
    // 값이 같으면 같은 객체 — Header 가 memoWindow 라 매 렌더 새 객체면 글자마다 머리줄 전체가 다시 그려진다(입력 지연, 2026-10-26)
    const viewState = useMemo(() => ({
        columnSelection: !!editorOptions.columnSelection,
        showComments: !!editorOptions.showComments,
        split: isEditorSplit,
        // 수직 분할은 SQL 편집 탭의 것 — 다른 작업 탭에서는 메뉴가 꺼진다(신고 2026-11-04: 다른 탭에서 눌러도 SQL 편집에 반영됐다)
        splitDisabled: !sqlTabActive,
        detach: isEditorDetached,
        analysis: analysisMode,
        assistant: assistantOpen,
    }), [editorOptions.columnSelection, editorOptions.showComments, isEditorSplit, sqlTabActive, isEditorDetached, analysisMode, assistantOpen]);
    const handleToggleView = useCallback((key) => {
        switch (key) {
            case 'columnSelection':
            case 'showComments':
                setEditorOptions(p => ({ ...p, [key]: !p[key] }));
                break;
            case 'split': if (sqlTabActive) setIsEditorSplit(p => !p); break;
            case 'detach': setIsEditorDetached(p => !p); break;
            case 'analysis': toggleAnalysisMode(); break;
            case 'assistant': if (AI_ENABLED) toggleAssistantPanel(); break;   // AI 제외 빌드에는 패널이 없다
            default: break;
        }
    }, [toggleAnalysisMode, toggleAssistantPanel, sqlTabActive]);
    // 에이전트가 채워 보낸 사례 초안(SSE case_draft). 모달이 이것을 받으면 등록 화면을 편집 상태로 연다.
    const [caseSeed, setCaseSeed] = useState(null);
    // 튜닝 사례 창에서 지금 고른 사례(에이전트의 「지금 앱 화면」 에 싣는다) — 렌더를 일으키지 않게 ref
    const openCaseRef = useRef(null);
    const handleCaseSelected = useCallback((c) => { openCaseRef.current = c ? { id: c.id, title: c.title, mine: !!c.mine } : null; }, []);
    // 패널의 이벤트 탭이 의존 배열에 넣는다 — 매 렌더 새 함수면 탭이 다시 붙어 이벤트를 놓친다.
    const handleOpenCaseDraft = useCallback((seed) => {
        setCaseSeed(seed);
        setCaseListOpen(true);
    }, []);
    /**
     * SQL 튜닝 작업에서 시작한 사례 등록이 저장됐다 — 작업 이력·「등록한 사례」 에 잇고 열린 작업 창을 새로 읽는다.
     * 서버가 사례가 실제로 있는지·내 것인지 다시 본다(화면이 보낸 id 를 믿지 않는다).
     */
    const handleCaseSavedFromSeed = useCallback(async (saved, seed) => {
        if (!saved || !seed || !seed.fromSqlTuneJob) return;
        try {
            await sqlTuningApi.linkCase(seed.fromSqlTuneJob, saved.id);
            setSqlTuningFocus({ jobId: String(seed.fromSqlTuneJob), step: null, open: false, nonce: Date.now() });
        } catch (err) { toast.error(formatApiError(err, t)); }
    }, [t]);
    /**
     * 에이전트가 잰 계획을 실행계획 탭으로 — **진행 중 자동 반영**(사용자 결정 2026-09-20).
     *
     * 화면을 뺏는 것은 **한 턴에 한 번뿐**이다. 측정할 때마다 탭을 활성화하면 사용자가 결과
     * 그리드를 보는 중에 계속 끌려다닌다.
     */
    const agentTurnSeenRef = useRef(false);
    const handleAgentTurnStart = useCallback(() => { agentTurnSeenRef.current = false; }, []);

    const handleAgentPlanCapture = useCallback((d) => {
        if (!d || !d.planText) return;
        const roleLabel = d.label || (d.role === 'baseline' ? t('common:planCompare.before') : null);
        const modeLabel = d.mode === 'actual' ? t('common:tun.actual') : t('common:tun.estimated');
        const tab = {
            id: `agent_${d.captureId || d.nonce}`,
            name: roleLabel ? `AI ${roleLabel} ${modeLabel}` : `AI ${modeLabel}`,
            data: d.planText,
            query: d.sql || '',
            connectionId: d.connectionId || null,
            origin: 'agent',
            captureId: d.captureId || null,
            metrics: d.metrics || null,
            mode: d.mode || null,
            threadId: d.threadId || null,
        };
        setExecutionPlanTabs(prev => pushPlanTabs(prev, tab));
        setActiveExecutionPlanTabId(tab.id);
        if (!agentTurnSeenRef.current) {
            setActiveMainTab('executionPlans');
            agentTurnSeenRef.current = true;
        }
    }, [t]);

    /** 에이전트의 결론 — 개선 전/후를 나란히 보는 탭 하나 */
    const handleAgentPlanCompare = useCallback((d) => {
        if (!d || !d.baseline) return;
        const results = Array.isArray(d.results) ? d.results : [];
        // 판정이 가장 좋은 것을 오른쪽에 세운다 — 없으면 첫 번째
        const best = results.find(r => r.label === d.best) || results[0] || null;
        if (!best) return;
        const tab = {
            id: `agent_cmp_${d.pairId || d.nonce}`,
            name: t('common:planCompare.tabName'),
            compare: { before: d.baseline, after: best, all: results },
            summary: d.summary || null,
            verdict: { best: d.best || null, warnings: d.warnings || [] },
            connectionId: d.connectionId || null,
            origin: 'agent',
            threadId: d.threadId || null,
            // 탭 배열의 다른 코드가 기대하는 모양을 갖춘다
            data: '',
            query: d.baseline.sql || '',
        };
        setExecutionPlanTabs(prev => pushPlanTabs(prev, tab));
        setActiveExecutionPlanTabId(tab.id);
        setActiveMainTab('executionPlans');
    }, [t]);

    /**
     * 튜닝 챌린지의 채점 계획을 왼쪽 실행계획 탭으로(2026-10-05). 에이전트의 결론 탭과 **같은 비교 화면**(PlanCompare)을 쓰고
     * 머리글만 「모범답안 / 내 제출」 로 바꾼다. 한쪽 계획만 있으면(제출이 실패한 경우 등) 그것만 단일 탭으로 연다.
     */
    const handleChallengePlans = useCallback((d) => {
        if (!d) return;
        const { answer, submitted } = d;
        const id = `chal_${d.nonce || Date.now()}`;
        let tab;
        if (answer && answer.planText && submitted && submitted.planText) {
            tab = {
                id,
                name: d.title,
                compare: { before: answer, after: submitted, all: [], labels: { before: answer.label, after: submitted.label } },
                connectionId: d.connectionId || null,
                origin: 'challenge',
                data: '',
                query: answer.sql || '',
            };
        } else {
            const only = (answer && answer.planText) ? answer : submitted;
            if (!only || !only.planText) return;
            tab = { id, name: `${d.title} · ${only.label}`, data: only.planText, query: only.sql || '', connectionId: d.connectionId || null, origin: 'challenge' };
        }
        setExecutionPlanTabs(prev => pushPlanTabs(prev, tab));
        setActiveExecutionPlanTabId(tab.id);
        setActiveMainTab('executionPlans');
        // 실행계획 탭은 SQL 편집 탭의 결과 영역에 있다 — 챌린지가 작업 탭이 된 뒤로는 가려져 안 보였다(2026-11-04)
        if (workspace) workspace.activate(SQL_TAB);
    }, [workspace]);

    /**
     * 보관한 실행계획을 **사례 등록 화면**으로 넘긴다.
     *
     * 서버가 LLM 없이 양식에 옮겨 담아 씨앗을 주고, 그 뒤는 사례 초안과 **같은 통로**다
     * (`handleOpenCaseDraft`). 증상 축은 기계가 채우지만 원인·처방은 사용자가 고른다 —
     * 저장소의 3축 게이트가 그 단계를 강제한다.
     */
    const handleRegisterPlanCase = useCallback(async (group) => {
        if (!group) return;
        try {
            const r = await planArchiveCaseDraft(group.id);
            if (!r || !r.seed) return;
            setPlanArchiveOpen(false);
            handleOpenCaseDraft({ ...r.seed, visibility: 'internal', nonce: Date.now() });
        } catch (error) {
            toast.error(formatApiError(error, t));
        }
    }, [handleOpenCaseDraft, t]);

    /**
     * 에이전트에 질문을 넘긴다 — **채우기만 하고 보내지는 않는다.** 사용자가 고쳐 보낼 수 있어야 한다.
     * @param opts.tuningLevel SQL 튜닝 탭에서 고른 수준. 넘기면 패널의 수준도 그것으로 맞춘다.
     */
    const askAssistant = useCallback((text, opts = {}) => {
        // 분석 패널은 닫지 않는다 — 에이전트가 오른쪽에 붙고 분석 패널은 왼쪽으로 밀린다
        setAssistantOpen(true);
        setAssistantPrefill({ text, tuningLevel: opts.tuningLevel, nonce: Date.now() });
    }, []);

    // 세션 DB 측 ground truth 확인 (V$SESSION). anchorRect 는 popover 위치용.
    const [verifyState, setVerifyState] = useState(null);
    // verifyState 형태: { sessionId, anchorRect, loading, result?, error? }
    const handleVerifySession = useCallback(async (session, anchorRect) => {
        if (!session?.sessionId) return;
        const sid = session.sessionId;
        setVerifyState({ sessionId: sid, anchorRect, loading: true });
        try {
            const result = await dispatch(verifySession(sid));
            setVerifyState((prev) => (prev && prev.sessionId === sid)
                ? { ...prev, loading: false, result, error: null }
                : prev);
        } catch (err) {
            setVerifyState((prev) => (prev && prev.sessionId === sid)
                ? { ...prev, loading: false, error: err.message || t('common:session.verifyFailed') }
                : prev);
        }
    }, [dispatch]);

    // Phase 3: 탭 ↔ 세션 바인딩
    // 바인딩 전에 세션이 sessionsList 에 살아있는지 검증 — 죽은 세션을 바인딩하면
    // 이어지는 cleanup useEffect 가 즉시 null 처리해서 사용자 입장에선 "바인딩 안 됨" 으로 보임.
    /**
     * 에디터 탭에 묶을 수 있는 세션인가.
     *
     * **시스템 전용 세션(system-collect·system-query)은 안 된다.** 그것들은 주인이 따로 있다:
     *   · system-collect — 모니터링 수집기가 주기마다 쓴다. 타임아웃이 10초라 사용자 쿼리가
     *     중간에 끊기고, 수집과 겹치면 "세션 사용 중" 으로 서로를 막는다
     *   · system-query   — 탐색기·조회가 쓴다. 30초 타임아웃에 같은 이유로 충돌한다
     *
     * 세션 선택 드롭다운은 이미 걸러내지만, **탐색기에서 세션 노드를 클릭하는 경로**는
     * 그냥 통과했다. 그래서 판정을 목록이 아니라 **바인딩하는 한 곳**에 둔다 — 화면마다
     * 걸러내면 새 경로가 생길 때마다 빠뜨린다.
     *
     * category 가 없는 예전 세션은 user 로 본다(레거시 호환).
     */
    const assertBindable = useCallback((sessionId) => {
        if (!sessionId) return true;                       // 해제는 언제나 허용
        const liveList = sessionsListRef.current || sessionsList || [];
        const s = liveList.find(x => x.sessionId === sessionId);
        if (!s) {
            // 목록에 없는 세션은 부를 번호가 없다 — id 조각을 보이지 않는다
            toast.warning(t('common:session.notActiveHint'));
            return false;
        }
        if (s.category && s.category !== 'user') {
            toast.warning(t('common:session.systemOnly'));
            return false;
        }
        return true;
    }, [sessionsList, t]);

    // 세션은 프로젝트 단위다 — 탭 하나에서 골라도 그 프로젝트(없으면 기본 작업공간)의 탭 전부에 건다.
    // 실행 중인 세션에 묶인 탭은 그대로 둔다(utils/projectSession.js).
    const isBusySessionId = useCallback((sid) => {
        const liveList = sessionsListRef.current || [];
        return isSessionBusy(liveList.find(x => x.sessionId === sid));
    }, []);
    const bindProjectSessionUnchecked = useCallback((fileId, sessionId) => {
        setOpenFiles(prev => bindSessionToProject(prev, fileId, sessionId, isBusySessionId));
    }, [isBusySessionId]);
    const handleBindSessionToTab = useCallback((fileId, sessionId) => {
        if (!assertBindable(sessionId)) return;
        bindProjectSessionUnchecked(fileId, sessionId);
    }, [assertBindable, bindProjectSessionUnchecked]);

    // 세션 stale cleanup — sessionsList 에서 사라진 sessionId 를 가진 탭은 unbind.
    //
    // 단순히 "지금 sessionsList 에 없으면 cleanup" 으로 하면 polling race 에 취약함:
    //   t=0  사용자 sessionId X 바인딩 (탐색기/SessionPicker 클릭)
    //   t=0+ openFiles[active].sessionId = X
    //   t=1  polling tick — sessionsList 가 X 포함 (정상) 또는 아직 미반영 (race)
    //   race 시: liveIds 에 X 없음 → 즉시 null 처리 → 사용자 입장에서 "바인딩 됐는데 풀림"
    //
    // 해결: "이 sessionId 가 한 번이라도 sessionsList 에 등장한 적 있는가?" 추적.
    //   - 본 적 있고 (seen) 지금 없으면 → 진짜 종료/삭제 → cleanup
    //   - 본 적 없으면 → polling 이 아직 sync 안 됐을 가능성 → 보존
    const seenSessionIdsRef = useRef(new Set());
    useEffect(() => {
        // 매 폴링 결과의 sessionId 들을 seen 에 누적 (set 이라 중복 무시).
        sessionsList.forEach(s => {
            if (s && s.sessionId) seenSessionIdsRef.current.add(s.sessionId);
        });
        const liveIds = new Set(sessionsList.map(s => s.sessionId));
        setOpenFiles(prev => {
            let changed = false;
            const next = prev.map(f => {
                if (!f.sessionId) return f;
                if (liveIds.has(f.sessionId)) return f;                // 현재 살아있음
                if (!seenSessionIdsRef.current.has(f.sessionId)) return f; // 아직 본 적 없음 (polling race 보호)
                changed = true;                                         // 한번 봤고 지금 없음 = 진짜 종료
                return { ...f, sessionId: null };
            });
            return changed ? next : prev;
        });
    }, [sessionsList]);

    // connectionId가 변경되면 ref 및 schemaActions에도 업데이트
    useEffect(() => {
        activeConnectionIdRef.current = activeConnectionId;
        setActiveConnectionIdForSchema(activeConnectionId);
    }, [activeConnectionId]);

    // axiosInstance 요청 시 connectionId 자동 주입 — **부르는 쪽이 정한 연결은 덮지 않는다.**
    // 예전에는 무조건 덮어써서, F4 가 탭 세션의 연결 A 를 실어 보내도 탐색기 연결 B 로 바뀌어 나갔다(2026-10-07).
    useEffect(() => {
        const interceptor = axiosInstance.interceptors.request.use((config) => {
            const connId = activeConnectionIdRef.current;
            if (connId && config.data && typeof config.data === 'object' && config.data.connectionId == null) {
                config.data.connectionId = connId;
            }
            return config;
        });
        return () => axiosInstance.interceptors.request.eject(interceptor);
    }, []);

    // 로그인 만료 — 서버가 AUTHENTICATION_REQUIRED 로 답하면 QBase 에 확인하고, 만료면 작업 화면 위에
    // 로그인 창을 띄운다(handleSessionExpired). 세션 폴링도 같은 판정을 부른다 — 여기는 SQL 실행처럼
    // 사용자가 누른 동작에서 곧바로 알리기 위한 것이다.
    // 기본 axios 에도 같은 가로채기를 건다(2026-11-04 신고 — 「로그인이 만료되면 다시 로그인하라는 창이 떠야」): 포럼·진화 기록 등
    // 서비스 47곳이 기본 axios 를 써서, 그쪽에서 받은 401 은 아무도 알아채지 못했다.
    useEffect(() => {
        const onError = (error) => {
            const r = error && error.response;
            if (r && r.status === 401 && r.data && r.data.errorType === 'AUTHENTICATION_REQUIRED') {
                dispatch(handleSessionExpired());
            }
            // QBase 점검 — 서버가 503 MAINTENANCE 로 넘긴다. 배너를 켠다(로그아웃·재전송 없음)
            if (r && r.status === 503 && r.data && r.data.resultCode === 'MAINTENANCE') {
                detectMaintenance(r.status, r.data, r.headers && r.headers['x-qbase-maintenance']);
            }
            return Promise.reject(error);
        };
        const id = axiosInstance.interceptors.response.use(undefined, onError);
        const gid = axios.interceptors.response.use(undefined, onError);
        // QBase 를 직접 부르는 길(qbaseClient)은 fetch 라 가로챌 수 없다 — 401 을 받으면 창 이벤트로 알린다
        const onQbase401 = () => dispatch(handleSessionExpired());
        window.addEventListener(AUTH_EXPIRED_EVENT, onQbase401);
        return () => {
            axiosInstance.interceptors.response.eject(id);
            axios.interceptors.response.eject(gid);
            window.removeEventListener(AUTH_EXPIRED_EVENT, onQbase401);
        };
    }, [dispatch]);

    // 아무것도 하지 않는 동안 만료된 것도 알아챈다 — 2분마다, 그리고 창으로 돌아올 때 QBase 에 묻는다(가벼운 세션 조회 한 번).
    // 판정은 handleSessionExpired 한 곳(QBase 가 「세션 없음」 이라고 답할 때만 로그인 창 — 확인할 수 없으면 그대로 둔다)
    useEffect(() => {
        if (!isAuthenticated || IS_STANDALONE) return undefined;
        const check = () => { if (!document.hidden) dispatch(handleSessionExpired()); };
        const timer = setInterval(check, 2 * 60 * 1000);
        const onVisible = () => { if (!document.hidden) check(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', onVisible); };
    }, [isAuthenticated, dispatch]);

    // QBase 점검 감시 — 로그인한 동안 1분마다 확인(점검 중에는 retryAfterSec 마다). 스탠드얼론은 QBase 가 없다
    useEffect(() => {
        if (!isAuthenticated || IS_STANDALONE) return undefined;
        return startMaintenanceWatch();
    }, [isAuthenticated]);

    // 로그인 상태 변경 시에만 로그 출력
    useEffect(() => {
        if (isAuthenticated) {
            console.log('🔍 [Redux State] 로그인 완료 후 인증 상태:', {
                isAuthenticated,
                user,
                isTemporaryPassword,
                userIsTempPassword: user?.isTempPassword,
                localStorage_tempPassword: localStorage.getItem('livesql_temp_password')
            });
        }
    }, [isAuthenticated, user, isTemporaryPassword]);

    const sqlEditorRef = useRef(null); // SQLEditor 인스턴스 참조를 위해 추가
    const [isEditorFocused, setIsEditorFocused] = useState(false); // 에디터 포커스 상태
    const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false); // 패스워드 변경 모달 상태
    const abortControllersRef = useRef({}); // 각 탭별 AbortController 저장 (탭 ID를 키로 사용)

    // 새로운 상태: 테이블 정보 탭들
    const [tableInfoTabs, setTableInfoTabs] = useState([]);
    const [activeTableInfoTabId, setActiveTableInfoTabId] = useState(null);

    // ERD 모달 상태
    const [isERDModalOpen, setIsERDModalOpen] = useState(false);
    const [selectedERDSchema, setSelectedERDSchema] = useState(null);
    const [currentConnectionData, setCurrentConnectionData] = useState(null);

    // SQL 튜닝 상태 (다중 쿼리 지원)

    // Initialize auth on app start
    // 스탠드얼론은 로그인이 없다 — QBase 를 아예 호출하지 않는다.
    // 이 가드가 없으면 부팅할 때마다 QBase 세션 조회가 나가서 실패하고
    // "[auth] 세션 복원 실패" 가 찍힌다 (기능은 로그인 게이트 덕에 멀쩡하지만,
    //  패키징된 앱에는 /qcore 프록시가 없어 항상 실패하는 헛된 요청이다).
    useEffect(() => {
        if (IS_STANDALONE) return;
        dispatch(initializeAuth());
    }, [dispatch]);

    // ResizeObserver "loop completed with undelivered notifications" 경고 차단 — well-known
    // benign warning. @tanstack/react-virtual 등 라이브러리가 ResizeObserver 콜백 안에서 DOM 변경
    // 시 발생. 실제 동작 무관하지만 webpack-dev-server / react-error-overlay 가 빨갛게 잡음.
    // 핵심: capture phase(=true) 로 등록 — overlay 의 bubble-phase listener 보다 먼저 실행되어
    // stopImmediatePropagation 이 효과가 있음. 또한 webpack-dev-server iframe 의
    // overlay 도 같이 닫음 (#webpack-dev-server-client-overlay).
    useEffect(() => {
        const KNOWN_BENIGN = [
            'ResizeObserver loop completed with undelivered notifications',
            'ResizeObserver loop limit exceeded',
        ];
        const isBenign = (msg) => msg && KNOWN_BENIGN.some(p => String(msg).includes(p));
        const closeWebpackOverlay = () => {
            // CRA / webpack-dev-server 5+: iframe 형태로 overlay 표시. iframe 이 이미 떴으면 제거.
            const overlays = document.querySelectorAll(
                '#webpack-dev-server-client-overlay, iframe[src*="webpack-dev-server"]'
            );
            overlays.forEach(el => { try { el.remove(); } catch {} });
        };
        const handler = (e) => {
            if (isBenign(e?.message)) {
                e.stopImmediatePropagation();
                e.preventDefault();
                // overlay 가 이미 떴을 수 있으므로 다음 tick 에 닫기.
                setTimeout(closeWebpackOverlay, 0);
            }
        };
        const rejectionHandler = (e) => {
            if (isBenign(e?.reason?.message || e?.reason)) {
                e.stopImmediatePropagation();
                e.preventDefault();
                setTimeout(closeWebpackOverlay, 0);
            }
        };
        window.addEventListener('error', handler, true);
        window.addEventListener('unhandledrejection', rejectionHandler, true);
        return () => {
            window.removeEventListener('error', handler, true);
            window.removeEventListener('unhandledrejection', rejectionHandler, true);
        };
    }, []);

    // 모든 [title] 속성을 [data-tooltip] + [aria-label] 로 자동 이전 — 커스텀 툴팁 일관 적용.
    // JSX 변경 없이 일괄 적용. 동적으로 추가되는 element 도 MutationObserver 로 catch.
    // 그리드 셀, CodeMirror gutter marker 등 overflow:hidden 부모를 가진 요소도 포함 — 실제 툴팁 표시는
    // 아래 portal-tooltip useEffect 가 document.body 에 fixed 로 띄우므로 클리핑 영향 없음.
    useEffect(() => {
        const moveTitle = (el) => {
            if (!el || el.nodeType !== 1 || !el.hasAttribute) return;
            if (el.hasAttribute('title')) {
                const t = el.getAttribute('title');
                if (t) {
                    el.setAttribute('data-tooltip', t);
                    if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', t);
                }
                el.removeAttribute('title');
            }
        };
        const sweep = (root) => {
            if (!root) return;
            if (root.nodeType === 1 && root.hasAttribute && root.hasAttribute('title')) moveTitle(root);
            if (root.querySelectorAll) root.querySelectorAll('[title]').forEach(moveTitle);
        };
        sweep(document.body);
        const obs = new MutationObserver((muts) => {
            for (const m of muts) {
                if (m.type === 'attributes' && m.attributeName === 'title') {
                    moveTitle(m.target);
                } else if (m.type === 'childList') {
                    m.addedNodes.forEach(sweep);
                }
            }
        });
        obs.observe(document.body, {
            attributes: true,
            attributeFilter: ['title'],
            subtree: true,
            childList: true,
        });
        return () => obs.disconnect();
    }, []);

    // Portal 툴팁 컨트롤러 — [data-tooltip] hover 시 document.body 에 fixed 로 띄움.
    // 부모의 overflow:hidden 영향 안 받음 (그리드 셀/CM gutter marker 등 안전).
    // 뷰포트 좌/우/하단 잘림 방지 — getBoundingClientRect 로 측정 후 clamp/flip.
    useEffect(() => {
        let tipEl = null;
        let hoverEl = null;
        let showTimer = null;
        const SHOW_DELAY_MS = 220;
        const MARGIN = 6;
        const ensureEl = () => {
            if (tipEl) return tipEl;
            tipEl = document.createElement('div');
            tipEl.className = 'app-portal-tooltip';
            tipEl.setAttribute('role', 'tooltip');
            document.body.appendChild(tipEl);
            return tipEl;
        };
        // 마지막 마우스 위치 — 툴팁을 **커서 아래**에 둔다(2026-10-23 신고: 큰 커서가 요소 아래에 뜬 툴팁 글을 가렸다)
        let lastPt = null;
        let viaPointer = false;
        const positionTip = (target) => {
            if (!tipEl || !target || !target.isConnected) return;
            const rect = target.getBoundingClientRect();
            const tipRect = tipEl.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            // 기본: target 아래 가운데. 마우스로 띄웠으면 **커서 끝에서 커서 크기만큼 아래**(설정 › 일반 › 마우스 커서 크기)보다 위로는 올리지 않는다.
            // 높은 요소(표의 줄·그래프)는 요소 아래가 아니라 커서 아래 — 요소 아래는 너무 멀다. 넓은 요소는 커서 위치를 가운데로
            const pt = viaPointer ? lastPt : null;
            const gap = pt ? cursorGapPx() : 0;
            let top = rect.bottom + MARGIN;
            if (pt) top = rect.height > 40 ? pt.y + gap : Math.max(top, pt.y + gap);
            const anchorX = pt && rect.width > 200 ? pt.x : rect.left + rect.width / 2;
            let left = anchorX - tipRect.width / 2;
            // 좌/우 잘림 clamp
            if (left + tipRect.width > vw - 4) left = vw - tipRect.width - 4;
            if (left < 4) left = 4;
            // 하단 잘림 시 위로 flip — 커서 끝(hotspot)보다 위에 둔다(커서는 끝에서 아래·오른쪽으로 그려진다)
            if (top + tipRect.height > vh - 4) {
                const ceiling = pt ? Math.min(rect.top, pt.y) : rect.top;
                const above = ceiling - tipRect.height - MARGIN;
                top = above >= 4 ? above : Math.max(4, vh - tipRect.height - 4);
            }
            // 위 계산은 화면 px(rect·innerWidth) — style 에는 CSS px 로(웹의 화면 확대, utils/appZoom)
            tipEl.style.left = `${toCssPx(left)}px`;
            tipEl.style.top = `${toCssPx(top)}px`;
        };
        const showFor = (el) => {
            const text = el.getAttribute('data-tooltip');
            if (!text) return;
            ensureEl();
            tipEl.textContent = text;
            tipEl.classList.add('visible');
            // 두 번 호출 — 초기 위치 + 실제 사이즈 측정 후 재배치 (max-width/줄바꿈 반영)
            positionTip(el);
            requestAnimationFrame(() => positionTip(el));
        };
        const hide = () => {
            if (showTimer) { clearTimeout(showTimer); showTimer = null; }
            if (tipEl) tipEl.classList.remove('visible');
            hoverEl = null;
        };
        const onMove = (e) => { lastPt = { x: e.clientX, y: e.clientY }; };
        const onOver = (e) => {
            lastPt = { x: e.clientX, y: e.clientY };
            const t = e.target && e.target.closest && e.target.closest('[data-tooltip]');
            if (!t || t === hoverEl) return;
            hoverEl = t;
            viaPointer = true;
            if (showTimer) clearTimeout(showTimer);
            showTimer = setTimeout(() => showFor(t), SHOW_DELAY_MS);
        };
        const onOut = (e) => {
            if (!hoverEl) return;
            const related = e.relatedTarget;
            if (related && hoverEl.contains(related)) return;
            hide();
        };
        const onFocusIn = (e) => {
            const t = e.target && e.target.closest && e.target.closest('[data-tooltip]');
            if (!t || t === hoverEl) return;
            // 키보드 focus-visible 만 트리거 — 마우스 클릭 후 자동 focus 는 건너뜀
            if (typeof t.matches === 'function' && !t.matches(':focus-visible')) return;
            hoverEl = t;
            viaPointer = false;      // 키보드로 왔으면 요소 아래(커서 위치와 무관)
            showFor(t);
        };
        const onFocusOut = () => hide();
        // 누르면 툴팁을 거둔다(2026-10-06) — 드롭다운(select)을 열면 툴팁이 열린 목록 위에 남아 항목을 가렸고, 누른 직후
        // 지연 타이머가 돌아 열린 목록 위에 새로 뜨기도 했다. hoverEl 은 남겨 둔다 — 같은 요소 위에 있는 동안 다시 뜨지 않게
        // (요소를 벗어났다 돌아오면 다시 뜬다)
        const onDown = () => {
            if (showTimer) { clearTimeout(showTimer); showTimer = null; }
            if (tipEl) tipEl.classList.remove('visible');
        };
        document.addEventListener('mousedown', onDown, true);
        document.addEventListener('mouseover', onOver, true);
        document.addEventListener('mousemove', onMove, { capture: true, passive: true });
        document.addEventListener('mouseout', onOut, true);
        document.addEventListener('focusin', onFocusIn, true);
        document.addEventListener('focusout', onFocusOut, true);
        // 스크롤/리사이즈 시 즉시 숨김 — 위치 추적 비용 회피
        document.addEventListener('scroll', hide, true);
        window.addEventListener('blur', hide);
        window.addEventListener('resize', hide);
        return () => {
            document.removeEventListener('mousedown', onDown, true);
            document.removeEventListener('mouseover', onOver, true);
            document.removeEventListener('mousemove', onMove, { capture: true });
            document.removeEventListener('mouseout', onOut, true);
            document.removeEventListener('focusin', onFocusIn, true);
            document.removeEventListener('focusout', onFocusOut, true);
            document.removeEventListener('scroll', hide, true);
            window.removeEventListener('blur', hide);
            window.removeEventListener('resize', hide);
            if (showTimer) clearTimeout(showTimer);
            if (tipEl && tipEl.parentNode) tipEl.parentNode.removeChild(tipEl);
            tipEl = null;
        };
    }, []);

    // DBMS manifest 로드 (Phase A) — 모든 컴포넌트가 capability flag 를 redux 에서 읽도록.
    useEffect(() => {
        dispatch(loadDbmsManifests());
    }, [dispatch]);
    
    // 임시패스워드 상태 감지 및 강제 변경 모달 표시
    useEffect(() => {
        console.log('🔍 [App Debug] 임시패스워드 상태 체크:', {
            isAuthenticated,
            isTemporaryPassword,
            showPasswordChangeModal: isAuthenticated && isTemporaryPassword
        });
        
        if (isAuthenticated && isTemporaryPassword) {
            setShowPasswordChangeModal(true);
        } else {
            setShowPasswordChangeModal(false);
        }
    }, [isAuthenticated, isTemporaryPassword]);

    // 최초 로딩 시 기본 DB 연결 useEffect - 한 번만 실행되도록 개선
    const [hasAutoConnected, setHasAutoConnected] = useState(false);
    const autoConnectAttempted = useRef(false);
    
    // 앱 시작 시 백엔드 연결 목록 로드 (빌트인 포함)
    /**
     * 연결 목록을 **누구 것으로** 받아 왔는가.
     *
     * 예전에는 boolean 한 개(`connectionsFetched`)였다. 한 번 받으면 영영 다시 받지 않으므로,
     * 한 브라우저에서 로그아웃하고 **다른 계정으로 로그인해도 앞 사용자의 연결이 그대로
     * 남았다**(실제 신고 2026-09-12). SPA 라 페이지가 새로 뜨지 않아 ref 가 살아 있었다.
     *
     * 사용자 id 로 바꾸면 계정이 바뀌는 순간 값이 달라져 자연히 다시 받는다.
     */
    const connectionsFetchedFor = useRef(null);
    // 목록 수신(성공/실패 무관) 이 끝났는지 — 아래 "첫 연결 자동 활성화" 가 이 신호를 기다린다.
    // 기다리지 않으면 persist 에 남은 옛 항목을 첫 연결로 잡아 헛된 스키마 조회가 나간다.
    const [connectionsLoaded, setConnectionsLoaded] = useState(false);
    const currentUsrid = (user && (user.usrid || user.id)) || (IS_STANDALONE ? 'standalone' : null);
    useEffect(() => {
        if (!isAuthenticated && !IS_STANDALONE) return;
        if (!currentUsrid) return;
        if (connectionsFetchedFor.current === currentUsrid) return;

        /*
          **앞 사용자의 연결을 먼저 비운다.** 서버 응답을 기다리는 사이에도 화면에 남아 있고,
          더 나쁘게는 redux-persist 가 그 사이 그것을 **새 사용자의 저장 공간에** 써 버린다
          (userPersistStorage 는 저장 시점의 `livesql_user` 를 본다). 그러면 다음 로그인에
          남의 연결이 되살아난다.

          받아오기가 실패해도 비운 채로 둔다 — 남의 목록을 보여 주는 것보다 빈 목록이 낫다.
        */
        if (connectionsFetchedFor.current !== null) {
            dispatch({ type: 'SET_CONNECTIONS', payload: [] });
            setConnectionsLoaded(false);
        }
        connectionsFetchedFor.current = currentUsrid;

        (async () => {
            try {
                const base = process.env.REACT_APP_API_BASE_URL || '/sql-developer';
                // 로컬 전용 연결(웹) — 이 브라우저의 암호문을 서버 메모리에 먼저 올린다. 그래야 아래 목록에 함께 뜨고 쓸 수 있다
                if (!IS_STANDALONE) await syncLocalConnections(currentUsrid);
                const res = await axios.get(`${base}/connections`);
                const backendConns = Array.isArray(res.data) ? res.data : (res.data?.data || []);

                const normalized = backendConns.map(c => ({
                    ...c,
                    type: 'connection',
                    children: c.children || null,
                    isConnected: c.isConnected !== undefined ? c.isConnected : true,
                    isLoadingSchemas: false,
                    schemasError: null
                }));

                // Web/Standalone 공통: backend 가 Single Source of Truth.
                // 옛 redux-persist 에 남은 stale id (backend 에 없는 항목) 는 자동 제거.
                // 동일 id 의 UI 상태 (children 스키마 트리) 는 보존해서 새로고침 시 다시 로드 안 해도 됨.
                const backendIds = new Set(normalized.map(c => c.id));
                const preserve = new Map(
                    (connectionsFromStore || [])
                        .filter(c => backendIds.has(c.id))
                        .map(c => [c.id, c.children])
                );
                const stale = (connectionsFromStore || []).filter(c => !backendIds.has(c.id));
                if (stale.length > 0) {
                    console.log(`[App] backend 에 없는 stale connection ${stale.length}개 정리:`,
                        stale.map(c => `${c.name}(${c.id})`).join(', '));
                }
                const builtins = normalized.filter(c => c.builtin);
                const regular = normalized.filter(c => !c.builtin);
                const payload = [...builtins, ...regular].map(c => ({
                    ...c,
                    children: preserve.get(c.id) ?? c.children
                }));
                dispatch({ type: 'SET_CONNECTIONS', payload });
                setConnectionsLoaded(true);
            } catch (err) {
                // 실패 시에는 connectionsLoaded 를 세우지 않는다. 세워 버리면 persist 에서 되살아난
                // 옛 목록을 "확인된 연결" 로 믿고 자동 스키마 조회가 나가, backend 에 없는 id 로
                // "Connection config not found" 가 발생한다.
                console.warn('[App] 백엔드 연결 목록 로드 실패 — 자동 연결 생략:', err.message);
                // 다음 시도에 다시 받도록 표식을 되돌린다 — 안 되돌리면 이 사용자는 이번 세션
                // 내내 빈 목록으로 남는다.
                connectionsFetchedFor.current = null;
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, currentUsrid]);

    // 로컬 전용 연결 — 앱이 열려 있는 동안 2분마다 서버에 알린다(끊기면 서버가 30분 뒤 지운다). 웹만
    useEffect(() => {
        if (IS_STANDALONE || !isAuthenticated || !currentUsrid) return undefined;
        return startLocalSync(currentUsrid);
    }, [isAuthenticated, currentUsrid]);

    // 연결이 로드되면 즉시 첫 번째 연결을 활성화 (더 직접적인 접근) - 로그인된 상태에서만
    useEffect(() => {
        if (!isAuthenticated && !IS_STANDALONE) return; // 로그인되지 않았으면 실행하지 않음
        // backend 목록이 도착하기 전에는 판단하지 않는다. persist 에 남은 옛 항목이나
        // 초기 상태를 "첫 연결" 로 오인하면 존재하지 않는 connectionId 로 스키마 조회가 나가고,
        // 그 실패가 schemasError 로 남아 탐색기에 (Error) 가 박힌다.
        if (!connectionsLoaded) return;

        if (connectionsFromStore && connectionsFromStore.length > 0 && !activeConnectionName) {
            const firstConnection = connectionsFromStore[0];
            // name만 설정 (id는 사용자가 명시적으로 선택할 때만 세팅 - 웹 모드 placeholder 대응)
            setActiveConnectionName(firstConnection.name);

            // 스키마도 한 번만 로드
            if (!autoConnectAttempted.current) {
                autoConnectAttempted.current = true;
                dispatch(fetchSchemasForConnection(firstConnection.id, firstConnection.dbType));
            }
        }
    }, [connectionsFromStore, isAuthenticated, connectionsLoaded]); // 목록 수신 완료 후에만 판단

    // 챌린지 데이터 최초 로딩 - 인증 완료 후 한 번만 실행
    const challengeDataLoaded = useRef(false);
    useEffect(() => {
        if (isAuthenticated && !challengeDataLoaded.current) {
            challengeDataLoaded.current = true;
            console.log('App - 챌린지 데이터 최초 로드');

            // 전체 챌린지 목록을 한 번에 로드 (수준값 없이)
            dispatch(loadChallengeList());
        }
    }, [isAuthenticated, dispatch]);

    // 참여그룹 변경 감지 및 초대된 챌린지 처리
    const lastProcessedGroup = useRef(null);
    const participationGroupProcessed = useRef(false);

    useEffect(() => {
        const handleParticipationGroupChange = () => {
            const savedGroup = localStorage.getItem('selectedParticipationGroup');
            const currentGroupKey = savedGroup || 'NO_GROUP';

            // 이미 처리한 그룹과 동일하면 스킵
            if (lastProcessedGroup.current === currentGroupKey) {
                return;
            }

            lastProcessedGroup.current = currentGroupKey;

            if (savedGroup) {
                try {
                    const parsedGroup = JSON.parse(savedGroup);
                    console.log('App - 참여그룹 변경 감지:', parsedGroup);

                    // 초대된 챌린지 로드 (groupId와 groupName 모두 전달)
                    dispatch(loadInvitedChallenges(parsedGroup.qryChallengePtcpGrpId, parsedGroup.ptcpGrpNm));
                } catch (error) {
                    console.error('참여그룹 정보 파싱 오류:', error);
                }
            } else {
                console.log('App - 참여그룹 해제: 초대된 챌린지 제거');
                dispatch(removeInvitedChallenges());
            }
        };

        // localStorage 변경 감지
        window.addEventListener('storage', handleParticipationGroupChange);
        window.addEventListener('participationGroupChanged', handleParticipationGroupChange);

        // 초기 체크 (한 번만)
        if (isAuthenticated && !participationGroupProcessed.current) {
            participationGroupProcessed.current = true;
            handleParticipationGroupChange();
        }

        return () => {
            window.removeEventListener('storage', handleParticipationGroupChange);
            window.removeEventListener('participationGroupChanged', handleParticipationGroupChange);
        };
    }, [isAuthenticated, dispatch]);

    // 추가 안전장치: 연결 상태 확인 - 로그인된 상태에서만
    useEffect(() => {
        if (isAuthenticated && connectionsFromStore && connectionsFromStore.length > 0 && !activeConnectionName) {
            // name만 설정 (id는 사용자 명시적 선택 시에만)
            setActiveConnectionName(connectionsFromStore[0].name);
        }
    }, [isAuthenticated, connectionsFromStore, activeConnectionName]); // 의존성 배열 추가

    // nextFileIdRef 동기화
    useEffect(() => {
        nextFileIdRef.current = nextFileId;
    }, [nextFileId]);

    // 기본 작업공간의 빈 새 탭 하나 — 번호를 소비한다. 탭이 0개가 되는 자리(마지막 탭 닫기·프로젝트 닫기)가
    // 이것을 쓴다: 0개면 화면이 「Loading or initializing...」 에서 멈춘다.
    const takeBlankTab = useCallback((sessionId = null) => {
        // 번호는 쓰이지 않은 것으로 — 모든 프로젝트를 통틀어 id 가 하나여야 한다(utils/tabIds.js)
        const currentId = freeTabNumber(openFilesRef.current, nextFileIdRef.current);
        nextFileIdRef.current = currentId + 1;
        setNextFileId(currentId + 1);
        return {
            id: `new_${currentId}`,
            name: t('common:file.defaultNameSql', { id: currentId }),
            content: '',
            isDirty: false,
            path: null,
            bindVariables: {},
            projectId: null,
            sessionId,
        };
    }, [t]);

    // ID 생성 로직을 호출하는 쪽으로 옮기는 것이 장기적으로 좋음.
    // 여기서는 우선 useEffect의 무한루프를 해결하는 데 집중.
    const createNewFile = useCallback((projectId) => {
        // id 는 **모든 탭**에서, 이름은 이 프로젝트 안에서 겹치지 않게 — 둘을 한 번호로 맞춘다
        const usedIds = new Set((openFiles || []).map(f => f.id));
        let currentId = freeTabNumber(openFiles, nextFileIdRef.current);
        const pid = projectId !== undefined ? projectId : null;
        // 프로젝트에 속한 탭은 .sql 확장자를 붙이지 않음 (프로젝트 내부 표기는 확장자 없음)
        const nameSuffix = pid ? '' : '.sql';

        // 현재 프로젝트(또는 개별스크립트) 내 탭 이름과 중복되지 않을 때까지 번호 증가
        const takenNames = new Set(
            (openFiles || [])
                .filter(f => (f.projectId || null) === pid)
                .map(f => f.name)
        );
        let candidateName = t('common:file.defaultName', { id: currentId, suffix: nameSuffix });
        while (takenNames.has(candidateName) || usedIds.has(`new_${currentId}`)) {
            currentId += 1;
            candidateName = t('common:file.defaultName', { id: currentId, suffix: nameSuffix });
        }

        const newFile = {
            id: `new_${currentId}`,
            name: candidateName,
            content: '',
            isDirty: false,
            path: null,
            bindVariables: {},
            projectId: pid,
            // 프로젝트의 세션을 물려받는다 — 새 탭에서 세션이 사라진 것처럼 보이지 않게(utils/projectSession.js)
            sessionId: projectSessionOf(openFiles, pid),
        };
        setNextFileId(currentId + 1);
        return newFile;
    }, [openFiles]);

    /**
     * 실행계획을 볼 수 있는 연결인가 — **아니면 왜 안 되는지** 돌려준다(null 이면 가능).
     *
     * 예전에는 연결 유무만 보고 보냈다. 그래서 세션이 없어 탐색기 기본값인 **내장 성능 수집
     * DB(SQLite)** 로 요청이 가면, 화면에 `adapter.getExecutionPlan not implemented for sqlite`
     * 라는 내부 문구가 떴다 — 진짜 원인(실행할 세션이 없다)은 어디에도 없었다(신고 2026-09-19).
     */
    const planBlockReason = useCallback((conn) => {
        if (!conn || !conn.dbType) return t('common:plan.selectConn');
        // 성능 수집 DB 는 탐색기·모니터링 전용이다. SQL 실행과 무관하다.
        if (conn.builtin) return t('common:plan.notRunnableConn');
        if (String(conn.dbType).toLowerCase() === 'sqlite') {
            return t('common:plan.unsupportedDb', { db: 'SQLite' });
        }
        return null;
    }, [t]);

    /**
     * 실행계획을 **어느 연결에서** 뽑을지 — 탭이 세션에 묶여 있으면 그 세션의 연결이다.
     *
     * 헤더의 전역 활성 연결만 보면, 탐색기 기본값인 내장 SQLite 가 활성인 채 Oracle 세션 탭에서
     * 계획을 요청했을 때 엉뚱한 연결로 간다. 실행계획보기(`handleExecutePlan`)는 2026-09-19 에
     * 고쳤는데 **SQL 실행계획 분석 패널 경로 두 곳은 옛 코드 그대로**여서, 세션은 Oracle 인데
     * "SQLite 에서는 볼 수 없습니다" 로 막혔다(실측 2026-09-20). 판정이 한 곳이어야 또 갈리지 않는다.
     *
     * activeFileRef/sessionsListRef 는 항상 최신을 가리키는 ref 라 deps 에 넣지 않는다.
     */
    const planConnection = useCallback(() => {
        const planTab = activeFileRef.current;
        const planSession = planTab?.sessionId
            ? (sessionsListRef.current || []).find(s => s.sessionId === planTab.sessionId)
            : null;
        return (planSession && connectionsFromStore.find(c => c.id === planSession.connectionId))
            || connectionsFromStore.find(conn => conn.name === activeConnectionName);
    }, [connectionsFromStore, activeConnectionName]);

    // 실행계획 한 건 — 「실행」 설정의 옵션(예상/실제·형식·실측 방식·SQL Monitor 리포트)을 따른다(2026-10-26: SQL 실행계획 분석을 Ctrl+E 에 합쳤다).
    // 실제를 골랐어도 값이 없는 바인드가 있으면 예상으로 뜬다(NULL 로 묶어 실행하면 = NULL 이 0행이라 실측이 거짓이다)
    // override — 이번 한 번만 바꿀 옵션(실행계획 탭의 「SQL Monitor 리포트」 가 실제 + 리포트로 다시 뜰 때). 설정은 바꾸지 않는다
    const runPlan = useCallback(async (stmt, conn, override) => {
        const o = { ...execSettingsRef.current, ...(override || {}) };
        const emptyBinds = Object.entries(stmt.bindVariables || {})
            .filter(([, b]) => !b || b.value === '' || b.value === null || b.value === undefined)
            .map(([name]) => name);
        const wantActual = o.planMode === 'actual';
        const mode = wantActual && emptyBinds.length === 0 ? 'actual' : 'estimated';
        let response;
        if (mode === 'estimated') {
            response = await axiosInstance.post('/get-execution-plan', {
                query: stmt.sql,
                dbType: conn.dbType,
                usrid: user?.email || user?.id || 'standalone',
                // 설정 › 실행 › 실행계획 표(기본 SYS.PLAN_TABLE$). 서버가 식별자로 검사한다
                planTable: o.planTable,
                formatOptions: o.planFormat || 'TYPICAL',
                connectionId: conn?.id || null,
            });
        } else {
            const bindParams = {};
            for (const [varName, { value, type }] of Object.entries(stmt.bindVariables || {})) {
                bindParams[varName] = type === 'NUMBER' && /^\s*-?\d+(\.\d+)?\s*$/.test(String(value)) ? Number(value) : value;
            }
            response = await axiosInstance.post('/get-actual-execution-plan', {
                query: stmt.sql,
                bindParams,
                formatOptions: o.planActualFormat || 'ALLSTATS LAST',
                addGatherStats: true,
                statsMethod: o.planStatsMethod || 'session',
                monitorReport: !!o.planMonitorReport,
                connectionId: conn?.id || null,
            });
        }
        const planData = response.data;
        const monitorReport = (planData && !Array.isArray(planData) && planData.monitorReport) || null;
        const monitorError = (planData && !Array.isArray(planData) && planData.monitorError) || null;
        let planRows;
        if (Array.isArray(planData)) planRows = planData;
        else if (planData && Array.isArray(planData.rows)) planRows = planData.rows;
        else if (typeof planData === 'string') planRows = [{ PLAN_OUTPUT: planData }];
        else planRows = [{ PLAN_OUTPUT: t('common:plan.unparsable') }];
        const planText = planRows.map(row => (typeof row === 'string'
            ? row
            : (row.PLAN_TABLE_OUTPUT || row['QUERY PLAN'] || row.PLAN_OUTPUT || JSON.stringify(row)))).join('\n');
        if (mode === 'actual' && o.planMonitorReport && monitorError) {
            toast.warning(t('common:tun.monitorReportFailed', { reason: t(`common:tun.monitorErr.${monitorError}`, { defaultValue: monitorError }) }));
        }
        return { planText, mode, fellBack: wantActual && mode === 'estimated', emptyBinds, monitorReport, monitorError, planObjects: planObjectsOf(planRows) };
    }, [axiosInstance, user, t]);

    const tuningPrefix = useCallback((mode) => (mode === 'estimated' ? t('common:tuning.estimated') : t('common:tuning.actual')), [t]);

    // 실행계획 보기(Ctrl+E) — 「실행」 설정의 옵션을 따른다(2026-10-26: SQL 실행계획 분석 메뉴를 여기로 합쳤다).
    // 고른 글에 문장이 여럿이면 문장마다 탭 하나. 바인드 값은 편집기에 넣어 둔 값(오른쪽 바인드 변수 섹션)을 쓴다
    const handleExecutePlan = useCallback(async (statementForPlan) => {
        if (!statementForPlan || statementForPlan.trim() === "") {
            toast.warning(t('common:plan.noQuery'));
            return;
        }
        // 탭이 세션에 바인딩돼 있으면 그 세션의 연결로 계획을 뽑는다 — 실행 경로와 동일 대상.
        const activeConnection = planConnection();
        const planBlocked = planBlockReason(activeConnection);
        if (planBlocked) { toast.warning(planBlocked); return; }

        const file = activeFileRef.current;
        const parsed = parseMultiSQLWithBindVariables(statementForPlan, {
            editorBinds: flattenEditorBinds(file && file.bindVariables),
            editorBindTypes: Object.assign({}, ...Object.values((file && file.bindTypes) || {})),
        });
        const stmts = parsed.statements.filter(st => st.sql && st.sql.trim());
        if (!stmts.length) { toast.warning(t('common:plan.noQuery')); return; }

        // 실제 실행계획은 **문장을 실행한다** — 조회가 아니면 데이터가 바뀔 수 있어 먼저 묻는다
        if (execSettingsRef.current.planMode === 'actual') {
            const notQuery = stmts.filter(st => !/^\s*(\(\s*)*(SELECT|WITH)\b/i.test(stripSqlComments(st.sql)));
            if (notQuery.length) {
                const ok = await confirm({ title: t('common:plo.actualConfirmTitle'), message: t('common:tun.actualNote'), confirmText: t('common:plo.actualConfirm'), variant: 'danger' });
                if (!ok) return;
            }
        }

        const newTabs = [];
        const fellBack = [];
        for (const st of stmts) {
            const num = nextExecutionPlanTabIdCounterRef.current;
            nextExecutionPlanTabIdCounterRef.current += 1;
            try {
                const r = await runPlan(st, activeConnection);
                if (r.fellBack) fellBack.push(...r.emptyBinds);
                newTabs.push({
                    id: `execution_plan_${Date.now()}_${num}`,
                    name: r.mode === 'actual' ? `${tuningPrefix('actual')} ${num}` : t('common:plan.tabName', { n: num }),
                    data: r.planText,
                    monitorReport: r.monitorReport,
                    planMode: r.mode,
                    query: st.sql,
                    // 오브젝트 통계도 **이 계획을 만든 연결**에서 조회해야 한다(안 넘기면 우측 패널이 조용히 빈다)
                    connectionId: activeConnection.id,
                    // 계획이 읽는 객체의 소유자 — 오브젝트 상세가 그 스키마의 테이블만 찾는다
                    planObjects: r.planObjects,
                });
            } catch (error) {
                newTabs.push({
                    id: `execution_plan_error_${Date.now()}_${num}`,
                    name: t('common:plan.tabNameError', { n: num }),
                    data: t('common:plan.fetchError', { message: formatApiError(error, t) }),
                    query: st.sql,
                    connectionId: activeConnection.id,
                });
            }
        }
        setNextExecutionPlanTabNum(nextExecutionPlanTabIdCounterRef.current);
        if (fellBack.length) toast.info(t('common:tp.emptyBindsEstimated', { names: [...new Set(fellBack)].join(', ') }));
        setExecutionPlanTabs(prevTabs => pushPlanTabs(prevTabs, newTabs));
        setActiveExecutionPlanTabId(newTabs[0].id);
        setActiveMainTab('executionPlans');
    }, [planConnection, planBlockReason, runPlan, tuningPrefix, confirm, t]);

    // 모니터링 > 오라클 SQL 성능 분석 모달의 "에디터로 이동" — 쿼리는 새 에디터 탭,
    // 실행계획은 실행계획 탭에 push (handleExecutePlan 의 탭 생성 패턴을 그대로 따름).
    const handleOpenSqlPerfInEditor = useCallback(({ sqlText, planText, label, monitorReport } = {}) => {
        if (sqlText) {
            const newFile = createNewFile(activeProjectId);
            newFile.content = sqlText;
            setOpenFiles(prevFiles => [newFile, ...prevFiles]);
            setActiveFileId(newFile.id);
        }
        if (planText) {
            const newTabId = `sql_perf_plan_${Date.now()}`;
            const newTab = {
                id: newTabId,
                name: label || t('common:analysis.perfTitle'),
                data: planText,
                // 수집이 받아 둔 SQL Monitor 리포트도 탭에 싣는다 — 보관함에 저장하면 함께 남는다(2026-10-07)
                monitorReport: monitorReport || null,
                query: sqlText || '',
            };
            setExecutionPlanTabs(prevTabs => {
                return pushPlanTabs(prevTabs, newTab);
            });
            setActiveExecutionPlanTabId(newTabId);
            setActiveMainTab('executionPlans');
        } else {
            setActiveMainTab('queryResults');
        }
    }, [createNewFile, activeProjectId]);

    // SQL 튜닝: 에디터에서 가져오기
    // DB 연결 관리 핸들러
    const handleConnectionEstablished = useCallback((connInfo) => {
        // connInfo: { id, name, dbType }
        setActiveConnectionId(connInfo.id);
        setActiveConnectionName(connInfo.name);

        // ref와 schemaActions를 즉시 업데이트 (useEffect 대기 없이)
        activeConnectionIdRef.current = connInfo.id;
        setActiveConnectionIdForSchema(connInfo.id);

        // Redux connections 목록에 이 연결이 없으면 추가
        const exists = connectionsFromStore.find(c => c.id === connInfo.id);
        if (!exists) {
            const newConnections = [...connectionsFromStore, {
                id: connInfo.id,
                name: connInfo.name,
                type: 'connection',
                dbType: connInfo.dbType || 'oracle',
                connType: connInfo.connType || 'dev',
                // 읽기 전용 여부는 **서버가 이 사람 기준으로 정해 준다** — 여기서 빠뜨리면
                // 목록에서 붙인 연결에만 배지가 없어 "어떤 건 되고 어떤 건 안 되는" 화면이 된다
                readOnly: !!connInfo.readOnly,
                readOnlyReason: connInfo.readOnlyReason || null,
                children: null,
                isLoadingSchemas: false,
                schemasError: null,
            }];
            dispatch({ type: 'SET_CONNECTIONS', payload: newConnections });
        }

        // 스키마 로드 (Redux 업데이트 반영 대기)
        setTimeout(() => {
            dispatch(fetchSchemasForConnection(connInfo.id, connInfo.dbType || 'oracle'));
        }, 300);
        toast.success(t('common:conn.connected', { name: connInfo.name }));
    }, [connectionsFromStore, dispatch]);

    /*
      연결 관리 창에서 저장했을 때 — 서버 목록의 값으로 앱의 연결 목록(redux)을 고친다.
      앱의 목록은 로그인할 때 한 번만 받고, 연결 관리 창은 제 상태(목록)만 갱신하며, 「연결」 도
      이미 있는 항목은 건드리지 않았다. 그래서 연결 유형을 운영→개발로 바꿔 저장하고 다시 연결해도
      실행 전 확인(confirmRiskyExecution)·색 띠·세션 고르기의 배지가 옛 「운영」 을 봤다(신고 2026-11-02).
      이미 있는 항목만 고치고(추가·삭제는 하지 않는다), 화면 상태(스키마 트리 등)는 그대로 둔다.
    */
    const handleConnectionsSaved = useCallback((serverList) => {
        if (!Array.isArray(serverList) || serverList.length === 0) return;
        const byId = new Map(serverList.filter(c => c && c.id).map(c => [c.id, c]));
        dispatch((d, getState) => {
            const cur = getState().schema.connections || [];
            let changed = false;
            const next = cur.map(c => {
                const s = byId.get(c.id);
                if (!s) return c;
                changed = true;
                // eslint-disable-next-line no-unused-vars
                const { children, isConnected, isLoadingSchemas, schemasError, type, ...serverFields } = s;
                return { ...c, ...serverFields };
            });
            if (changed) d({ type: 'SET_CONNECTIONS', payload: next });
        });
    }, [dispatch]);

    /** 편집기 파일에 저장된 바인드 값 — 가져오기가 그 값을 함께 가져온다(이름 → 값). */
    const editorBindValues = useCallback(() => {
        const file = activeFileRef.current;
        return flattenEditorBinds(file && file.bindVariables);
    }, []);

    /** SQL 튜닝·SQL 작성 창의 「편집기 커서 위치 SQL 가져오기」 — 실행·실행계획보기와 같은 문장(statementAtCursor) + 편집기 바인드 값 */
    const getEditorSqlForTuning = useCallback(() => {
        const view = sqlEditorRef.current;
        if (!view || !view.state) return null;
        const sel = view.state.selection.main;
        const selected = view.state.doc.sliceString(sel.from, sel.to);
        const st = selected.trim() ? { text: selected.trim() } : statementAtCursor(view.state.doc.toString(), sel.head);
        if (!st || !st.text) return null;
        return { sql: st.text.replace(/;\s*$/, ''), binds: editorBindValues() };
    }, [sqlEditorRef, editorBindValues]);

    // 실행계획 노드 클릭 → 에디터 해당 위치 하이라이트
    const handlePlanNodeClick = useCallback((node, planTab) => {
        console.log('[App] handlePlanNodeClick called:', node.id, node.operation, node.objectName);

        if (activePlanNodeId === node.id) {
            clearPlanHighlight();
            return;
        }
        setActivePlanNodeId(node.id);

        const planQuery = planTab?.query || '';
        console.log('[App] planTab.query:', planQuery.substring(0, 80));
        if (!planQuery) {
            clearPlanHighlight();
            return;
        }

        const editorContent = sqlEditorRef.current?.state?.doc?.toString() || '';
        console.log('[App] editorContent:', editorContent.substring(0, 80));
        const queryOffset = editorContent.toUpperCase().indexOf(planQuery.toUpperCase());
        const baseOffset = queryOffset >= 0 ? queryOffset : 0;
        const targetSql = queryOffset >= 0 ? planQuery : editorContent;

        const parsed = parseXplanText(planTab.data);
        console.log('[App] parsed nodes:', parsed.nodes.length, 'aliases:', parsed.aliases);
        const mapping = buildPlanToSqlMapping(parsed, targetSql);
        const rawHighlights = mapping.get(node.id) || [];
        console.log('[App] raw highlights for node', node.id, ':', rawHighlights);
        const highlights = rawHighlights.map(h => ({
            ...h,
            from: h.from + baseOffset,
            to: h.to + baseOffset,
        }));
        console.log('[App] final highlights (with offset', baseOffset, '):', highlights);
        setEditorHighlightRanges(highlights);
        setCloneHighlightRanges(highlights);
    }, [activePlanNodeId, sqlEditorRef, clearPlanHighlight]);

    // 실행계획 탭이 하나도 없으면 하이라이트도 없어야 한다 — 탭을 닫았는데 색칠만 남아 있으면
    // 무엇을 가리키는지 알 수 없다(신고 2026-09-18). 활성 편집기 파일이 바뀔 때도 같다.
    useEffect(() => {
        clearPlanHighlight();
    }, [executionPlanTabs.length === 0, activeFileId, clearPlanHighlight]);

    const executeSQLEditorPlan = useCallback(() => {
        // 임시패스워드 상태에서는 실행 차단
        if (isTemporaryPassword) {
            toast.warning(t('common:error.tempPassword'));
            return;
        }
        
        const editorView = sqlEditorRef.current;
        if (!editorView || !editorView.state) {
            toast.info(t('common:run.editorNotReady'));
            return;
        }

        // 편집기의 「현재 문장」과 같은 함수(utils/statementAtCursor) — 사본을 두면 실행과 실행계획이 다른 문장을 고른다
        const sel = editorView.state.selection.main;
        const selectedText = editorView.state.doc.sliceString(sel.from, sel.to);
        const st = selectedText.trim()
            ? { text: selectedText.trim() }
            : statementAtCursor(editorView.state.doc.toString(), sel.head);
        if (st && st.text) {
            handleExecutePlan(st.text);
        } else {
            toast.info(t('common:plan.noStatement'));
        }
    }, [handleExecutePlan, sqlEditorRef]);

    // 현재 프로젝트에 속한 탭만 노출 (visibleFiles). projectId 가 없는 탭은 기본 작업공간(null).
    const visibleFiles = useMemo(() => {
        return (openFiles || []).filter(f => (f.projectId || null) === activeProjectId);
    }, [openFiles, activeProjectId]);

    // 책갈피 — 같은 프로젝트(또는 개별 스크립트)의 모든 탭을 **탭 순서대로**(utils/sqlBookmarks).
    // 현재 탭을 맨 앞으로 끌어올리지 않는다 — 탭을 옮길 때마다 목록 순서가 바뀌어 찾던 자리를 잃었다(시험 피드백 2026-11-03)
    const bookmarkGroups = useMemo(() => visibleFiles
        .map(f => ({
            fileId: f.id,
            fileName: f.name,
            projectId: f.projectId || null,
            isActive: f.id === activeFileId,
            items: parseBookmarks(f.content || ''),
        }))
        .filter(g => g.items.length > 0), [visibleFiles, activeFileId]);

    // activeFile 계산을 useMemo로 최적화 (현재 프로젝트 내에서만 활성 허용)
    const activeFile = useMemo(() => {
        return visibleFiles.find(file => file.id === activeFileId);
    }, [visibleFiles, activeFileId]);

    // 다른 작업 탭에서 편집기에 SQL 을 넣거나 새 편집기 탭을 열면(SQL 작성 「편집기 새 탭」 · 빌더 반영 · AI 삽입 …) SQL 편집 탭을 앞으로 —
    // 안 그러면 넣은 것이 가려진 탭에 들어가 아무 일도 없었던 것처럼 보인다. 기동 직후 복원 중에는 부르지 않는다(activateSoft)
    const wsActivateSoft = workspace ? workspace.activateSoft : null;
    const activeFileContent = activeFile ? activeFile.content : undefined;
    const prevEditRef = useRef(null);
    useEffect(() => {
        const prev = prevEditRef.current;
        prevEditRef.current = { id: activeFileId, content: activeFileContent };
        if (!prev || !wsActivateSoft) return;
        if (prev.id !== activeFileId || prev.content !== activeFileContent) wsActivateSoft(SQL_TAB);
    }, [activeFileId, activeFileContent, wsActivateSoft]);

    // activeFileId 가 현재 프로젝트에 없으면 첫 번째 파일로 보정 (프로젝트 전환 시)
    // **보이는 탭이 하나도 없으면 빈 탭을 만든다**(신고 2026-10-26) — 개별 스크립트를 「다른 이름으로 저장」 하면 그 탭들이 새 프로젝트로 옮겨 가
    // 개별 스크립트가 빈 채로 남았고, 화면은 탭 없이 다른 프로젝트의 첫 탭을 편집기에 띄워 거기에 친 글이 **남의 프로젝트 탭을 덮었다**.
    useEffect(() => {
        if (visibleFiles.length === 0) {
            if (openFiles.length === 0) return;
            // 사라진 프로젝트를 가리키면 개별 스크립트로(그 자리에 탭을 만들면 보이지 않는 프로젝트에 탭이 생긴다)
            if (activeProjectId && !projects.some(p => p.id === activeProjectId)) { setActiveProjectId(null); return; }
            const blank = createNewFile(activeProjectId);
            setOpenFiles(prev => [blank, ...prev]);
            setActiveFileId(blank.id);
            return;
        }
        if (!visibleFiles.find(f => f.id === activeFileId)) {
            setActiveFileId(visibleFiles[0].id);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 보이는 탭·활성 탭이 바뀔 때만 본다
    }, [visibleFiles, activeFileId]);

    // 현재 활성 프로젝트 이름 (null = 기본 작업공간)
    const currentProjectName = useMemo(() => {
        if (!activeProjectId) return null;
        return projects.find(p => p.id === activeProjectId)?.name || null;
    }, [activeProjectId, projects]);

    // 현재 연결의 스키마 목록
    const currentSchemas = useMemo(() => {
        return connectionsFromStore.find(c => c.name === activeConnectionName)?.schemas || [];
    }, [connectionsFromStore, activeConnectionName]);

    const handleSaveFile = useCallback(async () => {
        if (!activeFile) return;
        let fileName = activeFile.name;
        // 스탠드얼론은 저장 대화상자가 이름까지 받으므로 따로 묻지 않는다
        if (!activeFile.path && !canWriteDisk()) {
            const newName = await prompt({
                title: t('common:file.nameTitle'),
                message: t('common:file.namePrompt'),
                defaultValue: activeFile.name,
                placeholder: 'myQuery.sql',
            });
            if (!newName) return;
            fileName = newName.endsWith('.sql') ? newName : `${newName}.sql`;
        }

        try {
            // 바인드 변수 존재 여부 확인
            const hasBindVariables = activeFile.bindVariables && Object.keys(activeFile.bindVariables).length > 0;

            let fileContent;
            let successMessage;

            if (hasBindVariables) {
                // 하이브리드 형식으로 저장 (.sql 확장자 유지)
                const bindVariableData = {
                    version: "1.0",
                    bindVariables: activeFile.bindVariables,
                    metadata: {
                        originalFileName: activeFile.name,
                        savedAt: new Date().toISOString()
                    }
                };

                fileContent = `${activeFile.content.trim()}

-- LIVESQL_BIND_VARIABLES_START
${JSON.stringify(bindVariableData, null, 2)}
-- LIVESQL_BIND_VARIABLES_END`;

                successMessage = t('common:file.savedWithBinds', { name: fileName });
            } else {
                // 일반 SQL 파일로 저장
                fileContent = activeFile.content;
                successMessage = t('common:file.saved', { name: fileName });
            }

            // 스탠드얼론: 저장한 적 있는 경로(diskPath)면 묻지 않고 덮어쓴다 / 웹: 내려받기
            const savedContent = activeFile.content;
            const r = await saveTextFile({
                fileName: /\.(sql|txt)$/i.test(fileName) ? fileName : `${fileName}.sql`,
                content: fileContent,
                kind: 'sql',
                diskPath: activeFile.diskPath,
            });
            if (!r || !r.ok) {
                if (!(r && r.canceled)) toast.error(t('common:file.saveFailed'));
                return;
            }
            if (r.path) {
                // 대화상자에서 고른 이름이 탭 이름이 된다
                fileName = r.path.split(/[\\/]/).pop() || fileName;
                successMessage = hasBindVariables
                    ? t('common:file.savedWithBinds', { name: fileName })
                    : t('common:file.saved', { name: fileName });
            }

            toast.success(successMessage);

            setOpenFiles(prevFiles =>
                prevFiles.map(file =>
                    file.id === activeFileId
                        ? {
                            ...file,
                            // 저장하는 동안 또 고쳤으면 고침 표시는 남긴다
                            isDirty: file.content === savedContent ? false : file.isDirty,
                            name: fileName,
                            path: fileName,
                            ...(r.path ? { diskPath: r.path } : {}),
                        }
                        : file
                )
            );
        } catch (error) {
            toast.error(t('common:file.saveFailed'));
        }
    }, [activeFile, activeFileId, prompt, t]);

    const handleNewTab = useCallback(() => {
        const newFile = createNewFile(activeProjectId); // 활성 프로젝트에 귀속
        // 새 탭은 맨 앞에 추가(왼쪽), 기존 탭들은 오른쪽으로 밀림
        setOpenFiles(prevFiles => [newFile, ...prevFiles]);
        setActiveFileId(newFile.id);
    }, [createNewFile, activeProjectId]);

    /**
     * 튜닝 사례 초안을 새 탭에 띄운다 (명세 4 "초안 내용을 에디터에 뿌리고 사용자가 확인").
     * 채팅으로는 긴 문서를 검토할 수 없다. 사용자가 읽고 피드백을 주면 에이전트가 고친다.
     */
    // 사례 초안은 이제 **사례 등록 화면**으로 간다(AssistantPanel 이 case_draft 를 직접 받는다).
    // 편집기 탭에 마크다운을 뿌리던 경로는 없앴다 — 거기서는 저장할 수가 없어서, 사용자가 다시
    // 모델에게 "저장해" 라고 말해야 했다. 두 경로를 남기면 어디서 저장됐는지 알 수 없다.

    // 실행계획 우측의 오브젝트 통계는 **그 계획을 만든 연결**에서 조회해야 한다.
    // 안 넘기면 서버가 400(MISSING_CONNECTION)을 주고 패널이 조용히 빈다 — 실제로 그랬다.
    const activePlanConnectionId = useMemo(
        () => (executionPlanTabs.find(tb => tb.id === activeExecutionPlanTabId) || {}).connectionId,
        [executionPlanTabs, activeExecutionPlanTabId],
    );

    const handleOpenFile = useCallback(() => {
        fileInputRef.current.click();
    }, []);

    // ===== 프로젝트(스크립트 그룹) 저장/불러오기 — Orange 스타일 =====

    // openFiles 배열을 프로젝트 포맷(JSON)으로 직렬화
    // 형식 자체는 utils/projectFile.js 한 곳에만 있다. 여기서는 **저장할 때 되돌려 줄 미지 필드**만
    // 챙긴다 — 열 때 파일에 있던 이 빌드가 모르는 최상위 값(carry)을 프로젝트에 매달아 뒀다.
    const serializeProject = useCallback((files, projectName) => {
        const carry = (projects.find(p => p.id === activeProjectId) || {}).carry;
        return serializeProjectFile(files, projectName || t('common:ui.project'), carry);
    }, [projects, activeProjectId, t]);

    // 프로젝트 파일(JSON 문자열) 파싱 — 유효성 검증 + 정규화
    const parseProjectFile = useCallback((text) => parseProjectFileText(text, t), [t]);

    // 프로젝트 파일 쓰기 — 스탠드얼론은 디스크에 직접(이미 저장한 경로면 묻지 않음), 웹은 내려받기.
    const writeProjectFile = useCallback((projectName, files, { diskPath, saveAs } = {}) => {
        const json = serializeProject(files, projectName);
        const safe = (projectName || t('common:ui.project')).replace(/[\\/:*?"<>|]/g, '_');
        const fileName = isProjectFileName(safe) ? safe : `${safe}${PROJECT_EXT}`;
        return saveTextFile({ fileName, content: json, kind: 'project', diskPath, saveAs });
    }, [serializeProject, t]);

    // 저장한 탭의 「고침」 표시를 내린다 — 안 내리면 저장 직후에도 탭에 ● 이 남고, 프로젝트를 닫을 때
    // 「저장하지 않은 내용」 확인이 또 뜬다(신고 2026-10-01). 저장한 **그 탭들만**: 저장 뒤에 고친
    // 다른 탭까지 내리면 안 된다. 내용이 저장 시점과 같은지까지 본다(저장 중에 또 고쳤을 수 있다).
    const markSaved = useCallback((savedFiles) => {
        const savedContent = new Map(savedFiles.map(f => [f.id, f.content]));
        setOpenFiles(prev => prev.map(f => (
            savedContent.has(f.id) && savedContent.get(f.id) === f.content && f.isDirty
                ? { ...f, isDirty: false } : f
        )));
    }, []);

    /**
     * 현재 활성 프로젝트 저장 — 「저장」 과 「다른 이름으로 저장」 이 같은 길을 쓴다.
     *
     * - 스탠드얼론: 저장한 적 있는 프로젝트(`diskPath`)는 묻지 않고 같은 파일에 덮어쓴다. 처음이거나
     *   「다른 이름으로」 면 저장 대화상자 하나만 띄우고, 프로젝트 이름은 고른 파일 이름을 따른다
     *   (이름을 묻고 또 위치를 묻던 두 번을 한 번으로).
     * - 웹: 이름을 묻고 내려받는다(예전 그대로).
     * - **실제로 쓴 뒤에만** 기본 작업공간을 프로젝트로 올리고 고침 표시를 내린다 — 대화상자를 닫았는데
     *   「저장됨」 이 되면 안 된다.
     */
    const saveActiveProject = useCallback(async ({ saveAs = false } = {}) => {
        const filesToSave = (openFiles || []).filter(f => (f.projectId || null) === activeProjectId);
        if (filesToSave.length === 0) {
            toast.warn(t('common:file.noTabToSave'));
            return;
        }
        const proj = projects.find(p => p.id === activeProjectId) || null;
        const disk = canWriteDisk();
        let name = currentProjectName;
        if (!disk && (saveAs || !name)) {
            const input = await prompt({
                title: saveAs ? t('common:ui.projectSaveAs') : t('common:project.nameTitle'),
                message: saveAs ? t('common:project.saveAsPrompt') : t('common:project.namePrompt'),
                defaultValue: name || t('common:ui.myProject'),
                placeholder: t('common:ui.myProject'),
            });
            if (!input) return;
            name = input.trim() || t('common:ui.myProject');
        }
        let r;
        try {
            r = await writeProjectFile(name || t('common:ui.myProject'), filesToSave, { diskPath: proj && proj.diskPath, saveAs });
        } catch (err) {
            toast.error(t('common:project.saveFailed', { message: err.message }));
            return;
        }
        if (!r || !r.ok) {
            if (r && r.canceled) return;
            toast.error(t('common:project.saveFailed', { message: (r && (r.detail || r.reason)) || '' }));
            return;
        }
        // 대화상자에서 고른 파일 이름이 곧 프로젝트 이름이다(덮어쓰기면 이름을 바꾸지 않는다)
        const pickedNew = r.path && (!proj || r.path !== proj.diskPath);
        if (pickedNew || !name) name = baseNameOf(r.path) || name || t('common:ui.myProject');
        const diskPath = r.path || (proj && proj.diskPath) || undefined;
        if (activeProjectId) {
            setProjects(prev => prev.map(p => p.id === activeProjectId ? { ...p, name, ...(diskPath ? { diskPath } : {}) } : p));
        } else {
            // 기본 작업공간을 새 프로젝트로 승격 — 해당 파일들에 projectId 부여
            const newId = `proj_${Date.now()}`;
            setProjects(prev => [...prev, { id: newId, name, ...(diskPath ? { diskPath } : {}) }]);
            setOpenFiles(prev => prev.map(f => ((f.projectId || null) === null ? { ...f, projectId: newId } : f)));
            setActiveProjectId(newId);
        }
        markSaved(filesToSave);
        toast.success(t('common:project.savedCount', { name, count: filesToSave.length }));
        return true;
    }, [openFiles, activeProjectId, projects, currentProjectName, writeProjectFile, markSaved, prompt, t]);

    const handleSaveProject = useCallback(() => saveActiveProject(), [saveActiveProject]);
    // 프로젝트 다른 이름으로 저장 — 새 이름으로 현재 활성 프로젝트의 이름도 변경
    const handleSaveProjectAs = useCallback(() => saveActiveProject({ saveAs: true }), [saveActiveProject]);

    // 불러온 프로젝트를 새 프로젝트로 추가 (기존 탭·프로젝트는 유지)
    const applyLoadedProject = useCallback(({ projectName, files, carry, tooNew, docVersion, diskPath }) => {
        if (!files || files.length === 0) {
            toast.warn(t('common:project.empty'));
            return;
        }
        // 이 빌드보다 새 형식 — 값은 그대로 보존해 되돌려 주지만, 무엇을 못 알아봤을 수 있으므로 알린다
        if (tooNew) toast.warn(t('common:project.tooNew', { version: docVersion }));
        const projectId = `proj_${Date.now()}`;
        // 탭 id 는 이미 열린 탭과 겹치지 않게(같은 프로젝트를 잇달아 열어도) — utils/tabIds.js
        const tagged = [];
        for (const f of files) tagged.push({ ...f, id: uniqueTabId(f.id, [...openFilesRef.current, ...tagged]), projectId });
        // carry — 파일에 있던 이 빌드가 모르는 최상위 필드. 저장할 때 그대로 되돌려 준다.
        setProjects(prev => [...prev, { id: projectId, name: projectName || t('common:ui.project'), carry, ...(diskPath ? { diskPath } : {}) }]);
        setOpenFiles(prev => [...prev, ...tagged]);
        setActiveProjectId(projectId);
        setActiveFileId(tagged[0].id);
        toast.success(t('common:project.loaded', { name: projectName, count: files.length }));
    }, []);

    // 프로젝트 로드는 곧바로 새 프로젝트로 추가 (기존 작업 보존) — 별도 확인 불필요
    const loadProjectWithConfirm = useCallback(async (parsed) => {
        applyLoadedProject(parsed);
    }, [applyLoadedProject]);

    /**
     * 동봉된 실습 샘플을 연다 — 「파일 › 샘플 프로젝트 열기」.
     *
     * 파일에서 읽은 프로젝트와 **똑같은 길**로 들어간다(`parseProjectFile` → `applyLoadedProject`).
     * 그래야 파서가 두 벌이 되지 않고, 연 뒤에는 보통 프로젝트처럼 고치고 따로 저장할 수 있다.
     * 서버를 거치지 않으므로 내부망에서도 그대로 열린다.
     */
    const handleOpenSampleProject = useCallback(async () => {
        try {
            // 번들에 포함한다 — 스탠드얼론은 file:// 로 떠서 런타임 fetch 가 막힌다
            const sample = (await import('./samples/eduSampleProject.json')).default;
            const parsed = parseProjectFile(JSON.stringify(sample));
            await loadProjectWithConfirm(parsed);
            toast.success(t('common:sample.opened', { name: parsed.projectName }));
        } catch (err) {
            console.error('[App] 샘플 프로젝트 열기 실패:', err);
            toast.error(t('common:sample.openFailed', { message: err.message }));
        }
    }, [loadProjectWithConfirm, parseProjectFile, t]);

    // 프로젝트 전환
    const handleSwitchProject = useCallback((projectId) => {
        setActiveProjectId(projectId || null);
    }, []);

    // 전역 검색 결과 클릭 → 해당 프로젝트/파일로 이동 + 특정 라인으로 스크롤
    const handleGlobalSearchNavigate = useCallback(({ projectId, fileId, lineNumber }) => {
        setActiveProjectId(projectId || null);
        setActiveFileId(fileId);
        setShowGlobalSearch(false);
        // 탭 전환 후 에디터가 다시 마운트/리렌더된 뒤 해당 라인으로 스크롤
        setTimeout(() => {
            const view = sqlEditorRef.current;
            if (!view || !lineNumber) return;
            try {
                const totalLines = view.state.doc.lines;
                const targetLine = Math.min(Math.max(1, lineNumber), totalLines);
                const line = view.state.doc.line(targetLine);
                view.dispatch({
                    selection: { anchor: line.from, head: line.from },
                    scrollIntoView: true
                });
                view.focus();
            } catch (e) {
                // 에디터가 아직 준비되지 않은 경우는 조용히 무시
            }
        }, 120);
    }, []);

    // 프로젝트 닫기 — 해당 프로젝트의 모든 탭을 openFiles 에서 제거
    const handleCloseProject = useCallback(async (projectId) => {
        if (!projectId) return; // 기본 작업공간은 닫을 수 없음
        const projFiles = (openFiles || []).filter(f => f.projectId === projectId);
        const hasDirty = projFiles.some(f => f.isDirty);
        if (hasDirty) {
            // 「저장 후 닫기」(사용자 요청 2026-10-26) — 저장이 실제로 끝났을 때만 닫는다(대화상자를 닫거나 실패하면 그대로 둔다).
            // 저장은 **활성 프로젝트**를 저장하는 길이라, 닫는 프로젝트가 활성이 아닐 때는 이 단추를 내지 않는다
            const canSave = projectId === activeProjectId;
            const ok = await confirm({
                title: t('common:ui.projectClose'),
                message: t('common:project.unsavedConfirm'),
                confirmText: canSave ? t('common:project.closeWithoutSave') : t('common:ui.close'),
                cancelText: t('common:ui.cancel'),
                variant: 'danger',
                ...(canSave ? { extraText: t('common:project.saveAndClose') } : {}),
            });
            if (!ok) return;
            if (ok === 'extra') {
                const saved = await saveActiveProject();
                if (!saved) return;
            }
        }
        // 남는 탭이 하나도 없으면 빈 새 탭을 만든다 — 탭이 0개면 화면이 「Loading or initializing...」
        // 에서 멈춘다(신고 2026-10-01: 프로젝트를 닫았더니 빈 화면). 탭 닫기의 마지막 탭과 같은 처리.
        const remaining = (openFiles || []).filter(f => f.projectId !== projectId);
        const firstDefault = remaining.find(f => (f.projectId || null) === null);
        const blank = firstDefault ? null : takeBlankTab();
        setOpenFiles(blank ? [...remaining, blank] : remaining);
        setProjects(prev => prev.filter(p => p.id !== projectId));
        if (activeProjectId === projectId || blank) {
            setActiveProjectId(null);
            setActiveFileId((firstDefault || blank).id);
        }
    }, [openFiles, confirm, activeProjectId, takeBlankTab, saveActiveProject]);

    // 에디터 편집 기능 핸들러들
    const handleEditorUndo = useCallback(() => {
        const editorView = sqlEditorRef.current;
        if (editorView) {
            import('@codemirror/commands').then(({ undo }) => {
                undo(editorView);
            });
        }
    }, []);

    const handleEditorRedo = useCallback(() => {
        const editorView = sqlEditorRef.current;
        if (editorView) {
            import('@codemirror/commands').then(({ redo }) => {
                redo(editorView);
            });
        }
    }, []);

    const handleEditorCopy = useCallback(async () => {
        const editorView = sqlEditorRef.current;
        if (editorView) {
            const selection = editorView.state.selection.main;
            const selectedText = editorView.state.doc.sliceString(selection.from, selection.to);
            if (selectedText) {
                try {
                    await navigator.clipboard.writeText(selectedText);
                    console.log('✅ 복사 완료:', selectedText.length, '자');
                } catch (error) {
                    console.error('❌ 복사 실패:', error);
                }
            }
        }
    }, []);

    const handleEditorCut = useCallback(async () => {
        const editorView = sqlEditorRef.current;
        if (editorView) {
            const selection = editorView.state.selection.main;
            const selectedText = editorView.state.doc.sliceString(selection.from, selection.to);
            if (selectedText) {
                try {
                    await navigator.clipboard.writeText(selectedText);
                    editorView.dispatch({
                        changes: { from: selection.from, to: selection.to, insert: '' }
                    });
                    console.log('✅ 잘라내기 완료:', selectedText.length, '자');
                } catch (error) {
                    console.error('❌ 잘라내기 실패:', error);
                }
            }
        }
    }, []);

    // SQLEditor 내부 handleContextMenuAction을 트리거하는 디스패처 (window event 기반)
    const dispatchEditorAction = useCallback((action) => {
        window.dispatchEvent(new CustomEvent('livesql-editor-action', { detail: { action } }));
    }, []);
    const handleEditorFormat = useCallback(() => dispatchEditorAction('format'), [dispatchEditorAction]);
    const handleEditorUppercase = useCallback(() => dispatchEditorAction('uppercase'), [dispatchEditorAction]);
    const handleEditorLowercase = useCallback(() => dispatchEditorAction('lowercase'), [dispatchEditorAction]);
    const handleEditorToggleComment = useCallback(() => dispatchEditorAction('toggleComment'), [dispatchEditorAction]);

    const handleEditorPaste = useCallback(async () => {
        const editorView = sqlEditorRef.current;
        if (editorView) {
            try {
                const clipboardText = await navigator.clipboard.readText();
                if (clipboardText) {
                    const selection = editorView.state.selection.main;
                    editorView.dispatch({
                        changes: { from: selection.from, to: selection.to, insert: clipboardText }
                    });
                    console.log('✅ 붙여넣기 완료:', clipboardText.length, '자');
                }
            } catch (error) {
                console.error('❌ 붙여넣기 실패:', error);
            }
        }
    }, []);

    // 쿼리 실행 중지 (탭별).
    //
    // **HTTP 를 끊는 것만으로는 중지가 아니다.** 그러면 화면에서만 사라지고 쿼리는 DB 에서 계속 돈다 —
    // 무거운 쿼리를 몇 번 다시 돌리면 DB 쪽에 그대로 쌓인다. 그래서 세션에 `break` 를 먼저 보낸다
    // (`/sessions/:id/cancel` → `adapter.cancelQuery` → Oracle `connection.break()`).
    //
    // 응답은 기다리지 않는다. break 는 최대 30초까지 걸릴 수 있는데(`breakTimeoutMs`),
    // **누른 순간 화면은 멈춰야** 사용자가 중지됐다고 믿을 수 있다. 취소 결과(미지원·강제 종료)는
    // `handleCancelSession` 이 토스트로 알린다.
    const handleStopQuery = useCallback((tabId) => {
        const entry = abortControllersRef.current[tabId];
        if (!entry) return;
        delete abortControllersRef.current[tabId];
        toast.info(t('common:run.stopRequested'));
        if (entry.sessionId) handleCancelSession({ sessionId: entry.sessionId });
        entry.controller.abort();
    }, [handleCancelSession, t]);

    // 쿼리 실행 모니터링 — 결과 탭의 「모니터링」. 그 탭을 실행 중인 세션과 문장으로 창을 연다(components/ExecMonitorModal)
    const [execMonitorTarget, setExecMonitorTarget] = useState(null);
    const handleMonitorQuery = useCallback((tabId) => {
        const tab = (queryResultTabs || []).find(x => x.id === tabId);
        if (!tab || !tab.sessionId) return;
        setExecMonitorTarget({ sessionId: tab.sessionId, sql: tab.sourceSql || tab.query || '', nonce: Date.now() });
        // 이미 열려 있으면 열림 상태가 바뀌지 않아 탭이 앞으로 오지 않는다 — 제목으로 불러낸다(2026-10-26 신고)
        setTimeout(() => { if (workspace) workspace.activateByTitle(t('common:emon.title')); }, 0);
    }, [queryResultTabs, workspace, t]);

    // 방금 실행한 문장 다시 보기 — 결과 영역의 「실행계획(실제)」 · 「SQL Monitor 리포트」(백엔드 database/lastExec.js).
    // 문장을 **다시 실행하지 않는다** — 그 세션에서 이미 돈 커서를 읽는다. 세션이 그 뒤 다른 문장을 돌렸으면 서버가 그렇게 말한다.
    const handleActualPlan = useCallback(async (tabId) => {
        const tab = (queryResultTabs || []).find(x => x.id === tabId);
        if (!tab || !tab.sessionId) return;
        const sql = tab.sourceSql || tab.query || '';
        try {
            const out = await lastActualPlan(tab.sessionId, sql);
            const rows = out.rows || [];
            const n = nextExecutionPlanTabIdCounterRef.current;
            nextExecutionPlanTabIdCounterRef.current += 1;
            setNextExecutionPlanTabNum(nextExecutionPlanTabIdCounterRef.current);
            const newTabId = `actual_plan_${Date.now()}_${n}`;
            const sess = (sessionsListRef.current || []).find(x => x.sessionId === tab.sessionId);
            setExecutionPlanTabs(prev => pushPlanTabs(prev, {
                id: newTabId,
                name: t('common:lastExec.tabName', { n }),
                data: rows.map(r => r.PLAN_TABLE_OUTPUT).join(String.fromCharCode(10)),
                query: sql,
                connectionId: (sess && sess.connectionId) || null,
                planObjects: planObjectsOf(rows),
            }));
            setActiveExecutionPlanTabId(newTabId);
            setActiveMainTab('executionPlans');
            // 줄별 실측이 없는 계획이면(통계를 모으기 전에 돈 문장) 그 사실을 말한다 — 빈 A-Rows 가 「0행」 으로 읽히면 안 된다
            if (!out.hasRowStats) toast.warning(t('common:lastExec.noRowStats'));
            else if (out.exec && !out.exec.pinned) toast.info(t('common:lastExec.notPinned'));
        } catch (err) { toast.error(formatApiError(err, t)); }
    }, [queryResultTabs, t]);

    const [monitorReportData, setMonitorReportData] = useState(null);
    const handleMonitorReport = useCallback(async (tabId) => {
        const tab = (queryResultTabs || []).find(x => x.id === tabId);
        if (!tab || !tab.sessionId) return;
        const sql = tab.sourceSql || tab.query || '';
        // Tuning Pack 라이선스 기능 — 처음 한 번은 묻는다(이 브라우저에서). 그 뒤로는 단추를 누른 것이 곧 선택이다
        let acked = false;
        try { acked = localStorage.getItem('livesql_sqlmon_report_ack') === '1'; } catch (_e) { /* 저장소를 못 써도 묻고 간다 */ }
        if (!acked) {
            const ok = await confirm({
                title: t('common:lastExec.packTitle'),
                message: t('common:lastExec.packMessage'),
                confirmText: t('common:lastExec.packConfirm'),
                cancelText: t('common:ui.cancel'),
            });
            if (!ok) return;
            try { localStorage.setItem('livesql_sqlmon_report_ack', '1'); } catch (_e) { /* 다음에도 묻는다 */ }
        }
        setMonitorReportData({ sql, loading: true });
        // 이미 열린 리포트 탭이면 열림이 바뀌지 않아 앞으로 오지 않았다 — 제목으로 불러낸다(2026-10-26 신고: 두 번째부터 탭이 바뀌지 않았다)
        setTimeout(() => { if (workspace) workspace.activateByTitle(t('common:lastExec.reportTitle')); }, 0);
        try {
            const out = await lastSqlMonitorReport(tab.sessionId, sql);
            setMonitorReportData({ sql, report: out.report, exec: out.exec, pinned: out.pinned });
        } catch (err) {
            setMonitorReportData({ sql, error: formatApiError(err, t) });
        }
    }, [queryResultTabs, confirm, t, workspace]);

    // 실행계획 탭의 「SQL Monitor 리포트」(2026-10-29 요청 — 진단 리포트 오른쪽). 실행계획 「실제」 는 세션이 아닌 풀 연결로 돌아
    // 결과 탭처럼 「방금 실행한 문장」 을 집을 수 없다 — 받아 둔 리포트가 있으면 그것을 열고, 없으면 묻고 나서
    // 그 문장을 실제 + 리포트로 **다시** 떠서(runPlan 한 길) 탭을 갈아 끼우고 연다
    const handlePlanMonitorReport = useCallback(async (tab) => {
        if (!tab) return;
        const show = (d) => {
            setMonitorReportData(d);
            setTimeout(() => { if (workspace) workspace.activateByTitle(t('common:lastExec.reportTitle')); }, 0);
        };
        if (tab.monitorReport) { show({ sql: tab.query, report: tab.monitorReport }); return; }
        if (!tab.query) return;
        const conn = (tab.connectionId && connectionsFromStore.find(c => c.id === tab.connectionId)) || planConnection();
        if (!conn) { toast.warning(t('common:plan.selectConn')); return; }
        if (conn.dbType && conn.dbType !== 'oracle') { toast.warning(t('common:plan.monitorOracleOnly')); return; }
        const notQuery = !/^\s*(\(\s*)*(SELECT|WITH)\b/i.test(stripSqlComments(tab.query));
        const ok = await confirm({
            title: t('common:plan.monitorRerunTitle'),
            message: `${t('common:plan.monitorRerunMessage')}${notQuery ? `\n\n${t('common:tun.actualNote')}` : ''}`,
            confirmText: t('common:plan.monitorRerunConfirm'),
            cancelText: t('common:ui.cancel'),
            variant: notQuery ? 'danger' : undefined,
        });
        if (!ok) return;
        try { localStorage.setItem('livesql_sqlmon_report_ack', '1'); } catch (_e) { /* 다음에도 묻는다 */ }
        const file = activeFileRef.current;
        const parsed = parseMultiSQLWithBindVariables(tab.query, {
            editorBinds: flattenEditorBinds(file && file.bindVariables),
            editorBindTypes: Object.assign({}, ...Object.values((file && file.bindTypes) || {})),
        });
        const st = parsed.statements.find(x => x.sql && x.sql.trim());
        if (!st) return;
        show({ sql: tab.query, loading: true });
        try {
            const r = await runPlan(st, conn, { planMode: 'actual', planMonitorReport: true });
            if (r.mode !== 'actual') {
                show({ sql: tab.query, error: t('common:plan.monitorEmptyBinds', { names: [...new Set(r.emptyBinds)].join(', ') }) });
                return;
            }
            setExecutionPlanTabs(prev => prev.map(x => (x.id === tab.id
                ? { ...x, data: r.planText, monitorReport: r.monitorReport, planMode: r.mode, planObjects: r.planObjects }
                : x)));
            if (r.monitorReport) show({ sql: tab.query, report: r.monitorReport });
            else show({ sql: tab.query, error: r.monitorError ? t(`common:tun.monitorErr.${r.monitorError}`, { defaultValue: r.monitorError }) : t('common:plan.monitorNone') });
        } catch (err) {
            show({ sql: tab.query, error: formatApiError(err, t) });
        }
    }, [connectionsFromStore, planConnection, confirm, runPlan, t, workspace]);

    // activeFileRef 업데이트를 최적화 - activeFile이 실제로 변경될 때만 업데이트
    useEffect(() => {
        if (activeFileRef.current !== activeFile) {
            activeFileRef.current = activeFile;
        }
    }, [activeFile]);

    // 폴링으로 잦게 갱신되는 sessionsList 도 ref 동기화 — useCallback 의 stale closure 회피용.
    // 매 1~3s 폴링 마다 sessionsList 가 새 reference 가 되는데, 이를 deps 로 쓰면 의존하는 모든
    // useCallback 이 재생성되어 caller chain 의 stale 가능성 노출. ref 로 바꿔 deps 영향 최소화.
    const sessionsListRef = useRef(sessionsList);
    useEffect(() => { sessionsListRef.current = sessionsList; }, [sessionsList]);

    const updateActiveFileContent = useCallback((newContent) => {
        // **지금 보이는 프로젝트의 탭만** 고친다 — 보이는 탭이 없을 때 남의 프로젝트 탭을 덮던 길을 막는다(신고 2026-10-26)
        const pid = activeProjectIdRef.current;
        setOpenFiles(prevFiles =>
            prevFiles.map(file =>
                file.id === activeFileId && (file.projectId || null) === pid
                    ? { ...file, content: newContent, isDirty: true }
                    : file
            )
        );
        // 분석 설정은 결과 탭의 것이다(그 탭의 sourceSql 스냅샷 기준) — 편집기 내용이 바뀌어도 지우지 않는다.
        // 예전엔 "파일 전체 내용 ≠ 실행한 문장" 으로 비교해 한 글자만 쳐도 모든 결과 탭의 설정이 지워졌다.

        // **실행계획 하이라이트는 지운다.** 그 표시는 글자 위치(offset)로 찍혀 있어서, 글이 바뀌면
        // 엉뚱한 자리를 가리킨다. 계획이 사라진 뒤에도 흔적만 남아 있다는 신고가 있었다(2026-09-18).
        // 분석 설정과 달리 스냅샷이 아니라 **지금 이 글에 대한 좌표**라 같이 유지할 수가 없다.
        clearPlanHighlight();
    }, [activeFileId, clearPlanHighlight]);

    // 에디터의 커서 위치에 텍스트 삽입하는 함수
    const insertTextAtCursor = useCallback((textToInsert) => {
        const editorView = sqlEditorRef.current;
        const activeFile = openFiles.find(f => f.id === activeFileId);

        if (!activeFile) {
            console.warn(t('common:run.noActiveFile'));
            return;
        }

        if (editorView && editorView.state && editorView.dispatch) {
            try {
                // CodeMirror 6에서 현재 커서 위치 가져오기
                const currentPosition = editorView.state.selection.main.head;

                // 삽입할 텍스트 앞뒤에 줄바꿈 추가 (기존 내용과 구분)
                let textWithLineBreaks = textToInsert;
                const currentContent = activeFile.content;

                // 커서 위치가 문서 끝이 아니고, 앞뒤에 내용이 있으면 줄바꿈 추가
                if (currentPosition > 0 && currentPosition < currentContent.length) {
                    textWithLineBreaks = `\n\n${textToInsert}\n\n`;
                } else if (currentPosition > 0) {
                    // 문서 끝에 추가하는 경우
                    textWithLineBreaks = `\n\n${textToInsert}`;
                } else {
                    // 문서 시작에 추가하는 경우
                    textWithLineBreaks = `${textToInsert}\n\n`;
                }

                // CodeMirror 6의 transaction을 사용해서 텍스트 삽입
                const transaction = editorView.state.update({
                    changes: {
                        from: currentPosition,
                        to: currentPosition,
                        insert: textWithLineBreaks
                    },
                    selection: {
                        anchor: currentPosition + textWithLineBreaks.length,
                        head: currentPosition + textWithLineBreaks.length
                    }
                });

                editorView.dispatch(transaction);

                // 파일 상태 업데이트
                const newContent = editorView.state.doc.toString();
                setOpenFiles(prevFiles =>
                    prevFiles.map(file =>
                        file.id === activeFileId
                            ? { ...file, content: newContent, isDirty: true }
                            : file
                    )
                );

                console.log('텍스트가 커서 위치에 삽입되었습니다.');
            } catch (error) {
                console.error('에디터에 텍스트 삽입 중 오류 발생:', error);

                // 폴백: 에디터 조작이 실패하면 맨 끝에 추가
                const currentContent = activeFile.content;
                const newContent = currentContent + (currentContent ? '\n\n' : '') + textToInsert;

                setOpenFiles(prevFiles =>
                    prevFiles.map(file =>
                        file.id === activeFileId
                            ? { ...file, content: newContent, isDirty: true }
                            : file
                    )
                );

                console.log('폴백: 텍스트가 문서 끝에 추가되었습니다.');
            }
        } else {
            // 에디터 뷰가 없는 경우: 파일 내용의 맨 끝에 추가
            const currentContent = activeFile.content;
            const newContent = currentContent + (currentContent ? '\n\n' : '') + textToInsert;

            setOpenFiles(prevFiles =>
                prevFiles.map(file =>
                    file.id === activeFileId
                        ? { ...file, content: newContent, isDirty: true }
                        : file
                )
            );

            console.log('텍스트가 파일 끝에 추가되었습니다.');
        }
    }, [activeFileId, openFiles, sqlEditorRef]);

    const handleTabClick = useCallback((fileId) => {
        setActiveFileId(fileId);
    }, []);

    const handleTabRename = useCallback((fileId, newName) => {
        setOpenFiles(prev => prev.map(f => f.id === fileId ? { ...f, name: newName } : f));
    }, []);

    // 탭 닫기 — 한 개든 여러 개든 이 함수 하나다(× 버튼 · 우클릭 「다른 탭 닫기」 등).
    // 닫는 탭은 **한 프로젝트 안의 것**이어야 한다(탭 줄이 한 프로젝트만 보인다).
    const closeTabs = useCallback((idsToClose) => {
        const closing = new Set(idsToClose || []);
        const targets = openFiles.filter(f => closing.has(f.id));
        if (targets.length === 0) return;
        const target = targets[0];
        const targetProjectId = target.projectId || null;
        const sameProjectFiles = openFiles.filter(f => (f.projectId || null) === targetProjectId);

        let newOpenFiles = openFiles.filter(file => !closing.has(file.id));
        let newActiveFileId = activeFileId;

        const remainingInProject = sameProjectFiles.filter(f => !closing.has(f.id));

        if (remainingInProject.length === 0) {
            // 현재 프로젝트의 마지막 탭이 닫힘
            if (targetProjectId === null) {
                // 기본 작업공간 → 빈 새 탭 하나 생성(기존 동작 유지). 세션은 이어 간다 — 작업공간의 세션이다
                const newFirstFile = takeBlankTab(target.sessionId || null);
                newOpenFiles = [...newOpenFiles, newFirstFile];
                newActiveFileId = newFirstFile.id;
            } else {
                // 프로젝트의 마지막 탭 — 프로젝트도 자동으로 닫음
                setProjects(prev => prev.filter(p => p.id !== targetProjectId));
                const firstDefault = newOpenFiles.find(f => (f.projectId || null) === null);
                // 기본 작업공간에도 탭이 없으면 하나 만든다 — 0개면 화면이 멈춘다
                const blank = firstDefault ? null : takeBlankTab();
                if (blank) newOpenFiles = [...newOpenFiles, blank];
                if (activeProjectId === targetProjectId || blank) {
                    setActiveProjectId(null);
                    newActiveFileId = (firstDefault || blank).id;
                }
            }
        } else if (closing.has(activeFileId)) {
            // 같은 프로젝트 안에서 — 닫힌 활성 탭의 오른쪽에 남은 첫 탭, 없으면 맨 끝 탭
            const idx = sameProjectFiles.findIndex(f => f.id === activeFileId);
            const after = sameProjectFiles.slice(idx + 1).find(f => !closing.has(f.id));
            newActiveFileId = (after || remainingInProject[remainingInProject.length - 1]).id;
        }

        setOpenFiles(newOpenFiles);
        if (activeFileId !== newActiveFileId) {
            setActiveFileId(newActiveFileId);
        }
        // 닫은 탭을 기억한다(다시 열기) — 손대지 않은 빈 탭은 빼고. 자리(프로젝트 안 순서)도 함께
        const worth = targets.filter(f => (f.content && f.content.trim()) || f.isDirty || f.path);
        if (worth.length) {
            closedTabsRef.current = [...closedTabsRef.current, {
                projectId: targetProjectId,
                files: worth.map(f => ({ file: f, idx: sameProjectFiles.findIndex(x => x.id === f.id) })),
            }].slice(-MAX_CLOSED_TABS);
            setClosedTabCount(closedTabsRef.current.length);
            // 알림 하나에 「되돌리기」 — 잇달아 닫으면 같은 알림을 고쳐 쓴다(쌓이지 않게)
            const body = (
                <span className="closed-tab-toast">
                    {worth.length > 1
                        ? t('common:ft.closedToastMany', { count: worth.length, name: worth[0].name.replace(/\.sql$/i, '') })
                        : t('common:ft.closedToast', { name: worth[0].name.replace(/\.sql$/i, '') })}
                    <button type="button" className="closed-tab-undo" onClick={() => { toast.dismiss('tab-closed'); if (reopenClosedRef.current) reopenClosedRef.current(); }}>{t('common:ft.undo')}</button>
                </span>
            );
            if (toast.isActive('tab-closed')) toast.update('tab-closed', { render: body, autoClose: 5000 });
            else toast.info(body, { toastId: 'tab-closed', autoClose: 5000 });
        }
        // 닫힌 에디터 탭에서 만든 결과 탭 정리 — 비고정은 제거, 고정은 editorTabId null (고아 결과)
        setQueryResultTabs(prev => prev
            .filter(t => !closing.has(t.editorTabId) || t.isPinned)
            .map(t => closing.has(t.editorTabId) ? { ...t, editorTabId: null } : t)
        );
    }, [openFiles, activeFileId, activeProjectId, takeBlankTab, t]);


    // 닫은 탭 다시 열기 — 가장 최근에 닫은 묶음을 원래 프로젝트의 원래 자리로. 프로젝트가 닫혔으면 개별 스크립트로.
    // 그 프로젝트에 손대지 않은 빈 탭 하나만 남아 있으면(마지막 탭을 닫아 생긴 것) 그것을 치운다
    const reopenClosedTab = useCallback(() => {
        const entry = closedTabsRef.current[closedTabsRef.current.length - 1];
        if (!entry) return;
        closedTabsRef.current = closedTabsRef.current.slice(0, -1);
        setClosedTabCount(closedTabsRef.current.length);
        const alive = entry.projectId === null || projects.some(p => p.id === entry.projectId);
        const pid = alive ? entry.projectId : null;
        if (!alive) toast.info(t('common:ft.reopenedStandalone'));
        const live = openFilesRef.current;
        let proj = live.filter(f => (f.projectId || null) === pid);
        const others = live.filter(f => (f.projectId || null) !== pid);
        if (proj.length === 1 && !proj[0].content && !proj[0].isDirty && !proj[0].path) proj = [];
        const restored = [];
        for (const { file, idx } of [...entry.files].sort((a, b) => a.idx - b.idx)) {
            const f = { ...file, id: uniqueTabId(file.id, [...others, ...proj]), projectId: pid };
            proj.splice(Math.max(0, Math.min(idx, proj.length)), 0, f);
            restored.push(f);
        }
        setOpenFiles([...others, ...proj]);
        setActiveProjectId(pid);
        setActiveFileId(restored[0].id);
    }, [projects, t]);
    reopenClosedRef.current = reopenClosedTab;

    // ── 7) 탭 복제 — 같은 내용·바인드·세션의 새 탭을 바로 오른쪽에 ──
    const duplicateTab = useCallback((fileId) => {
        const src = (openFiles || []).find(f => f.id === fileId);
        if (!src) return;
        const pid = src.projectId || null;
        const base = src.name.replace(/\.sql$/i, '');
        const ext = pid ? '' : '.sql';
        const taken = new Set(openFiles.filter(f => (f.projectId || null) === pid).map(f => f.name));
        let name = `${t('common:ft.copyName', { name: base })}${ext}`;
        for (let n = 2; taken.has(name); n += 1) name = `${t('common:ft.copyName', { name: base })} ${n}${ext}`;
        const nf = { ...createNewFile(pid), name, content: src.content || '', bindVariables: { ...(src.bindVariables || {}) }, sessionId: src.sessionId || null, isDirty: !!(src.content && src.content.trim()) };
        setOpenFiles(prev => {
            const i = prev.findIndex(f => f.id === fileId);
            const next = [...prev];
            next.splice(i < 0 ? 0 : i + 1, 0, nf);
            return next;
        });
        setActiveFileId(nf.id);
    }, [openFiles, createNewFile, t]);

    // 탭 닫기(× · 우클릭 「다른 탭 닫기」·「모두 닫기」) — **내용이 있는 탭이면 늘 먼저 묻는다**(2026-11-04 사용자 요청 — 예전에는 × 는 묻지 않았다).
    // 저장하지 않은 내용이 있으면 그 사실을, 아니면 내용이 있다는 사실을 말한다. 빈 탭은 묻지 않는다
    const handleCloseTabs = useCallback(async (ids) => {
        const targets = (openFiles || []).filter(f => ids.includes(f.id));
        const dirty = targets.filter(f => f.isDirty);
        const filled = targets.filter(f => !f.isDirty && String(f.content || '').trim());
        if (dirty.length > 0 || filled.length > 0) {
            const ok = await confirm({
                title: t(dirty.length > 0 ? 'common:ft.closeDirtyTitle' : 'common:ft.closeContentTitle'),
                message: dirty.length > 0
                    ? t('common:ft.closeDirtyMessage', { count: dirty.length, names: dirty.map(f => f.name).join(', ') })
                    : t('common:ft.closeContentMessage', { count: filled.length, names: filled.map(f => f.name).join(', ') }),
                confirmText: t('common:ui.close'),
                cancelText: t('common:ui.cancel'),
                variant: 'danger',
            });
            if (!ok) return;
        }
        closeTabs(ids);
    }, [openFiles, confirm, closeTabs, t]);
    const handleTabClose = useCallback((fileIdToClose) => handleCloseTabs([fileIdToClose]), [handleCloseTabs]);

    // 끌어서 순서 바꾸기 — 같은 프로젝트 안에서만(utils/tabOrder.js)
    const handleTabReorder = useCallback((dragId, targetId, before) => {
        setOpenFiles(prev => reorderTabs(prev, dragId, targetId, before));
    }, []);

    const handleFileSelected = (event) => {
        const files = event.target.files;
        if (!files) return;

        // const newOpenFiles = [...openFiles]; // 직접 수정하지 않고 setOpenFiles 콜백 사용
        let newActiveId = activeFileId;
        const filesToProcess = Array.from(files);

        // .sqlprj(예전 .livesqlproj) — 첫 항목만 프로젝트로 처리 (여러 프로젝트 동시 로드는 의미 없음)
        const projectFile = filesToProcess.find(f => isProjectFileName(f.name));
        if (projectFile) {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const parsed = parseProjectFile(e.target.result);
                    // 스탠드얼론: 연 파일의 경로를 기억해 두면 Ctrl+S 가 그 파일에 바로 쓴다
                    const diskPath = await rememberOpenedFile(projectFile);
                    await loadProjectWithConfirm(diskPath ? { ...parsed, diskPath } : parsed);
                } catch (err) {
                    toast.error(t('common:error.badProject', { message: err.message }));
                }
            };
            reader.readAsText(projectFile);
            event.target.value = null;
            return;
        }

        filesToProcess.forEach((selectedFile, index) => {
            const reader = new FileReader();
            const diskPathP = rememberOpenedFile(selectedFile); // 스탠드얼론만 값이 있다
            reader.onload = async (e) => {
                const diskPath = await diskPathP;
                setOpenFiles(prevOpenFiles => {
                    const localNextFileId = nextFileIdRef.current + index;
                    const newId = uniqueTabId(`file_${localNextFileId}_${selectedFile.name.replace(/\W/g, '_')}`, openFilesRef.current);
                    let content = e.target.result;
                    let bindVariables = {};
                    let displayName = selectedFile.name;

                    // 하이브리드 형식 또는 .livesql 파일 형식 감지 및 파싱
                    const hybridStartMarker = '-- LIVESQL_BIND_VARIABLES_START';
                    const hybridEndMarker = '-- LIVESQL_BIND_VARIABLES_END';
                    const hybridStartIndex = content.indexOf(hybridStartMarker);

                    if (hybridStartIndex !== -1) {
                        // 하이브리드 형식 파싱 (.sql 파일에 바인드 변수 포함)
                        try {
                            const sqlContent = content.substring(0, hybridStartIndex).trim();
                            const jsonStart = hybridStartIndex + hybridStartMarker.length;
                            const endIndex = content.indexOf(hybridEndMarker);

                            if (endIndex === -1) {
                                throw new Error('Invalid hybrid format: missing end marker');
                            }

                            const jsonContent = content.substring(jsonStart, endIndex).trim();
                            const bindData = JSON.parse(jsonContent);

                            content = sqlContent;
                            bindVariables = bindData.bindVariables || {};
                            displayName = selectedFile.name;

                            const bindVarCount = Object.values(bindVariables).reduce((total, stmtVars) => total + Object.keys(stmtVars).length, 0);
                            toast.success(t('common:bind.loaded', { count: bindVarCount }));
                        } catch (parseError) {
                            console.warn('하이브리드 형식 파싱 실패, 일반 텍스트 파일로 처리:', parseError);
                            toast.warn(t('common:bind.loadFailed'));
                        }
                    } else if (selectedFile.name.endsWith('.livesql')) {
                        // 기존 .livesql JSON 형식 파싱 (하위 호환성)
                        try {
                            const fileData = JSON.parse(content);
                            if (fileData.version && fileData.content) {
                                content = fileData.content;
                                bindVariables = fileData.bindVariables || {};
                                // 원본 SQL 파일 이름으로 표시 (.sql 확장자 추가)
                                const baseName = selectedFile.name.replace('.livesql', '');
                                displayName = baseName.endsWith('.sql') ? baseName : `${baseName}.sql`;

                                const bindVarCount = Object.values(bindVariables).reduce((total, stmtVars) => total + Object.keys(stmtVars).length, 0);
                                toast.success(t('common:bind.loadedLegacy', { count: bindVarCount }));
                            }
                        } catch (parseError) {
                            // JSON 파싱 실패 시 일반 텍스트 파일로 처리
                            console.warn('.livesql 파일 파싱 실패, 텍스트 파일로 처리:', parseError);
                            toast.warn(t('common:bind.loadFailed'));
                        }
                    }

                    const newFileEntry = {
                        id: newId,
                        name: displayName,
                        content: content,
                        isDirty: false,
                        path: selectedFile.name,
                        bindVariables: bindVariables,
                        // 기본 작업공간에 들어오므로 그 세션을 물려받는다
                        sessionId: projectSessionOf(prevOpenFiles, null),
                        ...(diskPath ? { diskPath } : {}),
                    };

                    // 같은 파일을 다시 열면 그 탭을 갱신한다 — **개별 스크립트 안에서만** 찾는다. 프로젝트의 탭과 이름이 같다고 그 탭을
                    // 갈아 끼우면 프로젝트의 탭이 내용째 사라지고 개별 스크립트로 넘어온다(연 파일은 기본 작업공간에 들어온다)
                    const existingFile = prevOpenFiles.find(f => (f.projectId || null) === null && (f.path === selectedFile.name || f.name === displayName));
                    let updatedFiles;
                    if (existingFile) {
                        // 기존 파일이 있으면 내용과 바인드 변수를 모두 업데이트
                        updatedFiles = prevOpenFiles.map(f => f.id === existingFile.id ? { ...newFileEntry, id: existingFile.id } : f);
                        newActiveId = existingFile.id;
                    } else {
                        updatedFiles = [...prevOpenFiles, newFileEntry];
                        newActiveId = newId;
                    }

                    // 마지막 파일 처리 후 activeId 설정 및 nextFileId 업데이트
                    if (index === filesToProcess.length - 1) {
                        setActiveFileId(newActiveId);
                        setNextFileId(prevId => prevId + filesToProcess.length);
                    }
                    return updatedFiles;
                });
            };
            reader.readAsText(selectedFile);
        });
        event.target.value = null;
    };

    // 화면 확대/축소 — 헤더 조절자. Ctrl+휠·Ctrl+= / - / 0 은 appZoom 이 문서에서 직접 받는다(같은 배율 하나)
    const handleZoomChange = useCallback((newZoom) => { setAppZoom(newZoom); }, []);
    useEffect(() => {
        initAppZoom();
        return subscribeZoom(setScreenZoom);
    }, []);

    /**
     * 영역 글자 크기(설정 › 각 영역)는 **기본값**이고 앱 확대가 그 위에 곱해진다(2026-10-10 — 예전엔 Ctrl+휠이 이 값을 바꿨다).
     * 편집기·결과 칸이 붙어 있든 분리돼 있든 **같은 변수**를 싣는다 — 분리한 칸에 빠져 있어 기본값으로 돌아가던 것을 고쳤다.
     * 결과 칸의 고정폭 글(실행계획 텍스트)도 그 탭의 크기를 따른다(--font-size-mono 를 결과 칸에서도 준다).
     */
    const editorZoneVars = { '--font-size-mono': `${zoneZoom.editor}px` };
    // 실행계획 글자는 **편집기와 같은 크기**(2026-10-26 사용자 요청 — 따로 둔 크기가 편집기보다 커서 어색했다). 저장된 plan 값은 남겨 두되 쓰지 않는다
    const resultZoneSize = activeMainTab === 'queryResults' ? zoneZoom.result : activeMainTab === 'executionPlans' ? zoneZoom.editor : zoneZoom.tableInfo;
    const resultZoneVars = { '--font-size-grid': `${resultZoneSize}px`, '--font-size-mono': `${resultZoneSize}px` };

    // 에디터 검색
    const handleEditorSearch = useCallback((text) => {
        setEditorSearch(prev => ({ ...prev, text }));
        const view = sqlEditorRef.current;
        if (!view || !text) {
            setEditorSearch(prev => ({ ...prev, matchIndex: -1, matchCount: 0 }));
            return;
        }
        const doc = view.state.doc.toString();
        const upper = doc.toUpperCase();
        const search = text.toUpperCase();
        let count = 0, idx = 0;
        while ((idx = upper.indexOf(search, idx)) !== -1) { count++; idx += search.length; }
        setEditorSearch(prev => ({ ...prev, matchCount: count, matchIndex: count > 0 ? 0 : -1 }));
        // 첫 번째 매칭으로 이동
        const firstIdx = upper.indexOf(search);
        if (firstIdx >= 0) {
            view.dispatch({ selection: { anchor: firstIdx, head: firstIdx + text.length }, scrollIntoView: true });
        }
    }, [sqlEditorRef]);

    const handleEditorSearchNav = useCallback((direction) => {
        const view = sqlEditorRef.current;
        if (!view || !editorSearch.text) return;
        const doc = view.state.doc.toString().toUpperCase();
        const search = editorSearch.text.toUpperCase();
        const positions = [];
        let idx = 0;
        while ((idx = doc.indexOf(search, idx)) !== -1) { positions.push(idx); idx += search.length; }
        if (positions.length === 0) return;
        const newIndex = direction === 'next'
            ? (editorSearch.matchIndex + 1) % positions.length
            : (editorSearch.matchIndex - 1 + positions.length) % positions.length;
        setEditorSearch(prev => ({ ...prev, matchIndex: newIndex }));
        const pos = positions[newIndex];
        view.dispatch({ selection: { anchor: pos, head: pos + editorSearch.text.length }, scrollIntoView: true });
    }, [sqlEditorRef, editorSearch.text, editorSearch.matchIndex]);


    // 다크모드 적용
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
        localStorage.setItem('livesql_theme', darkMode ? 'dark' : 'light');
    }, [darkMode]);

    // 앱 설정 영속화 — 이전에는 메모리 state 라 새로고침하면 초기화됐다.
    // 값이 바뀔 때만 저장하므로 렌더 비용은 무시할 수준.
    useEffect(() => { saveSection('zoneZoom', zoneZoom); }, [zoneZoom]);
    useEffect(() => { saveSection('editorOptions', editorOptions); }, [editorOptions]);
    useEffect(() => { saveSection('tabKindFilter', tabKindFilter); }, [tabKindFilter]);
    useEffect(() => { saveSection('gridFormat', gridFormat); }, [gridFormat]);
    useEffect(() => { saveSection('execution', execSettings); }, [execSettings]);
    useEffect(() => { saveSection('sqlFormat', sqlFormat); }, [sqlFormat]);
    useEffect(() => { saveSection('assistant', assistantSettings); }, [assistantSettings]);
    // 설정 창의 「즉시 적용」 — 지금 값을 한꺼번에 저장하고 알린다. 설정은 바꾸는 순간 화면에 들지만(위 effect),
    // 그렇다는 것을 사용자가 확인할 길이 없었다. 예전에 「바꿔도 안 듣던」 두 항목(자동완성·주석 보기)은 에디터에
    // 연결하지 않았던 것이 원인이었고 이제 바로 든다(SQLEditor autocomplete/showComments).
    // 실행계획에서 객체를 눌렀는데 오른쪽 「오브젝트 상세」가 접혀 있으면 편다(툴바 토글과 같은 값)
    const openPlanObjectDetail = useCallback(() => {
        setEditorOptions(p => (p.planObjectDetail ? p : { ...p, planObjectDetail: true }));
    }, []);

    const handleApplySettingsNow = useCallback(() => {
        saveSection('zoneZoom', zoneZoom);
        saveSection('editorOptions', editorOptions);
        saveSection('tabKindFilter', tabKindFilter);
        saveSection('gridFormat', gridFormat);
        saveSection('execution', execSettings);
        saveSection('sqlFormat', sqlFormat);
        saveSection('assistant', assistantSettings);
        toast.success(t('settings:appliedNow'));
    }, [zoneZoom, editorOptions, tabKindFilter, gridFormat, execSettings, sqlFormat, assistantSettings, t]);


    // "더보기"를 위한 함수 - 타임아웃 및 재시도 로직 포함
    const fetchMoreDataForTab = useCallback(async (tabIdToFetch, retryCount = 0) => {
        const maxRetries = 3;
        const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000); // 지수백오프, 최대 5초
        
        
        // 상태 경합 조건 방지를 위해 상태 업데이트 내부에서 검증
        let targetTabDataForApi = null;
        
        // 상태 업데이트: 조건 확인 및 로딩 시작을 원자적으로 처리
        setQueryResultTabs(prevTabs => {
            const currentTab = prevTabs.find(t => t.id === tabIdToFetch);
            
            // 조건 검증 - 이미 로딩 중이거나 더 이상 데이터가 없으면 상태 변경 없이 리턴
            if (!currentTab || !currentTab.cursorId || !currentTab.hasMore || currentTab.isLoadingMore) {
                return prevTabs; // 상태 변경 없음
            }

            // API 요청에 필요한 데이터 추출 (클로저 문제 방지)
            targetTabDataForApi = {
                query: currentTab.query,
                bindVariables: currentTab.bindVariables || {},
                cursorId: currentTab.cursorId,
                currentFetchSize: currentTab.currentFetchSize,
                sessionId: currentTab.sessionId || null // Phase 3: 결과 탭 생성 시 보존된 세션
            };

            // 로딩 상태로 업데이트
            // 콜백을 t 로 쓰면 번역 t 를 가려 t('...') 가 탭 객체 호출이 된다 (런타임 크래시)
            return prevTabs.map(tab =>
                tab.id === tabIdToFetch ? {
                    ...tab,
                    isLoadingMore: true,
                    retryCount: retryCount,
                    loadingMessage: retryCount > 0 ? t('common:run.retrying', { n: retryCount, max: maxRetries }) : t('common:run.loadingMore'),
                    error: null, // 새로운 fetch 시작 시 이전 에러 제거
                    canRetry: false // 로딩 중에는 재시도 비활성화
                } : tab
            );
        });

        // 조건을 만족하지 않았다면 API 요청 없이 종료
        if (!targetTabDataForApi) {
            return;
        }

        // 다음 페이지도 오래 걸릴 수 있다(커서가 남은 행을 계속 읽는다). 제한시간을 없앤 대신
        // **여기서도 중지할 수 있어야** 한다 — 중지 수단 없이 제한만 푸는 것은 화면을 영영 묶어 두는 짓이다.
        const pageController = new AbortController();
        abortControllersRef.current[tabIdToFetch] = {
            controller: pageController,
            sessionId: targetTabDataForApi.sessionId,
        };

        try {
            // 재시도 시 지연
            if (retryCount > 0) {
                await new Promise(resolve => setTimeout(resolve, retryDelay));
            }

            // Phase 3: 결과 탭의 sessionId 가 있으면 /sessions/:id/fetch 사용 (cursor 가 해당 세션의 connection 에 묶임)
            // sessionId 가 없는 legacy 탭은 기존 /fetch-data 경로 유지
            let response;
            if (targetTabDataForApi.sessionId) {
                response = await axiosInstance.post(
                    `/sessions/${targetTabDataForApi.sessionId}/fetch`,
                    {
                        sql: targetTabDataForApi.query,
                        bindVariables: targetTabDataForApi.bindVariables,
                        cursorId: targetTabDataForApi.cursorId,
                        fetchSize: targetTabDataForApi.currentFetchSize,
                        usrid: user?.email || user?.id || 'standalone'
                    },
                    { ...NO_TIMEOUT, signal: pageController.signal }
                );
            } else {
                response = await axiosInstance.post('/fetch-data', {
                    query: targetTabDataForApi.query,
                    bindVariables: targetTabDataForApi.bindVariables,
                    cursorId: targetTabDataForApi.cursorId,
                    fetchSize: targetTabDataForApi.currentFetchSize,
                    usrid: user?.email || user?.id || 'standalone',
                    connectionId: targetTabDataForApi.connectionId
                }, { ...NO_TIMEOUT, signal: pageController.signal });
            }

            const { cursorId: newCursorId, rows: newRows, hasMore: newHasMore, currentFetchTime, totalExecutionTime } = response.data;

            setQueryResultTabs(prevTabs => prevTabs.map(t => 
                t.id === tabIdToFetch 
                    ? { 
                        ...t, 
                        data: [...t.data, ...newRows],
                        cursorId: newCursorId,
                        hasMore: newHasMore, 
                        isLoadingMore: false,
                        executionTime: totalExecutionTime,
                        currentFetchTime,
                        retryCount: 0,
                        loadingMessage: null,
                        error: null, // 성공 시 이전 에러 제거
                        canRetry: false, // 성공 시 재시도 상태 초기화
                        // startRowIndex는 변경하지 않음 (첫 번째 행의 인덱스 유지)
                      } 
                    : t
            ));
        } catch (error) {
            // 사용자가 중지를 눌렀다 — 오류가 아니므로 재시도도 토스트도 없다
            if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
                setQueryResultTabs(prevTabs => prevTabs.map(tb =>
                    tb.id === tabIdToFetch
                        ? { ...tb, isLoadingMore: false, loadingMessage: null, retryCount: 0, canRetry: true }
                        : tb
                ));
                return;
            }
            const errorType = error.response?.data?.errorType;
            // 판별용 원문 — 화면에 쓰지 않는다. 'ORA-' 포함 여부로 SQL 오류를 가리므로
            // formatApiError 로 번역하면 판별이 깨진다. 표시는 아래 baseErrorMessage 를 쓸 것.
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || '';
            const isCursorExpired = errorType === 'CURSOR_EXPIRED';
            const isTimeout = errorType === 'TIMEOUT' || error.code === 'ECONNABORTED';
            const isNetworkError = !error.response || error.code === 'ECONNREFUSED' || error.code === 'NETWORK_ERROR';
            const isPermissionError = error.response?.status === 403 || errorType === 'PERMISSION_ERROR' || errorType === 'PERMISSION_DENIED';
            const isAuthError = error.response?.status === 401 || errorType === 'AUTHENTICATION_REQUIRED';
            const isSQLError = errorMessage.includes('ORA-') || errorMessage.includes('SQL') || errorType === 'SQL_ERROR';

            // 재시도 가능한 에러인지 확인 (권한/인증/SQL 에러는 재시도 불가)
            const canRetry = (isTimeout || isNetworkError) && retryCount < maxRetries && !isPermissionError && !isAuthError && !isSQLError;

            if (canRetry) {
                // 재시도
                return fetchMoreDataForTab(tabIdToFetch, retryCount + 1);
            }

            // 최종 에러 처리
            // errorType 코드가 있으면 번역하고, 없으면 서버 메시지를 그대로 쓴다.
            const baseErrorMessage = formatApiError(error, t);

            let finalErrorMessage = baseErrorMessage;
            let shouldKeepRetrying = false;

            if (isSQLError) {
                finalErrorMessage = t('common:error.sqlIcon', { message: baseErrorMessage });
                toast.error(t('common:error.sql', { message: baseErrorMessage }));
            } else if (isPermissionError) {
                const permissionDetails = error.response?.data?.violations
                    ? error.response.data.violations.map(v => v.message).join('\n• ')
                    : '';
                finalErrorMessage = t('common:error.permissionDetail', {
                    message: baseErrorMessage,
                    details: permissionDetails ? t('common:error.violations', { details: permissionDetails }) : '',
                });
                toast.error(t('common:run.pagingRejected', { message: baseErrorMessage }));
            } else if (isAuthError) {
                finalErrorMessage = t('common:error.authRequiredIcon');
                toast.error(t('common:error.authRequired'));
            } else if (isCursorExpired) {
                const ttlMinutes = Math.round((error.response?.data?.ttlMs || 300000) / 60000);
                finalErrorMessage = t('common:error.cursorExpired', { minutes: ttlMinutes, message: baseErrorMessage });
            } else if (isTimeout) {
                finalErrorMessage = t('common:error.timeout', { message: baseErrorMessage });
                shouldKeepRetrying = true;
            } else if (isNetworkError) {
                finalErrorMessage = t('common:error.network');
                shouldKeepRetrying = true;
            }
            
            const currentFetchTime = error.response?.data?.currentFetchTime;
            
            setQueryResultTabs(prevTabs => prevTabs.map(t => 
                t.id === tabIdToFetch 
                    ? { 
                        ...t, 
                        error: finalErrorMessage, 
                        isLoadingMore: false, 
                        hasMore: isCursorExpired ? false : t.hasMore,
                        cursorId: isCursorExpired ? null : t.cursorId,
                        currentFetchTime,
                        retryCount: 0,
                        loadingMessage: null,
                        canRetry: shouldKeepRetrying // 재시도 가능한 에러의 경우 true
                      }
                    : t
            ));
        } finally {
            // 재시도 경로는 스스로 새 컨트롤러를 만든다 — 지금 것만 거둔다
            if (abortControllersRef.current[tabIdToFetch]?.controller === pageController) {
                delete abortControllersRef.current[tabIdToFetch];
            }
        }
    }, [axiosInstance, user]);

    const handleHistoryClick = useCallback((histQuery) => {
        if (activeFileRef.current) {
            // 전체 내용을 대체하는 대신 커서 위치에 삽입
            insertTextAtCursor(histQuery);
        }
        // 만약 열린 파일이 하나도 없다면 새 파일을 만들고 거기에 내용을 넣는 로직도 고려 가능
    }, [insertTextAtCursor]);

    const clearHistoryCallback = useCallback(() => {
        dispatch(clearHistory());
    }, [dispatch]);

    const deleteHistoryCallback = useCallback((timestamp) => {
        dispatch(deleteHistoryItem(timestamp));
    }, [dispatch]);

    const fetchAndSetTableDetails = useCallback(async (tableIdentifier) => {

        const { connectionId, schemaName, tableName, dbType } = tableIdentifier;
        const tabId = `tableinfo_${connectionId}_${schemaName}_${tableName}`;
        // const existingTabIndex = tableInfoTabs.findIndex(tab => tab.id === tabId); // 바깥에서 계산하지 않음

        const connectionInStore = connectionsFromStore.find(c => c.id === connectionId);
        if (!connectionInStore) {
            // 연결 정보 없음 처리 (기존 로직 유지, 필요시 prevTabs 기반으로 수정)
            // 이 부분은 setTableInfoTabs 내부에서 처리하는 것이 더 안전할 수 있으나, 일단 현재 구조 유지
            // 기존에는 existingTabIndex를 사용했으나, 이제 setTableInfoTabs 내부에서 판단해야 함.
            setTableInfoTabs(prevTabs => {
                const existingIdx = prevTabs.findIndex(t => t.id === tabId);
                if (existingIdx !== -1) {
                    return prevTabs.map(t => 
                        t.id === tabId ? { ...t, isLoading: false, error: 'Connection details not found in store.' } : t
                    );
                }
                // 새 탭을 추가하려는데 연결 정보가 없는 경우는 드물지만, 아무것도 하지 않음
                return prevTabs; // 변경 없음
            });
            setActiveTableInfoTabId(tabId);
            return;
        }

        const updateOrCreateTab = (isLoading, data = null, error = null) => {
            setTableInfoTabs(prevTabs => {
                const existingTabIndex = prevTabs.findIndex(tab => tab.id === tabId);

                let newTabs = [...prevTabs];
                if (existingTabIndex !== -1) { // 기존 탭 업데이트
                    newTabs[existingTabIndex] = { ...newTabs[existingTabIndex], identifier: tableIdentifier, data, isLoading, error };
                } else { // 새 탭 추가
                    const newTab = {
                        id: tabId,
                        name: `${schemaName}.${tableName}`,
                        identifier: tableIdentifier,
                        data,
                        isLoading,
                        error,
                    };
                    if (newTabs.length >= 5) {
                        newTabs.shift(); // 가장 오래된 탭 제거 (FIFO)
                    }
                    newTabs.push(newTab);
                }
                return newTabs;
            });
            setActiveTableInfoTabId(tabId);
        };

        updateOrCreateTab(true); // 로딩 시작 상태로 탭 설정

        try {
            
            const queries = dbQueries[dbType];
            if (!queries || !queries.fetchColumnsForTable || !queries.fetchIndexesForTable) {
                throw new Error(t('common:error.noTableQuery', { dbType: dbType }));
            }

            const columnsQuery = queries.fetchColumnsForTable(schemaName, tableName);
            const indexesQuery = queries.fetchIndexesForTable(schemaName, tableName);

            const columnsResponse = await axiosInstance.post('/execute', {
                query: columnsQuery,
                usrid: user?.email || user?.id || 'standalone',
                connectionId
            });
            const rawColumns = Array.isArray(columnsResponse.data) 
                ? columnsResponse.data 
                : Array.isArray(columnsResponse.data?.rows) 
                    ? columnsResponse.data.rows 
                    : [];
            const processedColumns = rawColumns.map(col => ({
                columnid: col.COLUMNID,
                name: col.NAME,
                type: col.TYPE,
                length: col.LENGTH,
                nullable: col.NULLABLE,
                comments: col.COMMENTS, // App.js에서는 COMMENTS, MainDisplayTabs.js에서도 COMMENTS 사용 확인
            }));

            const indexesResponse = await axiosInstance.post('/execute', {
                query: indexesQuery,
                usrid: user?.email || user?.id || 'standalone',
                connectionId
            });
            const rawIndexes = Array.isArray(indexesResponse.data) 
                ? indexesResponse.data 
                : Array.isArray(indexesResponse.data?.rows) 
                    ? indexesResponse.data.rows 
                    : [];
            const processedIndexes = rawIndexes.map(idx => ({
                name: idx.NAME,
                columns: idx.COLUMNSSTRING ? idx.COLUMNSSTRING.split(',').map(s => s.trim()) : [],
                isUnique: idx.ISUNIQUE === 'Unique',
            }));

            const tableData = { 
                columns: processedColumns || [],
                indexes: processedIndexes || []
            };
            
            updateOrCreateTab(false, tableData, null); // 로딩 완료, 데이터 설정

        } catch (error) {
            updateOrCreateTab(false, { columns: [], indexes: [] }, error.message || 'Failed to fetch table details.');
        }
    }, [connectionsFromStore, axiosInstance, user]);

    const handleCloseTableInfoTab = useCallback((tabIdToClose) => {
        setTableInfoTabs(prevTabs => {
            const newTabs = prevTabs.filter(tab => tab.id !== tabIdToClose);
            if (activeTableInfoTabId === tabIdToClose) {
                if (newTabs.length > 0) {
                    setActiveTableInfoTabId(newTabs[newTabs.length - 1].id);
                } else {
                    setActiveTableInfoTabId(null);
                }
            }
            return newTabs;
        });
    }, [activeTableInfoTabId]); // activeTableInfoTabId만 의존성으로 가짐

    /**
     * 테이블 **전체 DDL** 을 오브젝트 정보 탭에 띄운다. 뷰·프로시저 DDL 과 같은 자리를 쓴다.
     *
     * `DBMS_METADATA.GET_DDL` 을 **먼저** 시도한다(저장 옵션·기본값까지 원본 그대로). 다른
     * 스키마 객체에는 SELECT_CATALOG_ROLE 이 필요해 ORA-31603 으로 실패하는 계정이 많은데
     * (교육 DB 의 EDU 로 HR 을 볼 때 실제로 그랬다), 그때는 ALL_* 로 조립한 것으로 넘어간다.
     * 조회는 되는데 DDL 만 못 보는 상태로 두지 않는다.
     */
    const handleShowTableDDL = useCallback(async (owner, tableName) => {
        const connId = activePlanConnectionId || activeConnectionId;
        const conn = connectionsFromStore.find(c => c.id === connId);
        if (!conn) { toast.error(t('common:conn.tableConnNotFound')); return; }
        const queries = dbQueries[conn.dbType || 'oracle'];
        if (!queries || typeof queries.fetchTableDDLFromCatalog !== 'function') {
            toast.info(t('common:mdt.ddlUnsupported', { dbType: conn.dbType || 'oracle' }));
            return;
        }

        const tabId = `ddl_${owner}_${tableName}`;
        const existing = tableInfoTabs.find(tb => tb.id === tabId);
        if (existing) { setActiveTableInfoTabId(tabId); setActiveMainTab('tableInfo'); return; }

        setTableInfoTabs(prev => [...prev, {
            id: tabId, name: `${owner}.${tableName}`,
            identifier: { schemaName: owner, tableName, objectType: 'TABLE' },
            isLoading: true, data: null,
        }]);
        setActiveTableInfoTabId(tabId);
        setActiveMainTab('tableInfo');

        const run = (query) => axiosInstance.post('/execute', {
            query, usrid: user?.email || user?.id || 'standalone', connectionId: connId,
        });
        const textOf = (res) => {
            const rows = res.data?.rows || (Array.isArray(res.data) ? res.data : []);
            return rows.map(r => String(r.DDL_TEXT ?? Object.values(r)[0] ?? '')).join('\n').trim();
        };

        try {
            let ddl = '';
            try {
                ddl = textOf(await run(queries.fetchTableDDL(owner, tableName)));
            } catch (_e) {
                ddl = '';   // 권한이 없다 — 카탈로그로 조립한다
            }
            let reconstructed = false;
            if (!ddl) {
                ddl = textOf(await run(queries.fetchTableDDLFromCatalog(owner, tableName)));
                reconstructed = true;
            }
            setTableInfoTabs(prev => prev.map(tb => tb.id === tabId
                ? { ...tb, isLoading: false, data: { type: 'TABLE', ddl: ddl || t('common:mdt.ddlEmpty'), reconstructed } }
                : tb));
        } catch (err) {
            setTableInfoTabs(prev => prev.map(tb => tb.id === tabId
                ? { ...tb, isLoading: false, data: { type: 'TABLE', ddl: formatApiError(err, t) } }
                : tb));
        }
    }, [activePlanConnectionId, activeConnectionId, connectionsFromStore, tableInfoTabs, axiosInstance, user, t]);

    const handleSchemaNodeActivate = useCallback((nodeData) => {
        // Phase 5: 세션 클릭 → 바인딩된 에디터 탭으로 자동 전환
        if (nodeData && nodeData.type === 'session' && nodeData.sessionId) {
            const boundTab = (openFiles || []).find(f => f.sessionId === nodeData.sessionId);
            if (boundTab) {
                setActiveFileId(boundTab.id);
                // 결과 탭 영역을 활성화 — running 세션이면 진행 중인 결과를 바로 보여주기 위함
                setActiveMainTab('queryResults');
                // CodeMirror EditorView 에 focus (SQLEditor 가 mount 된 다음 tick 에 호출)
                setTimeout(() => sqlEditorRef.current?.focus?.(), 0);
            } else {
                // 바인딩된 탭이 없으면 — 활성 탭이 미바인딩 상태일 때 자동 바인딩
                const activeTab = (openFiles || []).find(f => f.id === activeFileId);
                if (activeTab && !activeTab.sessionId) {
                    // 검증된 경로로 바인딩 — 죽은 세션·시스템 전용 세션은 handleBindSessionToTab 이
                    // 막고 이유를 알린다. **막혔는데 "바인딩됐다" 고 알리면 안 되므로** 성공 여부를 본다.
                    if (!assertBindable(nodeData.sessionId)) return;
                    handleBindSessionToTab(activeTab.id, nodeData.sessionId);
                    toast.success(t('common:session.binding', { name: nodeData.label || nodeData.sessionId }));
                    setTimeout(() => sqlEditorRef.current?.focus?.(), 0);
                } else if (activeTab && activeTab.sessionId) {
                    // 활성 탭이 다른 세션을 쓰는 중 — 알림만 띄우던 자리(「바인딩된 탭이 없습니다」)를
                    // 묻고 바꾸기로 바꿨다(2026-11-02 사용자 요청). 바꾸는 길은 세션 고르기와 같은
                    // handleBindSessionToTab 하나다(검증·프로젝트 단위 적용이 거기 있다).
                    if (!assertBindable(nodeData.sessionId)) return;
                    const liveList = sessionsListRef.current || [];
                    const fromSess = liveList.find(x => x.sessionId === activeTab.sessionId);
                    // 실행 중인 세션의 탭은 세션 고르기도 바꾸지 못하게 한다 — 같은 규칙
                    if (isSessionBusy(fromSess)) { toast.warning(t('common:sp.cannotChangeWhileRunning')); return; }
                    const toSess = liveList.find(x => x.sessionId === nodeData.sessionId);
                    const fromName = fromSess ? sessionDisplayName(fromSess, t) : t('common:sp.sessionChecking');
                    const toName = nodeData.label || (toSess ? sessionDisplayName(toSess, t) : nodeData.sessionId);
                    const fileId = activeTab.id;
                    const targetSid = nodeData.sessionId;
                    (async () => {
                        const ok = await confirm({
                            title: t('common:session.changeTitle'),
                            message: t('common:session.changeMessage', { from: fromName, to: toName })
                                + (fromSess && fromSess.uncommitted ? t('common:session.changeUncommitted') : ''),
                            confirmText: t('common:session.changeConfirm'),
                            cancelText: t('common:action.cancel'),
                        });
                        // 묻는 사이 세션이 사라졌을 수 있다 — 막혔는데 「바꿨다」 고 알리지 않게 다시 본다
                        if (!ok || !assertBindable(targetSid)) return;
                        handleBindSessionToTab(fileId, targetSid);
                        toast.success(t('common:session.changed', { name: toName }));
                        setTimeout(() => sqlEditorRef.current?.focus?.(), 0);
                    })();
                } else {
                    toast.info(t('common:session.noBoundTab'));
                }
            }
            return;
        }
        if (nodeData && nodeData.type === 'connection') {
            // name/id를 항상 함께 갱신 (분리되면 쿼리가 엉뚱한 DB로 감)
            setActiveConnectionName(nodeData.name);
            setActiveConnectionId(nodeData.id);
            activeConnectionIdRef.current = nodeData.id;
            setActiveConnectionIdForSchema(nodeData.id);
            const connectionInStore = connectionsFromStore.find(c => c.id === nodeData.id);
            if (connectionInStore && !connectionInStore.children && !connectionInStore.isLoadingSchemas) {
                 dispatch(fetchSchemasForConnection(nodeData.id, connectionInStore.dbType));
            }
        } else if (nodeData && nodeData.type === 'table') {
            const connectionOfTable = connectionsFromStore.find(c => c.id === nodeData.connectionId);
            if (connectionOfTable) {
                const tableIdentifier = {
                    connectionId: nodeData.connectionId,
                    schemaName: nodeData.schemaName,
                    tableName: nodeData.name,
                    dbType: connectionOfTable.dbType
                };
                fetchAndSetTableDetails(tableIdentifier).then(() => {
                    setActiveMainTab('tableInfo');
                });
            } else {
                toast.error(t('common:conn.tableConnNotFound'));
            }
        } else if (nodeData && (nodeData.type === 'view' || nodeData.type === 'procedure')) {
            // 뷰/프로시저: DDL 스크립트 조회
            const conn = connectionsFromStore.find(c => c.id === nodeData.connectionId);
            if (conn) {
                const queries = dbQueries[conn.dbType || 'oracle'];
                const isView = nodeData.type === 'view';
                const ddlType = isView ? 'VIEW' : (nodeData.objectType || 'PROCEDURE');
                const ddlQuery = isView
                    ? queries.fetchViewDDL(nodeData.schemaName, nodeData.name)
                    : queries.fetchProcedureDDL(nodeData.schemaName, nodeData.name);
                const tabId = `ddl_${nodeData.schemaName}_${nodeData.name}`;

                const existing = tableInfoTabs.find(t => t.id === tabId);
                if (existing) {
                    setActiveTableInfoTabId(tabId);
                    setActiveMainTab('tableInfo');
                    return;
                }

                setTableInfoTabs(prev => [...prev, {
                    id: tabId,
                    name: `${nodeData.schemaName}.${nodeData.name}`,
                    identifier: { schemaName: nodeData.schemaName, tableName: nodeData.name, objectType: ddlType },
                    isLoading: true, data: null,
                }]);
                setActiveTableInfoTabId(tabId);
                setActiveMainTab('tableInfo');

                axiosInstance.post('/execute', {
                    query: ddlQuery,
                    usrid: user?.email || user?.id || 'standalone',
                    connectionId: nodeData.connectionId,
                }).then(res => {
                    const rows = res.data?.rows || (Array.isArray(res.data) ? res.data : []);
                    let ddlText = '';
                    if (isView) {
                        const viewText = rows.length > 0 ? (rows[0].DDL_TEXT || rows[0].ddl_text || '') : '';
                        ddlText = viewText ? `CREATE OR REPLACE VIEW "${nodeData.schemaName}"."${nodeData.name}" AS\n${viewText}` : '';
                    } else {
                        // 프로시저: ALL_SOURCE의 각 행을 합침
                        ddlText = rows.map(r => r.DDL_TEXT || r.ddl_text || r.TEXT || r.text || '').join('');
                    }
                    // 콜백을 t 로 쓰면 번역 t 를 가려 t('...') 가 탭 객체 호출이 된다 (런타임 크래시)
                    setTableInfoTabs(prev => prev.map(tab => tab.id === tabId
                        ? { ...tab, isLoading: false, data: { ddl: ddlText || t('common:table.ddlNotFound'), type: ddlType } }
                        : tab
                    ));
                }).catch(err => {
                    setTableInfoTabs(prev => prev.map(tab => tab.id === tabId
                        ? { ...tab, isLoading: false, data: { ddl: t('common:table.ddlFailed', { message: err.message }), type: ddlType } }
                        : tab
                    ));
                });
            }
        }
    }, [assertBindable, dispatch, setActiveConnectionName, connectionsFromStore, fetchAndSetTableDetails, setActiveMainTab, tableInfoTabs, axiosInstance, user, openFiles, activeFileId, handleBindSessionToTab, confirm, t]);

    // ERD 모달 핸들러
    const handleERDClick = useCallback((schemaData) => {
        console.log('ERD 모달 열기 요청:', schemaData);
        if (schemaData && schemaData.type === 'schema') {
            const connectionOfSchema = connectionsFromStore.find(c => c.id === schemaData.connectionId);
            setSelectedERDSchema(schemaData.name);
            // store 에 매칭이 없어도 schemaData.connectionId 는 보존 — ERD 라우트는 id 만 있으면 동작.
            // backend 에서 connection definition 못 찾으면 명확한 404 반환.
            setCurrentConnectionData(connectionOfSchema || (schemaData.connectionId ? { id: schemaData.connectionId } : null));
            setIsERDModalOpen(true);

            if (!connectionOfSchema) {
                console.warn(`[ERD] connection store 에서 ${schemaData.connectionId} 매칭 실패 — id 만 보존하여 진행`);
            }
        }
    }, [connectionsFromStore]);

    const handleERDModalClose = useCallback(() => {
        setIsERDModalOpen(false);
        setSelectedERDSchema(null);
        setCurrentConnectionData(null);
    }, []);

    /**
     * 오른쪽 패널을 **그 화면으로** 연다. 여는 곳이 여럿이라(툴바 버튼·메뉴 챌린지 ›·실행이력)
     * 한 함수로 모은다 — 두 상태(열림 · 무엇을)를 따로 세우면 한쪽만 바꾸는 일이 생긴다.
     */
    const openSidePanel = useCallback((view) => {
        setIsSidePanelOpen(true);
        setExternalActiveTab(view);
        setCoverHidden(false);
    }, []);

    const handleViewExecutionHistory = useCallback(() => {
        openSidePanel('history');
    }, [openSidePanel]);

    const handleEditorResize = useCallback((newSize) => {
        // 사용자 드래그 중에는 splitSize 갱신 (접기 모드 X 일 때만).
        if (splitCollapseMode === 'none') setSplitSize(newSize);
    }, [splitCollapseMode]);

    const toggleCollapseBottom = useCallback(() => {
        setSplitCollapseMode(m => m === 'bottom' ? 'none' : 'bottom');
    }, []);

    const handleEditorFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleEditorBlur = useCallback(() => {
        // isEditorFocused 는 일부러 내리지 않는다 — 결과 그리드 더블클릭 붙여넣기·Header 버튼이 「마지막으로
        // 편집기를 썼다」 는 뜻으로 쓴다. (책갈피 탐색기는 2026-11-03 부터 커서와 무관하게 보인다)
    }, []);

    // 결과 그리드 셀/헤더 더블클릭 → 에디터 cursor 위치에 텍스트 삽입.
    // 에디터 포커스 상태일 때만 동작 (그리드 클릭으로 포커스가 옮겨가는 사이에는 ref 가 직전 상태 유지).
    const handlePasteToEditor = useCallback((text) => {
        if (!isEditorFocused) return false;
        const view = sqlEditorRef.current;
        if (!view || !view.state || typeof view.dispatch !== 'function') return false;
        const insert = String(text == null ? '' : text);
        if (!insert) return false;
        const pos = view.state.selection.main.head;
        view.dispatch({
            changes: { from: pos, to: pos, insert },
            selection: { anchor: pos + insert.length },
            scrollIntoView: true,
        });
        view.focus();
        return true;
    }, [isEditorFocused]);

    // 테이블 상세 정보 또는 실행 계획을 새 탭에 표시하는 공통 로직
    const openNewMainDisplayTab = useCallback((type, title, data, metadata = {}) => {
        if (type === 'tableDetail') {
            const newTabId = `table_${metadata.schemaName}_${metadata.tableName}_${Date.now()}`;
            const newTab = {
                id: newTabId,
                type: 'tableDetail', // 'tableDetail', 'executionPlan'
                title: title || `Table: ${metadata.schemaName}.${metadata.tableName}`,
                data: data, // 컬럼, 인덱스 정보 등
                connectionId: metadata.connectionId,
                dbType: metadata.dbType,
                schemaName: metadata.schemaName,
                tableName: metadata.tableName,
            };
            setQueryResultTabs(prevTabs => [...prevTabs, newTab]);
            setActiveQueryResultTabId(newTabId);
            setActiveMainTab('queryResults'); // 메인 탭 활성화
            return newTabId;
        } else if (type === 'executionPlan') {
            // 실행 계획 탭 로직은 이미 존재하고 잘 동작하므로, 여기서는 tableDetail에 집중
            // 필요하다면 이 함수를 확장하여 실행 계획 탭 생성도 통합 가능
            // executionPlan type is handled separately
            return null;
        }
    }, []);

    const handleViewSchemaInfoRequest = useCallback(async (objectNameFromEditor) => {
        let objectName = objectNameFromEditor;
        // Phase 3+ : 활성 탭의 바인딩 세션 → connection 으로 해소. fallback 없음.
        const activeTab = activeFileRef.current;
        if (!activeTab) {
            toast.warn(t('common:run.noActiveTab'));
            return;
        }
        if (!activeTab.sessionId) {
            toast.warn(t('common:session.tabNotBound'));
            return;
        }
        const liveSessions = sessionsListRef.current || [];
        const boundSession = liveSessions.find(s => s.sessionId === activeTab.sessionId);
        if (!boundSession) {
            toast.warn(t('common:session.boundMissing', { id: activeTab.sessionId }));
            return;
        }
        const currentActiveConnection = connectionsFromStore.find(c => c.id === boundSession.connectionId);
        if (!currentActiveConnection || !currentActiveConnection.id) {
            toast.warn(t('common:session.connMissing', { id: boundSession.connectionId }));
            return;
        }

        const connectionId = currentActiveConnection.id;
        const dbType = currentActiveConnection.dbType;
        const schemaNodes = currentActiveConnection.children;

        // 메뉴 › 실행 › 스키마 정보 보기로 들어온 경우 — 커서에서 직접 읽는다.
        // **규칙은 편집기 F4 와 같은 함수다**(`utils/sqlIdentifier`). 예전에는 여기서 따로
        // 훑었는데 그 정규식이 `/\\w/` 라 **어떤 글자에도 맞지 않아**, 메뉴로 부르면 늘
        // "테이블 이름을 찾을 수 없습니다" 였다(실측 2026-09-20).
        if (!objectName && sqlEditorRef.current) {
            objectName = qualifiedNameAtCursor(sqlEditorRef.current);
        }
        
        if (!objectName || objectName.trim() === '') {
            toast.info(t('common:table.nameNotFound'));
            return;
        }

        const normalizedObjectName = objectName.trim().toUpperCase();

        if (!schemaNodes || schemaNodes.length === 0) {
            toast.warn(t('common:conn.noSchemaLoaded'));
            return;
        }

        /**
         * 스키마 노드 아래의 **실제 오브젝트**들. 트리가 `스키마 → 폴더(테이블/뷰/…) → 오브젝트`
         * 3단계라, 스키마의 자식을 바로 뒤지면 폴더 이름(`테이블 (21)`)과 견주게 되어 **무엇도
         * 찾지 못한다** — F4 가 줄곧 "찾을 수 없습니다" 였던 진짜 이유다(실측 2026-09-20).
         * 프로시저는 이름에 인자가 붙어(`fn(a, b)`) 이름으로 맞출 수 없으니 뺀다.
         */
        const objectsOf = (schemaNode) => (schemaNode?.children || [])
            .flatMap(child => (child.type === 'folder' ? (child.children || []) : [child]))
            .filter(node => node.type === 'table' || node.type === 'view');

        /** 스키마를 지정해 찾는다 */
        const findInSchema = (schemaName, tableName) => {
            const targetSchemaNode = schemaNodes.find(sNode => (sNode.name || '').toUpperCase() === schemaName);
            const tableNode = objectsOf(targetSchemaNode).find(tNode => (tNode.name || '').toUpperCase() === tableName);
            return tableNode
                ? { schemaName: targetSchemaNode.name, tableName: tableNode.name, connectionId, dbType }
                : null;
        };
        /** 스키마를 모를 때 — 전체를 훑는다 */
        const findAnywhere = (tableName) => {
            for (const sNode of schemaNodes) {
                const tableNode = objectsOf(sNode).find(tNode => (tNode.name || '').toUpperCase() === tableName);
                if (tableNode) return { schemaName: sNode.name, tableName: tableNode.name, connectionId, dbType };
            }
            return null;
        };

        let foundTableInfo = null;
        if (normalizedObjectName.includes('.')) {
            // `스키마.테이블` 은 물론 `스키마.테이블.컬럼` 도 온다(컬럼에 커서를 둔 경우).
            // 앞 **두 토막**을 스키마·테이블로 보고, 아니면 마지막 토막을 이름으로 훑는다 —
            // `db.스키마.테이블` 처럼 앞이 스키마가 아닌 경우까지 이 폴백이 받는다.
            const parts = normalizedObjectName.split('.').filter(Boolean);
            foundTableInfo = findInSchema(parts[0], parts[1]) || findAnywhere(parts[parts.length - 1]);
        } else {
            foundTableInfo = findAnywhere(normalizedObjectName);
        }

        if (foundTableInfo) {
            toast.success(t('common:table.loading', { schema: foundTableInfo.schemaName, table: foundTableInfo.tableName }));
            const tableIdentifier = {
                connectionId: foundTableInfo.connectionId,
                schemaName: foundTableInfo.schemaName,
                tableName: foundTableInfo.tableName,
                dbType: foundTableInfo.dbType 
            };
            fetchAndSetTableDetails(tableIdentifier).then(() => {
                setActiveMainTab('tableInfo'); 
            });
        } else {
            toast.error(t('common:table.notInSchema', { name: objectName }));
        }

    }, [connectionsFromStore, activeConnectionName, fetchAndSetTableDetails, sqlEditorRef]);

    const [isBindModalOpen, setIsBindModalOpen] = useState(false);
    const [bindModalData, setBindModalData] = useState({ statements: [], queryToExecute: '' });

    // SQL 문장 파싱 함수 (SQLEditor와 동일한 로직)
    // 실행 안전장치 (Phase 2) — 운영 연결의 DML/DDL, WHERE 없는 UPDATE/DELETE 를 실행 전에 확인.
    // 설정 > 실행 에서 각각 끌 수 있다. 확인이 필요 없으면 즉시 true 를 반환해 기존 흐름 그대로 진행.
    const confirmRiskyExecution = useCallback(async (sqlText) => {
        if (!sqlText || !sqlText.trim()) return true;
        // **실행 대상 연결**로 판정한다 — 탭이 바인딩한 세션의 연결이다.
        // 예전에는 탐색기 연결(activeConnectionName)로 봤는데, 세션 선택이 다른 연결의 세션도
        // 고를 수 있으므로 둘이 갈린다: 탐색기가 개발을 보는 채 운영 세션에 DML 을 실행하면
        // **경고가 뜨지 않았다.** 반대로 헛경고도 났다. 세션이 없으면 실행이 어차피 막히지만,
        // 그 전에 부르는 경로가 있어 탐색기 연결로 폴백한다.
        const tabNow = activeFileRef.current;
        const sessNow = tabNow?.sessionId
            ? (sessionsListRef.current || []).find(x => x.sessionId === tabNow.sessionId)
            : null;
        const conn = (sessNow && connectionsFromStore.find(c => c.id === sessNow.connectionId))
            || connectionsFromStore.find(c => c.name === activeConnectionName);
        const connType = conn?.connType || 'dev';

        const parsed = parseStatements(sqlText).map(st => st.text).filter(Boolean);
        const a = analyzeStatements(parsed.length > 0 ? parsed : [sqlText]);

        const lines = [];
        if (execSettings.confirmOnProd && isProduction(connType) && (a.hasDml || a.hasDdl)) {
            const kinds = [a.hasDml && t('common:preRun.dml'), a.hasDdl && t('common:preRun.ddl')].filter(Boolean).join(' · ');
            lines.push(t('common:preRun.message', { connType: t(getConnType(connType).labelKey), name: conn?.name || activeConnectionName, kinds: kinds }));
        }
        if (execSettings.warnMissingWhere && a.missingWhereVerbs.length > 0) {
            const verbs = [...new Set(a.missingWhereVerbs)].join(', ');
            lines.push(t('common:preRun.noWhere', { verbs: verbs }));
        }
        if (lines.length === 0) return true;

        return await confirm({
            title: t('common:preRun.title'),
            message: lines.join('\n\n') + t('common:preRun.proceed'),
            confirmText: t('common:ui.run'),
            cancelText: t('common:ui.cancel'),
            variant: 'danger',
        });
    }, [connectionsFromStore, activeConnectionName, execSettings, confirm]);

    const handleExecute = useCallback(async (originalQueryToExecute, isSelection = false) => {
        let queryToExecute = originalQueryToExecute;

        // 쿼리가 전달되지 않았으면 에러 (전체 파일 실행 기능 제거)
        if (!queryToExecute || queryToExecute.trim() === "") {
            toast.warn(t('common:run.noQuery'));
            return;
        }

        // 주석(라인/블록) 은 SQL 의 일부라 원본 그대로 백엔드 전송.
        // 백엔드 sanitizeQuery 가 개행/주석을 보존한 채 세미콜론만 제거함.

        // 위험한 실행이면 사용자 확인을 먼저 받는다 (운영 DML/DDL · WHERE 없는 UPDATE/DELETE)
        if (!(await confirmRiskyExecution(queryToExecute))) return;

        console.log(`[Frontend] handleExecute - Single statement execution, queryLength: ${queryToExecute.length}`);
        console.log(`[Frontend] handleExecute - queryToExecute:`, queryToExecute);

        // SQL 문장들을 파싱하여 각각의 바인드 변수를 검출
        const statements = parseStatements(queryToExecute);
        const statementsWithBindVars = statements.filter(stmt => stmt.bindVars.length > 0);

        console.log(`[Frontend] handleExecute - Parsed ${statements.length} statements, ${statementsWithBindVars.length} have bind vars`);

        if (statementsWithBindVars.length > 0) {
            const currentActiveFile = openFiles.find(f => f.id === activeFileId);
            if (!currentActiveFile) {
                toast.error(t('common:run.noActiveFileBind'));
                return;
            }

            // 문장별 바인드 변수 정보 구성
            const statementBindInfo = statementsWithBindVars.map((stmt, index) => ({
                statementIndex: statements.indexOf(stmt),
                statementText: stmt.text,
                bindVars: stmt.bindVars,
                currentValues: {}
            }));

            // 전달된 쿼리만 파싱 (단일 문장 실행만 지원)
            const realTimeStatements = parseStatements(queryToExecute);
            const realTimeStatementsWithBindVars = realTimeStatements.filter(stmt => stmt.bindVars.length > 0);

            console.log('[Frontend] handleExecute - Parsed statements with bind vars:', realTimeStatementsWithBindVars[0]?.bindVars || []);

            // 기존 저장된 바인드 변수 값들을 적용 (실시간 파싱 기준)
            const fileBindVars = currentActiveFile.bindVariables || {};
            console.log('[Frontend] handleExecute - Auto-execution - Current file bind variables:', fileBindVars);

            // 전달된 쿼리의 바인드 변수 정보 구성 (statement_0으로 처리)
            const realTimeStatementBindInfo = realTimeStatementsWithBindVars.map((stmt, index) => ({
                statementIndex: 0, // 단일 실행은 항상 statement_0으로 처리
                statementText: stmt.text,
                bindVars: stmt.bindVars,
                currentValues: {}
            }));

            let allVariablesFilled = true;
            const finalStatementBindVars = {};

            realTimeStatementBindInfo.forEach(info => {
                const statementKey = `statement_${info.statementIndex}`;
                finalStatementBindVars[statementKey] = {};

                info.bindVars.forEach(varName => {
                    let bindVarValue = null;

                    // 모든 statement_X에서 해당 변수 찾기 (첫 번째로 찾은 값 사용)
                    for (const [stmtKey, stmtVars] of Object.entries(fileBindVars)) {
                        if (stmtKey.startsWith('statement_') && stmtVars && stmtVars[varName] !== undefined) {
                            bindVarValue = stmtVars[varName];
                            console.log(`[Frontend] Found ${varName} in ${stmtKey}:`, JSON.stringify(bindVarValue));
                            break;
                        }
                    }

                    // 빈 문자열('')도 유효한 값으로 처리 - undefined나 null만 "값 없음"으로 처리
                    if (bindVarValue !== null && bindVarValue !== undefined) {
                        info.currentValues[varName] = bindVarValue;
                        finalStatementBindVars[statementKey][varName] = bindVarValue;
                        console.log(`[Frontend] Auto-execution using value for ${statementKey}.${varName}:`, JSON.stringify(bindVarValue));
                    } else {
                        allVariablesFilled = false;
                        console.log(`[Frontend] Auto-execution missing value for ${statementKey}.${varName}`);
                    }
                });
            });

            console.log('[Frontend] handleExecute - Final statement bind vars for auto-execution:', finalStatementBindVars);

            // 모든 바인드 변수에 값이 있으면 자동으로 실행
            if (allVariablesFilled) {
                toast.info(t('common:bind.autoRun'), { autoClose: 2000 });
                console.log('[Frontend] Auto-executing with bind variables:', finalStatementBindVars);
                // 문장별 래퍼를 평탄화해 넘긴다 — 래퍼째 보내면 백엔드가 객체를 바인드하다 NJS-012.
                const flatBindVars = {};
                Object.values(finalStatementBindVars).forEach(v => Object.assign(flatBindVars, v));
                await proceedWithExecution(queryToExecute, queryToExecute, typedBinds(flatBindVars, currentActiveFile.bindTypes));
                return;
            }

            // 일부 변수에 값이 없으면 모달 열기 (실시간 파싱 결과 사용) — 고른 형식은 변수 이름으로 이어 준다
            const savedTypes = Object.assign({}, ...Object.values(currentActiveFile.bindTypes || {}));
            realTimeStatementBindInfo.forEach(info => { info.currentTypes = savedTypes; });
            setBindModalData({
                statements: realTimeStatementBindInfo,
                queryToExecute: queryToExecute, // 원본 쿼리 전달
            });
            setIsBindModalOpen(true);
            return; // 모달에서 사용자 입력을 기다린 후, onBindSubmit에서 실행 계속
        }

        // 바인드 변수가 없는 경우 바로 실행 로직으로
        await proceedWithExecution(queryToExecute);

    }, [activeFileId, openFiles, confirmRiskyExecution]);
    // ↑ proceedWithExecution 은 deps 에서 제외 — 아래에서 정의되어 TDZ. proceedWithExecution
    //   자체가 activeFileRef / sessionsListRef 로 항상 최신 state 참조하므로, handleExecute 가
    //   옛 reference 를 호출해도 결과는 동일 (stale closure 무영향).

    // 바인드 변수 처리 후 실제 실행 로직 (기존 handleExecute의 뒷부분)
    const proceedWithExecution = useCallback(async (finalQueryToExecute, originalQuery = null, bindVariables = null, opts = {}) => {
        // Phase 3: 세션 바인딩 기반 실행. 활성 탭의 sessionId 가 있어야 실행 가능.
        // activeFileRef / sessionsListRef 로 항상 최신 state 참조 — caller chain 의 stale closure 회피.
        const activeTab = activeFileRef.current
            || (openFiles || []).find(f => f.id === activeFileId);
        // opts.sessionId — 데이터 분석 실행은 활성 편집기 탭이 아니라 **결과 탭이 나온 세션**으로 돈다.
        // 활성 탭 기준이면 다른 파일(다른 DB)을 열어 둔 채 누를 때 분석 SQL 이 엉뚱한 DB 로 간다.
        const boundSessionId = opts.sessionId || activeTab?.sessionId;
        if (!opts.sessionId) {
            if (!activeTab) {
                toast.error(t('common:run.noActiveTab'));
                return;
            }
            if (!activeTab.sessionId) {
                toast.warning(t('common:session.pickFirst'));
                return;
            }
        }
        // sessionsList 는 폴링 결과로 1~3s 지연이 있을 수 있어 신뢰할 수 없음 (race).
        // 바인딩 자체를 신뢰하고 진행 — 진짜로 죽은 세션이면 backend 가 404 반환.
        const liveList = sessionsListRef.current || sessionsList || [];
        const session = liveList.find(s => s.sessionId === boundSessionId) || null;
        if (!session) {
            // sessionId 는 set 되어 있지만 sessionsList 에 없음 — 진단 로그 (race tolerance 정책상 진행은 막지 않음).
            console.warn(`[proceedWithExecution] sessionId="${boundSessionId}" 가 sessionsList 에 없음. 폴링 지연이거나 stale binding 가능성. 진행 후 backend 응답으로 검증.`);
        }
        if (session && (session.status === 'running' || session.status === 'cancelling')) {
            toast.warning(t('common:session.busy', { status: session.status }));
            return;
        }
        const activeConnection = session
            ? connectionsFromStore.find(c => c.id === session.connectionId) || null
            : null;

        const currentTabNumToUse = nextQueryResultTabIdCounterRef.current;
        nextQueryResultTabIdCounterRef.current += 1;
        setNextQueryResultTabNum(nextQueryResultTabIdCounterRef.current);

        const newTabId = `query_result_${Date.now()}_${currentTabNumToUse}`;
        const newTabName = t('common:ui.resultTab', { n: currentTabNumToUse });
        const initialFetchSize = 50;
        // 매 실행마다 새 statementId — 결과 탭/history 의 연결고리.
        const statementId = `stmt_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
        const sourceEditorTabId = opts.editorTabId || activeTab?.id || null;
        const sourceSqlSnapshot = finalQueryToExecute;
        const analysisTabFields = { sourceSql: sourceSqlSnapshot };

        const clientStartTime = performance.now();

        // 탭별 AbortController 생성.
        // **세션 id 를 함께 둔다** — 중지할 때 DB 쪽 실행을 끊으려면 그 세션이 필요한데,
        // 그때 탭 목록(state)에서 찾으면 stale closure 에 걸린다(다중 세션 트러블슈팅의 단골 함정).
        const abortController = new AbortController();
        abortControllersRef.current[newTabId] = { controller: abortController, sessionId: boundSessionId };

        console.log(`[Frontend] Created AbortController for tab ${newTabId}, aborted: ${abortController.signal.aborted}`);

        setQueryResultTabs(prevTabs => {
            const newTab = {
                id: newTabId,
                name: newTabName,
                query: finalQueryToExecute,
                connectionId: activeConnection?.id || null,
                sessionId: boundSessionId, // Phase 3: 다음 fetch 도 동일 세션 사용
                // 연결고리 — 에디터 탭 ↔ statement ↔ 결과 탭
                editorTabId: sourceEditorTabId,
                statementId,
                ...analysisTabFields,
                bindVariables: bindVariables || {},
                data: [],
                isLoading: true,
                isLoadingMore: false,
                isExecuting: true, // 실행 중 상태
                error: null,
                message: null,
                cursorId: null,
                hasMore: false,
                currentFetchSize: initialFetchSize,
                executionTime: null,
                clientExecutionTime: null,
                startRowIndex: 0, // 첫 번째 쿼리는 0부터 시작
                isFetchingAll: false, // 전체출력 상태
                isPinned: false // 탭 고정 상태
            };

            // 고정된 탭과 일반 탭 분리
            const pinnedTabs = prevTabs.filter(t => t.isPinned);
            const unpinnedTabs = prevTabs.filter(t => !t.isPinned);

            // 새 탭을 맨 앞에 추가하고, 일반 탭은 최대 5개까지 유지
            const newUnpinnedTabs = [newTab, ...unpinnedTabs];
            const limitedUnpinnedTabs = newUnpinnedTabs.length > 5 ? newUnpinnedTabs.slice(0, 5) : newUnpinnedTabs;

            // 고정된 탭을 맨 앞에, 일반 탭을 뒤에 배치
            return [...pinnedTabs, ...limitedUnpinnedTabs];
        });
        setActiveQueryResultTabId(newTabId);
        setActiveMainTab('queryResults');

        try {
            // Phase 3: 세션 바인딩 라우팅 — /sessions/:sessionId/fetch 사용
            const requestData = {
                sql: finalQueryToExecute,
                bindVariables: bindVariables || {},
                fetchSize: initialFetchSize,
                usrid: user?.email || user?.id || 'standalone',
                requestId: `${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
                timestamp: new Date().toISOString()
            };

            // 바인드 변수가 있을 때 로깅
            if (bindVariables && Object.keys(bindVariables).length > 0) {
                console.log('[Frontend] Sending bind variables to server:', bindVariables);
            }

            const sessionFetchUrl = `/sessions/${boundSessionId}/fetch`;
            console.log(`[Frontend] Sending request to ${sessionFetchUrl}:`, requestData);
            console.log(`[Frontend] Before request - AbortController aborted: ${abortController.signal.aborted}`);

            const response = await axiosInstance.post(sessionFetchUrl, requestData, {
                ...NO_TIMEOUT,
                signal: abortController.signal
            });

            console.log(`[Frontend] After request - AbortController aborted: ${abortController.signal.aborted}`);

            console.log(`[Frontend] Received response from ${sessionFetchUrl}:`, response.data);
            const clientEndTime = performance.now();
            const clientTime = (clientEndTime - clientStartTime) / 1000;
            const { cursorId, rows, metaData, hasMore, currentFetchTime, totalExecutionTime, message: responseMessage, multiStatementResults } = response.data;

            // 다중 문장 결과가 있는 경우 각각 별도 탭으로 생성
            if (multiStatementResults && multiStatementResults.length > 1) {
                console.log(`[Frontend] Multiple statements detected: ${multiStatementResults.length} statements`);

                setQueryResultTabs(prevTabs => {
                    // 기존 로딩 탭 제거
                    const tabsWithoutCurrent = prevTabs.filter(t => t.id !== newTabId);

                    // 각 문장의 결과를 별도 탭으로 생성
                    const newTabs = multiStatementResults.map((stmtResult, index) => {
                        const stmtTabId = `${newTabId}_stmt_${index}`;
                        const stmtTabName = `${newTabName} (${index + 1}/${multiStatementResults.length})`;

                        // 다중 statement — 각 결과 탭에도 동일 connection/session/editor 연결고리.
                        // statementId 는 각 sub-statement 별로 별도 (`{statementId}_${index}`).
                        const subStatementId = `${statementId}_${index}`;
                        if (stmtResult.error) {
                            // 에러가 발생한 문장
                            return {
                                id: stmtTabId,
                                name: stmtTabName,
                                query: stmtResult.statement,
                                connectionId: activeConnection?.id || null,
                                sessionId: boundSessionId,
                                editorTabId: sourceEditorTabId,
                                statementId: subStatementId,
                                ...analysisTabFields,
                                bindVariables: bindVariables || {},
                                data: [],
                                isLoading: false,
                                isLoadingMore: false,
                                isExecuting: false,
                                error: t('common:error.statement', { n: index + 1, message: stmtResult.error.message }),
                                message: null,
                                cursorId: null,
                                hasMore: false,
                                currentFetchSize: initialFetchSize,
                                executionTime: stmtResult.executionTime || 0,
                                clientExecutionTime: clientTime,
                                startRowIndex: 0,
                                isFetchingAll: false,
                                isPinned: false
                            };
                        } else {
                            // 성공한 문장
                            const stmtRows = stmtResult.result?.rows || [];
                            return {
                                id: stmtTabId,
                                name: stmtTabName,
                                query: stmtResult.statement,
                                connectionId: activeConnection?.id || null,
                                sessionId: boundSessionId,
                                editorTabId: sourceEditorTabId,
                                statementId: subStatementId,
                                ...analysisTabFields,
                                bindVariables: bindVariables || {},
                                data: stmtRows,
                                columns: stmtResult.result?.metaData || null,
                                isLoading: false,
                                isLoadingMore: false,
                                isExecuting: false,
                                error: null,
                                // 행이 없는 문장(DDL·DML)은 **무엇을** 실행했는지 앞에 붙인다 — 다른 문장이 돌아도 화면이 말해 주게
                                message: stmtRows.length > 0
                                    ? (stmtResult.result?.message || t('common:run.rowsReturned2', { count: stmtRows.length }))
                                    : t('common:run.executedStatement', { stmt: statementLabel(stmtResult.statement), msg: affectedText(stmtResult.statement, stmtResult.result?.rowsAffected, t) || stmtResult.result?.message || t('common:run.executed') }),
                                cursorId: stmtResult.result?.cursorId || null,
                                hasMore: stmtResult.result?.hasMore || false,
                                currentFetchSize: initialFetchSize,
                                executionTime: stmtResult.result?.totalExecutionTime || 0,
                                clientExecutionTime: clientTime,
                                startRowIndex: 0,
                                isFetchingAll: false,
                                isPinned: false
                            };
                        }
                    });

                    // 고정된 탭과 일반 탭 분리
                    const pinnedTabs = tabsWithoutCurrent.filter(t => t.isPinned);
                    const unpinnedTabs = [...newTabs, ...tabsWithoutCurrent.filter(t => !t.isPinned)];

                    // 일반 탭은 최대 5개까지 유지
                    const limitedUnpinnedTabs = unpinnedTabs.length > 5 ? unpinnedTabs.slice(0, 5) : unpinnedTabs;

                    return [...pinnedTabs, ...limitedUnpinnedTabs];
                });

                // 첫 번째 탭 활성화
                setActiveQueryResultTabId(`${newTabId}_stmt_0`);

            } else {
                // 단일 문장 결과 (기존 로직)
                // 콜백을 t 로 쓰면 번역 t 를 가려 t('...') 가 탭 객체 호출이 된다 (런타임 크래시)
                setQueryResultTabs(prevTabs => prevTabs.map(tab =>
                    tab.id === newTabId
                        ? {
                            ...tab,
                            data: rows || [],
                            columns: metaData || tab.columns || null,
                            isLoading: false,
                            isExecuting: false, // 실행 완료
                            error: null,
                            message: rows && rows.length > 0
                                ? (responseMessage || t('common:run.rowsReturned', { count: rows.length }))
                                : t('common:run.executedStatement', { stmt: statementLabel(finalQueryToExecute), msg: affectedText(finalQueryToExecute, response.data.rowsAffected, t) || responseMessage || t('common:run.executed') }),
                            cursorId,
                            hasMore,
                            executionTime: totalExecutionTime, // 총 실행 시간
                            currentFetchTime, // 현재 fetch 시간
                            clientExecutionTime: clientTime
                          }
                        : tab
                ));
            }
            // 히스토리에 상세 정보 추가 — 연결고리 (editorTabId/statementId/resultTabId) 포함
            dispatch(addToHistory({
                // opts.historyQuery — 주석이 든 원문(보낸 문장은 주석을 뺀 것이다)
                query: opts.historyQuery || finalQueryToExecute,
                originalQuery: opts.historyQuery || originalQuery || finalQueryToExecute,
                bindVariables: bindVariables || null,
                timestamp: new Date().toISOString(),
                success: true,
                fetchCount: Array.isArray(rows) ? rows.length : 0,
                executionTime: totalExecutionTime,
                hasMore: hasMore || false,
                editorTabId: sourceEditorTabId,
                statementId,
                resultTabId: newTabId,
            }));
        } catch (error) {
            console.error('[Frontend] Error in proceedWithExecution:', error);
            console.error('[Frontend] Error details:', {
                name: error.name,
                code: error.code,
                message: error.message,
                response: error.response?.data
            });

            const clientEndTime = performance.now();
            const clientTime = (clientEndTime - clientStartTime) / 1000;

            // 세션이 backend 에서 사라진 경우 (404) — 즉시 unbind + 안내
            if (error.response?.status === 404 && (error.response?.data?.error || '').includes('Session not found')) {
                // 죽은 세션에 묶인 편집기 탭을 푼다 — 분석 실행은 활성 탭이 아닌 결과 탭의 세션을 쓰므로 세션으로 찾는다
                setOpenFiles(prev => prev.map(f => f.sessionId === boundSessionId ? { ...f, sessionId: null } : f));
                setQueryResultTabs(prevTabs => prevTabs.filter(t => t.id !== newTabId));
                toast.warning(t('common:session.boundGone'));
                delete abortControllersRef.current[newTabId];
                return;
            }

            // 쿼리 취소 처리
            if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
                // 콜백을 t 로 쓰면 번역 t 를 가려 t('...') 가 탭 객체 호출이 된다 (런타임 크래시)
                setQueryResultTabs(prevTabs => prevTabs.map(tab =>
                    tab.id === newTabId
                        ? {
                            ...tab,
                            isLoading: false,
                            isExecuting: false, // 실행 중지
                            error: null,
                            message: t('common:run.stoppedByUser'),
                            executionTime: null,
                            clientExecutionTime: clientTime
                          }
                        : tab
                ));
                toast.warning(t('common:run.stopped'));
                // AbortController 정리
                delete abortControllersRef.current[newTabId];
                return;
            }

            const errorResponse = error.response?.data || {};
            const errorMessage = errorResponse.error || error.message || 'An error occurred';
            const currentFetchTime = errorResponse.currentFetchTime;

            // 권한 에러 특별 처리
            if (error.response?.status === 403 || errorResponse.errorType === 'PERMISSION_ERROR' || errorResponse.errorType === 'PERMISSION_DENIED') {
                const permissionDetails = errorResponse.violations
                    ? errorResponse.violations.map(v => v.message).join('\n• ')
                    : '';

                const detailedPermissionMessage = t('common:error.permissionDetail', {
                    message: errorMessage,
                    details: permissionDetails ? t('common:error.violations', { details: permissionDetails }) : '',
                });

                // 콜백을 t 로 쓰면 번역 t 를 가려 t('...') 가 탭 객체 호출이 된다 (런타임 크래시)
                setQueryResultTabs(prevTabs => prevTabs.map(tab =>
                    tab.id === newTabId
                        ? {
                            ...tab,
                            isLoading: false,
                            isExecuting: false, // 실행 완료 (권한 에러)
                            error: detailedPermissionMessage,
                            message: t('common:error.permission'),
                            errorType: 'PERMISSION_ERROR',
                            executionTime: currentFetchTime,
                            clientExecutionTime: clientTime
                          }
                        : tab
                ));

                // 권한 에러 toast 알림
                toast.error(t('common:run.rejected', { message: errorMessage }));
            }
            // 인증 에러 처리
            else if (error.response?.status === 401 || errorResponse.errorType === 'AUTHENTICATION_REQUIRED') {
                const authMessage = t('common:error.authRequiredIcon');

                // 콜백을 t 로 쓰면 번역 t 를 가려 t('...') 가 탭 객체 호출이 된다 (런타임 크래시)
                setQueryResultTabs(prevTabs => prevTabs.map(tab =>
                    tab.id === newTabId
                        ? {
                            ...tab,
                            isLoading: false,
                            isExecuting: false, // 실행 완료 (인증 에러)
                            error: authMessage,
                            message: t('common:error.auth'),
                            errorType: 'AUTHENTICATION_REQUIRED',
                            executionTime: currentFetchTime,
                            clientExecutionTime: clientTime
                          }
                        : tab
                ));

                toast.error(t('common:error.authRequired'));
            }
            // 일반 에러 처리
            else {
                // 콜백을 t 로 쓰면 번역 t 를 가려 t('...') 가 탭 객체 호출이 된다 (런타임 크래시)
                setQueryResultTabs(prevTabs => prevTabs.map(tab =>
                    tab.id === newTabId
                        ? {
                            ...tab,
                            isLoading: false,
                            isExecuting: false, // 실행 완료 (에러)
                            error: errorMessage,
                            message: t('common:error.generic'),
                            errorType: errorResponse.errorType || 'GENERAL_ERROR',
                            executionTime: currentFetchTime,
                            clientExecutionTime: clientTime
                          }
                        : tab
                ));
            }

            // 에러 발생 시에도 히스토리에 추가 (원본 쿼리와 바인드 변수 정보 포함)
            dispatch(addToHistory({
                query: opts.historyQuery || finalQueryToExecute, // 실행된 쿼리 — 주석이 든 원문이 있으면 그것
                originalQuery: opts.historyQuery || originalQuery || finalQueryToExecute, // 원본 쿼리
                bindVariables: bindVariables || null, // 바인드 변수들
                timestamp: new Date().toISOString(),
                success: false,
                fetchCount: 0,
                executionTime: currentFetchTime || clientTime,
                error: errorMessage,
                errorType: errorResponse.errorType || 'GENERAL_ERROR',
                hasMore: false
            }));
        } finally {
            // AbortController 정리 (에러가 아닌 경우)
            if (abortControllersRef.current[newTabId]) {
                delete abortControllersRef.current[newTabId];
            }
        }
    }, [activeConnectionName, connectionsFromStore, axiosInstance, nextQueryResultTabNum, dispatch, user, openFiles, activeFileId, sessionsList]); // 세션 바인딩 라우팅 위해 openFiles/activeFileId/sessionsList 도 deps

    const handleBindModalSubmit = useCallback(async (submittedStatementBindVars, submittedTypes = {}) => {
        const { queryToExecute: originalQuery, statements, editOnly, historyQuery } = bindModalData;

        console.log('[Frontend] handleBindModalSubmit - Received bind variables:', submittedStatementBindVars);

        // 1. 현재 활성 파일의 bindVariables를 문장별 구조로 업데이트
        setOpenFiles(prevFiles => {
            const updatedFiles = prevFiles.map(file =>
                file.id === activeFileId
                    ? {
                        ...file,
                        bindVariables: {
                            ...file.bindVariables,
                            ...submittedStatementBindVars // submittedStatementBindVars는 { statement_0: {b1: 'value'}, statement_1: {b1: 'different'} } 형태
                        },
                        // 고른 형식(기본 VARCHAR2) — 다음 실행·편집에도 이어진다
                        bindTypes: { ...(file.bindTypes || {}), ...submittedTypes },
                        isDirty: true,
                    }
                    : file
            );

            const updatedFile = updatedFiles.find(f => f.id === activeFileId);
            console.log('[Frontend] handleBindModalSubmit - Updated file bind variables:', updatedFile?.bindVariables);

            return updatedFiles;
        });

        console.log('[Frontend] handleBindModalSubmit - About to execute with bind variables:', submittedStatementBindVars);

        // 편집기 오른쪽 섹션의 「편집」 — 값만 저장하고 실행하지 않는다(2026-10-26: 예전에는 탭 전체를 실행했다)
        if (editOnly) {
            setIsBindModalOpen(false);
            toast.success(t('common:bv.saved'));
            return;
        }

        // 바인드 변수 정보를 합쳐서 표시용 메시지 생성
        const allBindVars = {};
        Object.values(submittedStatementBindVars).forEach(stmtVars => {
            Object.assign(allBindVars, stmtVars);
        });

        toast.info(<div>{t('common:preRun.withBinds')}<br/><strong>Variables:</strong> {Object.entries(allBindVars).map(([k, v]) => `${k}=${v}`).join(', ')}</div>, { autoClose: 5000 });

        setIsBindModalOpen(false); // 모달 닫기

        console.log('[Frontend] handleBindModalSubmit - Final execution with bind vars:', JSON.stringify(allBindVars, null, 2));
        // 실행에는 평탄화된 값을 넘긴다 — 문장별 래퍼({statement_1:{...}})를 그대로 보내면
        // 백엔드가 oracledb 에 객체를 바인드하려다 NJS-012 로 죽는다 (실측).
        // 래퍼 구조는 파일의 bindVariables 저장(모달 prefill)용으로만 쓴다.
        await proceedWithExecution(originalQuery, originalQuery, typedBinds(allBindVars, submittedTypes), historyQuery ? { historyQuery } : {});

    }, [activeFileId, bindModalData, proceedWithExecution, t]);

    const handleBindModalClose = useCallback(() => {
        setIsBindModalOpen(false);
    }, []);

    // 바인드 변수 편집 핸들러 (SQLEditor에서 클릭 시 호출)
    const handleEditBindVariables = useCallback((targetQuery = null, specificStatementIndex = null) => {
        // 활성 탭의 글, 없으면 **편집기에 보이는 글** — 탭 기록이 잠깐 비어 「편집할 쿼리가 없습니다」 가 뜨던 길을 막는다(2026-10-26)
        const viewText = sqlEditorRef.current && sqlEditorRef.current.state ? sqlEditorRef.current.state.doc.toString() : '';
        let queryToEdit = targetQuery || (activeFileRef.current && activeFileRef.current.content) || viewText;

        if (!queryToEdit || queryToEdit.trim() === '') {
            toast.warn(t('common:run.noQueryToEdit'));
            return;
        }

        // SQL 문장들을 파싱하여 각각의 바인드 변수를 검출
        const statements = parseStatements(queryToEdit);
        const statementsWithBindVars = statements.filter(stmt => stmt.bindVars.length > 0);

        if (statementsWithBindVars.length === 0) {
            toast.info(t('common:bind.none'));
            return;
        }

        const currentActiveFile = openFiles.find(f => f.id === activeFileId) || activeFileRef.current;
        if (!currentActiveFile) {
            toast.error(t('common:run.noActiveFile'));
            return;
        }

        // 특정 문장만 편집하는 경우 해당 문장만 필터링
        let targetStatements = statementsWithBindVars;
        if (specificStatementIndex !== null) {
            const targetStatement = statements[specificStatementIndex];
            if (targetStatement && targetStatement.bindVars && targetStatement.bindVars.length > 0) {
                targetStatements = [targetStatement];
            } else {
                toast.warn(t('common:bind.noneInStatement', { n: specificStatementIndex + 1 }));
                return;
            }
        }

        // 문장별 바인드 변수 정보 구성
        const statementBindInfo = targetStatements.map((stmt, index) => ({
            statementIndex: statements.indexOf(stmt),
            statementText: stmt.text,
            bindVars: stmt.bindVars,
            currentValues: {},
            currentTypes: (currentActiveFile.bindTypes || {})[`statement_${statements.indexOf(stmt)}`] || {},
        }));

        // 기존 저장된 바인드 변수 값들을 적용
        const fileBindVars = currentActiveFile.bindVariables || {};
        console.log('[Frontend] handleEditBindVariables - Current file bind variables:', fileBindVars);

        statementBindInfo.forEach(info => {
            info.bindVars.forEach(varName => {
                const statementKey = `statement_${info.statementIndex}`;
                if (fileBindVars[statementKey] && fileBindVars[statementKey][varName] !== undefined) {
                    info.currentValues[varName] = fileBindVars[statementKey][varName];
                    console.log(`[Frontend] Found saved value for ${statementKey}.${varName}:`, JSON.stringify(fileBindVars[statementKey][varName]));
                } else {
                    console.log(`[Frontend] No saved value for ${statementKey}.${varName} - value is:`, fileBindVars[statementKey]?.[varName]);
                }
            });
        });

        console.log('[Frontend] handleEditBindVariables - Statement bind info:', statementBindInfo);

        // 편집 모달 열기
        setBindModalData({
            statements: statementBindInfo,
            queryToExecute: queryToEdit,
            isSpecificStatement: specificStatementIndex !== null,
            specificStatementNumber: specificStatementIndex !== null ? specificStatementIndex + 1 : null,
            editOnly: true,
        });
        setIsBindModalOpen(true);
    }, [activeFileId, openFiles]);

    const executeSQLEditorQuery = useCallback(() => {
        // 임시패스워드 상태에서는 실행 차단
        if (isTemporaryPassword) {
            toast.warning(t('common:error.tempPassword'));
            return;
        }
        
        if (activeFileRef.current && activeFileRef.current.content) {
            const editorView = sqlEditorRef.current;
            let queryToExecute = '';
            let isSelection = false;

            if (editorView && editorView.state) {
                const { main } = editorView.state.selection;
                const selectedText = editorView.state.doc.sliceString(main.from, main.to);
                
                if (main.from !== main.to && selectedText.trim()) {
                    // 선택된 텍스트가 있으면 사용
                    queryToExecute = selectedText.trim();
                    isSelection = true;
                } else {
                    // 선택된 텍스트가 없으면 커서 위치의 문장 추출
                    const docText = editorView.state.doc.toString();
                    const cursorPosition = main.head;
                    
                    // SQLEditor와 동일한 로직으로 문장 추출
                    if (!docText.trim()) {
                        return;
                    }

                    // 세미콜론으로 문장들을 분리 (SQLEditor와 동일한 로직)
                    const statements = [];
                    let currentStart = 0;
                    let inSingleQuote = false;
                    let inDoubleQuote = false;
                    let escaped = false;

                    for (let i = 0; i < docText.length; i++) {
                        const char = docText[i];

                        if (escaped) {
                            escaped = false;
                            continue;
                        }

                        if (char === '\\') {
                            escaped = true;
                            continue;
                        }

                        if (char === "'" && !inDoubleQuote) {
                            inSingleQuote = !inSingleQuote;
                        } else if (char === '"' && !inSingleQuote) {
                            inDoubleQuote = !inDoubleQuote;
                        } else if (char === ';' && !inSingleQuote && !inDoubleQuote) {
                            const statement = docText.substring(currentStart, i).trim();
                            if (statement) {
                                statements.push({
                                    text: statement,
                                    start: currentStart,
                                    end: i
                                });
                            }
                            currentStart = i + 1;
                        }
                    }

                    // 마지막 문장 (세미콜론이 없을 수도 있음)
                    const lastStatement = docText.substring(currentStart).trim();
                    if (lastStatement) {
                        statements.push({
                            text: lastStatement,
                            start: currentStart,
                            end: docText.length
                        });
                    }

                    if (statements.length === 0) {
                        return;
                    }

                    // 커서 위치에 해당하는 문장 찾기
                    let foundStatement = null;
                    for (const stmt of statements) {
                        if (cursorPosition >= stmt.start && cursorPosition <= stmt.end) {
                            foundStatement = stmt.text;
                            break;
                        }
                    }

                    // 커서가 어떤 문장에도 속하지 않으면 가장 가까운 문장 반환
                    if (!foundStatement) {
                        let closestStatement = statements[0];
                        let minDistance = Math.abs(cursorPosition - statements[0].start);

                        for (const stmt of statements) {
                            const distanceToStart = Math.abs(cursorPosition - stmt.start);
                            const distanceToEnd = Math.abs(cursorPosition - stmt.end);
                            const minDistanceToStmt = Math.min(distanceToStart, distanceToEnd);

                            if (minDistanceToStmt < minDistance) {
                                minDistance = minDistanceToStmt;
                                closestStatement = stmt;
                            }
                        }
                        foundStatement = closestStatement.text;
                    }

                    queryToExecute = foundStatement;
                }
            } else {
                // 에디터 뷰가 없으면 전체 파일 내용 사용
                queryToExecute = activeFileRef.current.content;
            }
            
            if (queryToExecute && queryToExecute.trim()) {
                handleExecute(queryToExecute, isSelection);
            } else {
                toast.info(t('common:run.noStatement'));
            }
        }
    }, [handleExecute, activeFileRef, sqlEditorRef, isTemporaryPassword]);

    const handleExecuteAll = useCallback(() => {
        // 임시패스워드 상태에서는 실행 차단
        if (isTemporaryPassword) {
            toast.warning(t('common:error.tempPassword'));
            return;
        }
        
        if (activeFile && activeFile.content) {
            handleExecute(activeFile.content);
        }
    }, [activeFile, handleExecute, isTemporaryPassword]);

    const handleExecuteWithStatementIndex = useCallback(async (queryToExecute, statementIndex) => {
        if (!queryToExecute || queryToExecute.trim() === "") {
            toast.warn(t('common:run.noQuery'));
            return;
        }
        if (!(await confirmRiskyExecution(queryToExecute))) return;

        // 라인 주석(--) 제거 전처리
        // 주석은 문자열 안의 `--` 를 건드리지 않고 지운다(정규식으로 지우면 '--x' 같은 값이 잘렸다).
        // 실행이력에는 **주석이 든 원문**을 남긴다(2026-10-28 사용자 요청 — 이력에서 주석이 사라졌다). 보내는 문장만 주석을 뺀다
        const historyQuery = queryToExecute.trim();
        queryToExecute = stripSqlComments(queryToExecute);
        // 주석만 있던 조각(책갈피 줄 등)은 보낼 문장이 없다
        if (!queryToExecute.trim()) {
            toast.warn(t('common:run.noQuery'));
            return;
        }

        console.log(`[Frontend] handleExecuteWithStatementIndex - Statement index: ${statementIndex}, queryLength: ${queryToExecute.length}`);
        console.log(`[Frontend] handleExecuteWithStatementIndex - queryToExecute:`, queryToExecute);

        // SQL 문장 파싱
        const realTimeStatements = parseStatements(queryToExecute);
        const realTimeStatementsWithBindVars = realTimeStatements.filter(stmt => stmt.bindVars.length > 0);

        console.log(`[Frontend] handleExecuteWithStatementIndex - Parsed ${realTimeStatements.length} statements, ${realTimeStatementsWithBindVars.length} have bind vars`);

        if (realTimeStatementsWithBindVars.length > 0) {
            const currentActiveFile = openFiles.find(f => f.id === activeFileId);
            if (!currentActiveFile) {
                toast.error(t('common:run.noActiveFileBind'));
                return;
            }

            // 기존 저장된 바인드 변수 값들을 적용
            const fileBindVars = currentActiveFile.bindVariables || {};
            console.log('[Frontend] handleExecuteWithStatementIndex - Current file bind variables:', fileBindVars);

            // 실제 statement index를 사용하여 바인드 변수 구성
            const realTimeStatementBindInfo = realTimeStatementsWithBindVars.map((stmt, index) => ({
                statementIndex: statementIndex, // 전달받은 실제 statement index 사용
                statementText: stmt.text,
                bindVars: stmt.bindVars,
                currentValues: {}
            }));

            let allVariablesFilled = true;
            const finalStatementBindVars = {};

            realTimeStatementBindInfo.forEach(info => {
                const statementKey = `statement_${info.statementIndex}`;
                finalStatementBindVars[statementKey] = {};

                info.bindVars.forEach(varName => {
                    let bindVarValue = null;

                    // 해당 statement의 바인드 변수 값 찾기
                    const stmtVars = fileBindVars[statementKey];
                    if (stmtVars && stmtVars[varName] !== undefined) {
                        bindVarValue = stmtVars[varName];
                        console.log(`[Frontend] Found ${varName} in ${statementKey}:`, JSON.stringify(bindVarValue));
                    }

                    // 빈 문자열('')도 유효한 값으로 처리
                    if (bindVarValue !== null && bindVarValue !== undefined) {
                        info.currentValues[varName] = bindVarValue;
                        finalStatementBindVars[statementKey][varName] = bindVarValue;
                    } else {
                        console.log(`[Frontend] Missing bind variable ${varName} for ${statementKey}`);
                        allVariablesFilled = false;
                    }
                });
            });

            console.log('[Frontend] handleExecuteWithStatementIndex - Auto-execution bind variables:', finalStatementBindVars);

            if (allVariablesFilled) {
                // 모든 바인드 변수가 채워져 있으면 자동 실행
                console.log('[Frontend] handleExecuteWithStatementIndex - All variables filled, executing automatically');
                // 문장별 래퍼를 평탄화해 넘긴다 — 래퍼째 보내면 백엔드가 객체를 바인드하다 NJS-012.
                const flatBindVars = {};
                Object.values(finalStatementBindVars).forEach(v => Object.assign(flatBindVars, v));
                await proceedWithExecution(queryToExecute, null, flatBindVars, { historyQuery });
            } else {
                // 바인드 변수 모달 표시
                console.log('[Frontend] handleExecuteWithStatementIndex - Missing variables, showing modal');
                setBindModalData({
                    statements: realTimeStatementBindInfo,
                    queryToExecute: queryToExecute,
                    historyQuery,
                });
                setIsBindModalOpen(true);
            }
        } else {
            // 바인드 변수가 없으면 바로 실행
            console.log('[Frontend] handleExecuteWithStatementIndex - No bind variables, executing directly');
            await proceedWithExecution(queryToExecute, null, null, { historyQuery });
        }
    }, [openFiles, activeFileId, proceedWithExecution, setBindModalData, setIsBindModalOpen, confirmRiskyExecution]);

    const handleExecuteCurrentWithContext = useCallback((queryToExecute, statementIndex) => {
        // 임시패스워드 상태에서는 실행 차단
        if (isTemporaryPassword) {
            toast.warning(t('common:error.tempPassword'));
            return;
        }

        console.log(`[Frontend] handleExecuteCurrentWithContext - Statement index: ${statementIndex}, Query:`, queryToExecute);

        // statementIndex 정보와 함께 handleExecute 호출
        handleExecuteWithStatementIndex(queryToExecute, statementIndex);
    }, [isTemporaryPassword, handleExecuteWithStatementIndex]);

    const handleExecuteCurrent = useCallback(() => {
        // 임시패스워드 상태에서는 실행 차단
        if (isTemporaryPassword) {
            toast.warning(t('common:error.tempPassword'));
            return;
        }

        // SQLEditor의 현재 문장 실행 기능 사용
        if (sqlEditorRef.current && sqlEditorRef.current.handleExecuteInternal) {
            sqlEditorRef.current.handleExecuteInternal();
        }
    }, [isTemporaryPassword]);

    // Global keyboard event handler
    useEffect(() => {
        const handleGlobalKeyDown = (event) => {
            // PL/SQL 편집기 등 자체 CodeMirror 인스턴스를 가진 모달이 열려 있으면
            // 메인 SQLEditor 대상 전역 단축키(F5 전체실행 등)가 겹쳐 실행되지 않게 건너뛴다.
            if (document.querySelector('.plsqled-overlay')) {
                return;
            }
            if ((event.ctrlKey || event.metaKey) && event.key === 'e') {
                event.preventDefault(); // 브라우저 기본 동작 방지

                // SQLEditor가 포커스 되어 있을 때만 실행 계획 관련 로직 호출
                if (isEditorFocused && sqlEditorRef.current) {
                    executeSQLEditorPlan(); // App.js 내의 콜백 함수 호출
                }
            } else if ((event.ctrlKey || event.metaKey) && event.shiftKey && (event.key === 'F' || event.key === 'f')) {
                // Ctrl+Shift+F — 전역 검색 (모든 열린 스크립트/프로젝트 대상)
                event.preventDefault();
                setShowGlobalSearch(true);
            } else if ((event.ctrlKey || event.metaKey) && event.shiftKey && (event.key === 'S' || event.key === 's')) {
                // Ctrl+Shift+S — 프로젝트 저장
                event.preventDefault();
                handleSaveProject();
            } else if ((event.ctrlKey || event.metaKey) && event.shiftKey && (event.key === 'O' || event.key === 'o')) {
                // Ctrl+Shift+O — 프로젝트 열기 (기존 파일 다이얼로그와 공유)
                event.preventDefault();
                handleOpenFile();
            } else if ((event.ctrlKey || event.metaKey) && event.key === 's') {
                event.preventDefault(); // 브라우저 기본 동작 (페이지 저장 등) 방지
                handleSaveFile();
            } else if ((event.ctrlKey || event.metaKey) && event.key === 'o') {
                event.preventDefault();
                handleOpenFile();
            } else if (event.altKey && (event.key === 'n' || event.key === 'N')) {
                event.preventDefault();

                // 에디터 포커스 상태와 관계없이 새 탭 생성
                handleNewTab();
            } else if (event.key === 'F5') {
                event.preventDefault(); // 브라우저 새로고침 방지

                // F5는 전체 파일 실행
                handleExecuteAll();
            }
        };

        window.addEventListener('keydown', handleGlobalKeyDown, { capture: true });
        return () => {
            window.removeEventListener('keydown', handleGlobalKeyDown, { capture: true });
        };
    }, [isEditorFocused, executeSQLEditorPlan, handleSaveFile, handleSaveProject, handleNewTab, handleOpenFile, handleExecuteAll]); // 의존성 배열에 추가

    // 전체출력 기능 - 남은 데이터를 한번에 가져오기 (최대 10,000건)
    const handleFetchAll = useCallback(async (tabId) => {
        const targetTab = queryResultTabs.find(tab => tab.id === tabId);
        if (!targetTab || !targetTab.hasMore || targetTab.isFetchingAll) {
            return;
        }

        // 탭 상태를 "전체 가져오는 중"으로 설정
        setQueryResultTabs(prevTabs => prevTabs.map(tab =>
            tab.id === tabId
                ? { ...tab, isFetchingAll: true }
                : tab
        ));

        // 최대 1만 건을 100건씩 나눠 받는다 — 길면 몇 분이다. 제한시간을 없앤 대신 **중지**가 있어야 한다.
        const allController = new AbortController();
        abortControllersRef.current[tabId] = { controller: allController, sessionId: targetTab.sessionId || null };

        try {
            let allNewRows = [];
            let currentCursorId = targetTab.cursorId;
            let stillHasMore = targetTab.hasMore;
            let totalFetched = targetTab.data ? targetTab.data.length : 0;
            const maxRows = 10000;
            const initialDataLength = targetTab.data ? targetTab.data.length : 0;

            console.log(`전체출력 시작: 현재 ${initialDataLength}건, 최대 ${maxRows}건까지 가져오기`);

            while (stillHasMore && totalFetched < maxRows) {
                const remainingQuota = maxRows - totalFetched;
                const fetchSize = Math.min(100, remainingQuota); // 한번에 최대 100건씩

                const response = await axiosInstance.post('/fetch-data', {
                    cursorId: currentCursorId,
                    fetchSize: fetchSize,
                    usrid: user?.email || user?.id || 'standalone',
                    connectionId: targetTab.connectionId
                }, { ...NO_TIMEOUT, signal: allController.signal });

                const { cursorId: newCursorId, rows: newRows, hasMore: newHasMore } = response.data;
                
                if (newRows && newRows.length > 0) {
                    allNewRows = [...allNewRows, ...newRows];
                    totalFetched += newRows.length;
                    
                    console.log(`Fetch 진행: +${newRows.length}건, 누적 새로운 데이터: ${allNewRows.length}건, 총 예상: ${initialDataLength + allNewRows.length}건`);
                }

                currentCursorId = newCursorId;
                stillHasMore = newHasMore;

                // 최대 건수 도달 시 중단
                if (totalFetched >= maxRows) {
                    stillHasMore = false;
                    break;
                }
            }

            // 최종 업데이트 (한 번만!)
            setQueryResultTabs(prevTabs => prevTabs.map(tab => 
                tab.id === tabId 
                    ? { 
                        ...tab, 
                        data: [...targetTab.data, ...allNewRows], // 원본 + 새로운 데이터
                        cursorId: currentCursorId,
                        hasMore: stillHasMore,
                        isFetchingAll: false
                      }
                    : tab
            ));

            console.log(`전체출력 완료: ${allNewRows.length}건 추가 로드 (총 ${initialDataLength + allNewRows.length}건)`);

        } catch (error) {
            // 중지는 오류가 아니다 — 여기까지 받은 것은 버리고 탭은 이어서 쓸 수 있게 둔다
            if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
                setQueryResultTabs(prevTabs => prevTabs.map(tb =>
                    tb.id === tabId ? { ...tb, isFetchingAll: false } : tb
                ));
                return;
            }
            console.error('전체출력 중 오류:', error);
            const errorResponse = error.response?.data || {};
            let errorMessage = errorResponse.error || error.message || t('common:csv.exportAllFailed');

            // 권한 에러 특별 처리
            if (error.response?.status === 403 || errorResponse.errorType === 'PERMISSION_ERROR' || errorResponse.errorType === 'PERMISSION_DENIED') {
                const permissionDetails = errorResponse.violations
                    ? errorResponse.violations.map(v => v.message).join('\n• ')
                    : '';
                errorMessage = t('common:error.permissionDetail', {
                    message: errorMessage,
                    details: permissionDetails ? t('common:error.violations', { details: permissionDetails }) : '',
                });
                toast.error(t('common:run.fetchRejected', { message: errorResponse.error }));
            }
            // 인증 에러 처리
            else if (error.response?.status === 401 || errorResponse.errorType === 'AUTHENTICATION_REQUIRED') {
                errorMessage = t('common:error.authRequiredIcon');
                toast.error(t('common:error.authRequired'));
            }

            setQueryResultTabs(prevTabs => prevTabs.map(tab =>
                tab.id === tabId
                    ? {
                        ...tab,
                        isFetchingAll: false,
                        error: errorMessage,
                        errorType: errorResponse.errorType || 'GENERAL_ERROR'
                      }
                    : tab
            ));
        } finally {
            if (abortControllersRef.current[tabId]?.controller === allController) {
                delete abortControllersRef.current[tabId];
            }
        }
    }, [queryResultTabs, axiosInstance, user]);

    // CSV 저장 기능
    const handleExportCSV = useCallback((tabId) => {
        const targetTab = queryResultTabs.find(tab => tab.id === tabId);
        if (!targetTab || !targetTab.data || targetTab.data.length === 0) {
            toast.warning(t('common:file.nothingToSave'));
            return;
        }

        try {
            const data = targetTab.data;
            if (data.length === 0) return;

            // 시스템 컬럼(__rowNumber__ 등) 제외한 실제 데이터만 추출
            const firstRow = data[0];
            const columns = Object.keys(firstRow).filter(key => !key.startsWith('__'));
            
            // CSV 헤더 생성
            const csvHeaders = columns.join(',');
            
            // CSV 데이터 생성
            const csvRows = data.map(row => 
                columns.map(col => {
                    const value = row[col];
                    // 쉼표나 따옴표가 포함된 값은 따옴표로 감싸기
                    if (value === null || value === undefined) return '';
                    const stringValue = String(value);
                    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                        return `"${stringValue.replace(/"/g, '""')}"`;
                    }
                    return stringValue;
                }).join(',')
            );

            const csvContent = [csvHeaders, ...csvRows].join('\n');
            
            // BOM 추가 (한글 엑셀에서 올바르게 표시되도록)
            const bom = '\uFEFF';
            const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
            
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${targetTab.name}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);

            console.log(`CSV 저장 완료: ${data.length}건, ${columns.length}개 컬럼`);

        } catch (error) {
            console.error('CSV 저장 중 오류:', error);
            toast.error(t('common:csv.saveFailed'));
        }
    }, [queryResultTabs]);
    
    /**
     * 계획 탭 하나를 파일로 — `.md`(사람이 읽는 것) / `.livesqlplan`(다시 여는 것).
     * 서버를 거치지 않는다(handleExportCSV 와 같은 수법). **BOM 은 붙이지 않는다** —
     * 그건 CSV 를 엑셀이 읽게 하려던 것이고, 여기 붙이면 마크다운 첫 글자가 깨져 보인다.
     */
    const handleExportPlanFile = useCallback((tab, format = 'md') => {
        if (!tab) return;
        try {
            const conn = connectionsFromStore.find(c => c.id === tab.connectionId);
            const group = {
                title: tab.name,
                kind: tab.compare ? 'pair' : 'single',
                createdAt: new Date().toISOString().slice(0, 19).replace('T', ' '),
                summary: tab.summary || null,
                verdict: tab.verdict || null,
                items: planTabToItems(tab, conn),
            };
            const md = format === 'md';
            const body = md ? planArchiveToMarkdown(group) : serializePlanArchive(group);
            const blob = new Blob([body], {
                type: md ? 'text/markdown;charset=utf-8' : 'application/json;charset=utf-8',
            });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            link.download = `${tab.name}_${stamp}.${md ? 'md' : 'livesqlplan'}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (error) {
            toast.error(formatApiError(error, t));
        }
    }, [connectionsFromStore, t]);

    /** 계획 탭 하나를 보관함으로. 제목은 사람이 정한다 — 목록에서 다시 찾는 단서가 그것뿐이다. */
    const handleSavePlanToArchive = useCallback(async (tab) => {
        if (!tab) return;
        const conn = connectionsFromStore.find(c => c.id === tab.connectionId);
        const title = await prompt({
            title: t('common:planArchive.savePromptTitle'),
            message: t('common:planArchive.savePromptLabel'),
            defaultValue: conn ? `${tab.name} · ${conn.name}` : tab.name,
            confirmText: t('common:planArchive.save'),
        });
        if (!title) return;   // 취소
        try {
            await savePlanArchive({
                title,
                kind: tab.compare ? 'pair' : 'single',
                summary: tab.summary || null,
                verdict: tab.verdict || null,
                source: tab.origin === 'agent' ? 'agent' : 'manual',
                threadId: tab.threadId || null,
                connectionId: tab.connectionId || null,
                items: planTabToItems(tab, conn),
            });
            toast.success(t('common:planArchive.saved'));
        } catch (error) {
            toast.error(formatApiError(error, t));
        }
    }, [connectionsFromStore, prompt, t]);

    /** 실행계획 탭의 계획·SQL 을 SQL 진단 리포트의 새 작업 양식으로 넘긴다(비교 탭이면 개선 전 쪽) */
    const handlePlanToReview = useCallback((tab) => {
        if (!tab) return;
        const items = planTabToItems(tab, connectionsFromStore.find(c => c.id === tab.connectionId));
        const it = items[0] || {};
        setSqlReviewSeed({ title: tab.name || '', sql: it.sql || '', planText: it.planText || '', sourceNote: t('common:srv.fromPlan'), nonce: Date.now() });
        setSqlReviewOpen(true);
    }, [connectionsFromStore, t]);

    /**
     * 보관물을 실행계획 탭으로 되돌린다.
     *
     * **저장 당시의 연결이 지금도 내 것인지 확인하고**, 아니면 id 를 떼고 연다 — 안 떼면
     * 오브젝트 상세가 403/404 로 조용히 빈다. 뗐다는 사실은 한 번 알려 준다.
     */
    const handleOpenArchivedPlan = useCallback((group) => {
        if (!group || !group.items || !group.items.length) return;
        const alive = (cid) => !!cid && connectionsFromStore.some(c => c.id === cid);
        const connId = group.items[0].connectionId;
        const keepConn = alive(connId);
        if (connId && !keepConn) toast.info(t('common:planArchive.connGone'));

        const base = { origin: 'archive', connectionId: keepConn ? connId : null };
        let tab;
        if (group.kind === 'pair' && group.items.length >= 2) {
            const [before, after] = group.items;
            tab = {
                ...base,
                id: `archive_cmp_${group.id}`,
                name: group.title,
                compare: { before, after },
                summary: group.summary || null,
                verdict: group.verdict || null,
                // 비교 탭도 탭 배열의 다른 코드가 기대하는 모양을 갖춰야 한다
                data: '',
                query: before.sql || '',
            };
        } else {
            const it = group.items[0];
            tab = {
                ...base,
                id: `archive_${it.id || group.id}`,
                name: group.title,
                data: it.planText || '',
                monitorReport: it.monitorReport || null,
                query: it.sql || '',
                metrics: it.metrics || null,
                mode: it.mode || null,
            };
        }
        setExecutionPlanTabs(prev => pushPlanTabs(prev, tab));
        setActiveExecutionPlanTabId(tab.id);
        setActiveMainTab('executionPlans');
    }, [connectionsFromStore, t]);

    const handleActiveMainTabChange = useCallback((newTabKey) => {
        setActiveMainTab(newTabKey);
    }, []);

    // StatusLine에 전달할 데이터를 계산하기 위한 로직
    const activeQueryResultTab = queryResultTabs.find(tab => tab.id === activeQueryResultTabId);
    // 결과 데이터 내보내기(CSV) 모달 — 대상은 현재 활성 결과 탭
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    // CSV 가져오기 모달 — 대상은 현재 활성 연결(결과 탭과 무관하게 열 수 있다)
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);

    // 데이터 분석의 「결과 탭의 쿼리」 대상 — 지금 결과 탭의 SQL·컬럼 메타·표본 행(자료형 추정용)
    const analysisResultSource = useMemo(() => {
        const r = activeQueryResultTab;
        if (!r) return null;
        const sql = String(r.sourceSql || r.query || '').trim();
        if (!sql) return null;
        const data = r.data;
        return { sql, name: r.name || '', columns: r.columns || r.metaData || [], sampleRow: Array.isArray(data) && data.length > 0 ? data[0] : null };
    }, [activeQueryResultTab]);

    // 분석 SQL 을 편집기의 현재 줄 아래에 넣는다 — 사용자가 고쳐서 실행한다(데이터 분석은 실행하지 않는다)
    const insertAnalysisSql = useCallback((sql) => {
        const view = sqlEditorRef.current;
        if (!view || !view.state || typeof view.dispatch !== 'function') {
            toast.warn(t('common:run.editorNotFound'));
            return;
        }
        if (!sql || !String(sql).trim()) { toast.warn(t('common:analysis.noSql')); return; }
        const pos = view.state.selection.main.head;
        // 현재 줄의 끝까지 이동해서 그 아래에 삽입 (커서 중간에 split 방지)
        const lineEnd = view.state.doc.lineAt(pos).to;
        const insertText = t('common:analysis.snippet', { sql });
        view.dispatch({
            changes: { from: lineEnd, to: lineEnd, insert: insertText },
            selection: { anchor: lineEnd + insertText.length },
            scrollIntoView: true,
        });
        view.focus();
        toast.info(t('common:analysis.inserted'));
    }, [t]);

    // 에이전트가 낸 SQL 을 현재 탭에 삽입 — handleWriteSql 과 같은 방식(현재 줄 끝에 붙인다).
    const handleInsertAssistantSql = useCallback((sql) => {
        const view = sqlEditorRef.current;
        if (!view || !view.state || typeof view.dispatch !== 'function') {
            toast.warn(t('common:run.editorNotFound'));
            return;
        }
        // 편집기에서 바로 실행할 수 있게 항상 세미콜론으로 끝낸다.
        // 이미 있으면 붙이지 않는다 — `;;` 는 구문 오류다.
        const withSemi = /;\s*$/.test(sql) ? sql.trimEnd() : `${sql.trimEnd()};`;
        const lineEnd = view.state.doc.lineAt(view.state.selection.main.head).to;
        const insertText = `

${t('assistant:insertSnippet', { sql: withSemi, at: new Date().toLocaleString() })}`;
        view.dispatch({
            changes: { from: lineEnd, to: lineEnd, insert: insertText },
            selection: { anchor: lineEnd + insertText.length },
            scrollIntoView: true,
        });
        view.focus();
    }, [t]);

    /**
     * **활성 탭의 실행 대상** — 탭이 바인딩한 세션과 그 세션의 연결.
     *
     * 헤더의 전역 활성 연결(탐색기가 보고 있는 것)이 **아니다.** 세션 선택은 다른 연결의
     * 세션도 고를 수 있으므로 둘이 다를 수 있고, 실제로 SQL 이 나가는 곳은 이쪽이다.
     * 실행 대상 표시(머리줄의 운영 빨간 줄 · 편집기 세션 선택기)와 에이전트가 같은 값을 봐야 "개발이라 적혀 있는데 운영에 실행" 이 안 생긴다.
     */
    const activeTabSession = activeFile?.sessionId
        ? (sessionsList || []).find(s => s.sessionId === activeFile.sessionId)
        : null;
    const activeTabConnection = activeTabSession
        ? connectionsFromStore.find(c => c.id === activeTabSession.connectionId)
        : null;

    // ── 에이전트의 상시 관찰(2026-10-20, utils/agentObserver) — 기계가 모은다, LLM 은 사람이 「분석 맡기기」 를 누를 때만 ──
    // 결과 탭의 변화(실패·느린 쿼리)는 탭마다 앞 상태와 견주어 한 번씩만 남긴다. 오래 도는 쿼리·오래된 미커밋은 10초마다 본다
    const obsTabsRef = useRef(new Map());
    useEffect(() => {
        const seen = obsTabsRef.current;
        for (const tab of queryResultTabs) {
            const prev = seen.get(tab.id) || {};
            const next = { ...prev, error: tab.error || null, executing: !!tab.isExecuting };
            if (tab.isExecuting && !prev.executing) { next.startedAt = Date.now(); next.longNoted = false; }
            if (tab.error && tab.error !== prev.error) {
                observe({ kind: 'sqlError', severity: 'error', title: String(tab.error).split(String.fromCharCode(10))[0], detail: String(tab.error), sql: tab.query || '',
                    source: tab.name || '', key: `sqlError:${tab.id}:${tab.error}` });
            }
            const ms = Number(tab.executionTime);
            if (prev.executing && !tab.isExecuting && !tab.error && Number.isFinite(ms) && ms >= OBS_SLOW_MS) {
                observe({ kind: 'slowQuery', severity: 'warn', title: t('assistant:obs.slowQuery', { sec: (ms / 1000).toFixed(1) }), sql: tab.query || '',
                    detail: t('assistant:obs.slowDetail', { rows: Array.isArray(tab.data) ? tab.data.length : 0 }), source: tab.name || '', key: `slow:${tab.id}:${ms}` });
            }
            seen.set(tab.id, next);
        }
        for (const id of [...seen.keys()]) if (!queryResultTabs.some(x => x.id === id)) seen.delete(id);
    }, [queryResultTabs, t]);
    const obsSessionsRef = useRef(new Map());
    const obsLatestRef = useRef({ tabs: queryResultTabs, sessions: sessionsList });
    obsLatestRef.current = { tabs: queryResultTabs, sessions: sessionsList };
    useEffect(() => {
        const id = setInterval(() => {
            const now = Date.now();
            const { tabs, sessions } = obsLatestRef.current;
            for (const tab of tabs || []) {
                const st = obsTabsRef.current.get(tab.id);
                if (st && st.executing && !st.longNoted && st.startedAt && now - st.startedAt >= OBS_LONG_RUNNING_MS) {
                    st.longNoted = true;
                    observe({ kind: 'longRunning', severity: 'warn', title: t('assistant:obs.longRunning', { sec: Math.round((now - st.startedAt) / 1000) }),
                        sql: tab.query || '', source: tab.name || '', key: `long:${tab.id}:${st.startedAt}` });
                }
            }
            const open = new Set();
            for (const sess of sessions || []) {
                if (!sess || !sess.uncommitted) continue;
                open.add(sess.sessionId);
                const rec = obsSessionsRef.current.get(sess.sessionId) || { since: now, noted: false };
                if (!rec.noted && now - rec.since >= OBS_UNCOMMITTED_MS) {
                    rec.noted = true;
                    observe({ kind: 'uncommitted', severity: 'warn', title: t('assistant:obs.uncommitted', { min: Math.round((now - rec.since) / 60000), name: sess.name || sess.sessionId }),
                        detail: t('assistant:obs.uncommittedDetail'), source: sess.name || '', key: `uncommitted:${sess.sessionId}:${rec.since}` });
                }
                obsSessionsRef.current.set(sess.sessionId, rec);
            }
            for (const sid of [...obsSessionsRef.current.keys()]) if (!open.has(sid)) obsSessionsRef.current.delete(sid);
        }, 10000);
        return () => clearInterval(id);
    }, [t]);

    /**
     * **탐색기는 헤더(탭 세션)의 연결을 따라간다**(신고 2026-10-07). 헤더는 「연결 A 에 연결됨」 인데 툴팁은
     * 「탐색기는 연결 B」 라고 말했다 — 탭에 다른 연결의 세션을 묶어도 탐색기 상태(activeConnectionId)는 마지막에
     * 누른 연결 그대로였기 때문이다. 그 상태는 요청마다 실리는 기본 연결이기도 해서 F4(오브젝트 정보)·DDL 이
     * B 로 갔다. 탭을 바꾸거나 세션을 묶을 때 탐색기를 그 연결로 옮긴다(탐색기에서 연결을 직접 누르면 그쪽을
     * 보는 것은 여전히 사용자 선택이다).
     */
    const activeTabConnId = activeTabConnection ? activeTabConnection.id : null;
    useEffect(() => {
        if (!activeTabConnId || activeTabConnId === activeConnectionIdRef.current) return;
        const conn = connectionsFromStore.find(c => c.id === activeTabConnId);
        if (!conn) return;
        setActiveConnectionName(conn.name);
        setActiveConnectionId(conn.id);
        activeConnectionIdRef.current = conn.id;
        setActiveConnectionIdForSchema(conn.id);
        // F4 는 이 연결의 스키마 트리에서 오브젝트를 찾는다 — 안 읽혀 있으면 「스키마 없음」 이 된다
        if (!conn.children && !conn.isLoadingSchemas) {
            dispatch(fetchSchemasForConnection(conn.id, conn.dbType));
        }
        // connectionsFromStore 는 일부러 뺀다 — 목록이 바뀔 때마다 탐색기를 되돌리면 탐색기에서 다른 연결을 볼 수가 없다
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTabConnId]);
    // 편집기의 컬럼 자동완성이 컬럼을 받아 올 연결 — **탭의 세션 연결**이 먼저, 없으면 탐색기의 전역 연결.
    // 화면 검증(2026-10-03)에서 탐색기는 내장 SQLite 를 보고 탭은 교육DB 세션을 쓰고 있었다 — 탐색기 연결로 컬럼을 물으면 빈다
    const editorConnection = activeTabConnection || connectionsFromStore.find(c => c.name === activeConnectionName) || null;
    editorConnRef.current = editorConnection;
    // 사용자 주석(2026-10-29, docs/features/32-user-comment.md) — 로그인한 사람의 정의를 받고, 결과 도구 줄의 「사용자 주석」 을 켜면
    // 매핑된 연결 중 쓰고 있는 것(편집기·결과 탭의 연결이거나 탐색기에서 펼친 연결)의 영역 조회 쿼리를 돌려 영역명만 리덕스에 둔다.
    // 값의 주석은 결과 표에서 컬럼의 아이콘을 눌러 펼칠 때 받는다(F2 는 체크와 무관하게 서버에 바로 묻는다)
    const ucmtUser = IS_STANDALONE ? 'standalone' : (isAuthenticated ? (user?.id || user?.email || 'me') : null);
    useEffect(() => { if (ucmtUser) dispatch(loadUserCommentDefs()); }, [ucmtUser, dispatch]);
    const ucmtDefs = useSelector(state => state.userComments.defs);
    const ucmtAreasByConn = useSelector(state => state.userComments.areasByConn);
    const ucmtInUse = useMemo(() => {
        const ids = new Set();
        if (editorConnection) ids.add(editorConnection.id);
        queryResultTabs.forEach(tb => { if (tb.connectionId) ids.add(tb.connectionId); });
        connectionsFromStore.forEach(c => { if (c.isConnected !== false && Array.isArray(c.children)) ids.add(c.id); });
        return [...ids].sort().join('\n');
    }, [editorConnection, queryResultTabs, connectionsFromStore]);
    const ucmtShowOn = !!editorOptions.userComments;
    useEffect(() => {
        if (!ucmtShowOn) return;
        const mapped = new Set();
        (ucmtDefs || []).forEach(d => { if (d.spec && d.spec.enabled !== false) (d.spec.connectionIds || []).forEach(id => mapped.add(id)); });
        ucmtInUse.split('\n').filter(Boolean).forEach(id => {
            if (mapped.has(id) && !ucmtAreasByConn[id]) dispatch(loadUserCommentAreas(id));
        });
    }, [ucmtShowOn, ucmtDefs, ucmtInUse, ucmtAreasByConn, dispatch]);
    // 「SQL 편집의 연결」 — **탭의 세션 연결만**(2026-10-28 사용자 요청). 처음 로딩하면 탭에 세션이 없으므로 연결도 없다.
    // 위의 editorConnection 은 자동 완성용이라 탐색기의 연결(로딩 때 첫 연결로 자동 선택)로 메우는데, 그것을 SQL 작성·SQL 빌더·데이터 분석의
    // 연결로 쓰면 편집기는 「세션 미선택」 인데 그 화면들은 「교육DB」 라고 말했다
    const sqlTabConnProps = {
        connectionId: activeTabConnection ? activeTabConnection.id : null,
        dbType: activeTabConnection ? (activeTabConnection.dbType || 'oracle') : 'oracle',
    };
    /** SQL 빌더 — 편집기의 선택 영역(없으면 커서가 있는 문장, 실행과 같은 statementAtCursor)과 그 자리 */
    const readEditorForBuilder = useCallback(() => {
        const view = sqlEditorRef.current;
        if (!view || !view.state) return null;
        const doc = view.state.doc.toString();
        const sel = view.state.selection.main;
        let from;
        let to;
        if (sel.from !== sel.to && doc.slice(sel.from, sel.to).trim()) { from = sel.from; to = sel.to; }
        else {
            const st = statementAtCursor(doc, sel.head);
            if (!st) return null;
            from = st.start; to = st.end;
        }
        while (from < to && /\s/.test(doc[from])) from++;
        while (to > from && /[\s;]/.test(doc[to - 1])) to--;
        if (to <= from) return null;
        const sql = doc.slice(from, to);
        sqlBuilderOrigin.current = { fileId: activeFileId, from, to, sql };
        return { sql };
    }, [sqlEditorRef, activeFileId]);
    const openSqlBuilder = useCallback(() => {
        const got = readEditorForBuilder();
        setSqlBuilderSeed({ sql: got ? got.sql : '', nonce: Date.now() });
        setSqlBuilderOpen(true);
    }, [readEditorForBuilder]);
    /** 「편집기에 반영」 — 가져온 자리가 그대로면 그 자리를 바꾸고, 그 사이 바뀌었으면 커서 위치에 넣는다(엉뚱한 곳을 덮지 않게) */
    const applySqlBuilder = useCallback((sql) => {
        const view = sqlEditorRef.current;
        const o = sqlBuilderOrigin.current;
        if (view && view.state && o && o.fileId === activeFileId && view.state.doc.sliceString(o.from, o.to) === o.sql) {
            view.dispatch({ changes: { from: o.from, to: o.to, insert: sql } });
            sqlBuilderOrigin.current = { ...o, to: o.from + sql.length, sql };
            toast.success(t('common:sqb.applied'));
            return;
        }
        handleInsertAssistantSql(sql);
        toast.info(t('common:sqb.appliedAtCursor'));
    }, [sqlEditorRef, activeFileId, handleInsertAssistantSql, t]);
    const openSqlBuilderInNewTab = useCallback((sql) => {
        const newFile = createNewFile(activeProjectId);
        newFile.content = sql;
        setOpenFiles(prevFiles => [newFile, ...prevFiles]);
        setActiveFileId(newFile.id);
    }, [createNewFile, activeProjectId]);

    // SQL 작성의 「실행 (아래 결과 영역)」 — 그 탭 아래의 공용 결과 영역으로 실행한다(2026-10-19). 실행은 편집기와 같은 길(handleExecute —
    // 위험 문장 확인 · 바인드 · 결과 탭)이고 **활성 편집기 탭의 세션**으로 간다. 그래서 SQL 작성이 고른 연결과 그 탭의 연결이 다르면
    // 엉뚱한 DB 에서 돌지 않게 실행하지 않고 새 편집기 탭에 넣는다(연결을 거기서 고른다)
    const runSqlInResult = useCallback((sql, connectionId) => {
        if (!sql || !String(sql).trim()) return;
        if (connectionId && (!editorConnection || editorConnection.id !== connectionId)) {
            toast.info(t('common:sau.runInResultOtherConn'));
            openSqlBuilderInNewTab(sql);
            return;
        }
        // SQL 작성은 결과 영역 자리까지 차지한다(2026-10-28 사용자 결정) — 결과는 SQL 편집 탭의 결과 영역에 나오므로 그 탭을 앞으로
        if (wsActivateSoft) wsActivateSoft(SQL_TAB);
        handleExecute(sql);
    }, [editorConnection, handleExecute, openSqlBuilderInNewTab, t, wsActivateSoft]);

    const editorConnProps = {
        connectionId: editorConnection ? editorConnection.id : null,
        dbType: editorConnection ? (editorConnection.dbType || 'oracle') : 'oracle',
        // 탐색기 트리에 없는 테이블(탭 연결이 탐색기와 다를 때)은 이 스키마로 묻는다
        defaultSchema: editorConnection ? (editorConnection.defaultSchema || editorConnection.user || '') : '',
        usrid: user?.email || user?.id || 'standalone',
    };
    // 커밋·롤백 — **이 탭의 세션** 커넥션에 보낸다. 풀 커넥션에 보내던 세션 설정 창의 단추는 없앴다(2026-10-03)
    const [txnBusy, setTxnBusy] = useState(false);
    const endTxn = useCallback(async (verb) => {
        const sid = activeTabSession?.sessionId;
        if (!sid) { toast.warn(t('common:sess.noSessionHint')); return; }
        setTxnBusy(true);
        try {
            await axiosInstance.post(`/sessions/${encodeURIComponent(sid)}/${verb}`);
            toast.success(t(verb === 'commit' ? 'common:sess.committed' : 'common:sess.rolledBack'));
            dispatch(fetchSessions());
        } catch (err) {
            toast.error(formatApiError(err, t));
        } finally {
            setTxnBusy(false);
        }
    }, [activeTabSession, dispatch, t]);
    const handleCommit = useCallback(() => endTxn('commit'), [endTxn]);
    const handleRollback = useCallback(() => endTxn('rollback'), [endTxn]);
    // Quick Start 가 쓰는 연결 — 탭에 세션이 없으면(그게 Quick Start 의 출발점이다) 헤더의 전역 연결로.
    const quickConnection = activeTabConnection
        || (activeConnectionId ? connectionsFromStore.find(c => c.id === activeConnectionId) : null)
        || null;
    // 편집기 가운데 워터마크 — **늘 표시한다**(사용자 요청 2026-09-13).
    // 세션이 있으면 이 탭의 SQL 이 실제로 나가는 연결명. 세션이 없으면 전역 연결명에 「세션 없음」을 붙인다 —
    // 이름만 띄우면 거기로 실행된다고 믿게 된다. 처음엔 세션이 없을 때 숨겼는데 "아무것도 안 보인다" 는 지적을 받았다.
    const editorWatermark = activeTabConnection
        ? activeTabConnection.name
        : (quickConnection ? t('common:ui.watermarkNoSession', { name: quickConnection.name }) : '');
    /**
     * Quick Start 의 「대신 해줘」 — 도움말이 시키는 세 동작. 탐색기의 "+"·파일 › 새 파일과 **같은 핸들러**를 쓴다.
     *   open_connections  연결 관리 창을 연다
     *   open_session      현재 연결에 세션을 열고 **활성 탭에 묶는다** (탐색기에서 + 누르고 클릭하는 것과 같다)
     *   new_tab           새 탭을 열되 활성 탭의 세션을 **이어받는다** — 안 그러면 다음 단계(실행)에서 세션 없음
     * @returns {Promise<boolean>} 됐는지
     */
    const handleQuickAction = useCallback(async (a) => {
        if (a.type === 'open_connections') { setIsConnectionModalOpen(true); return true; }
        if (a.type === 'open_session') {
            const connId = quickConnection?.id;
            if (!connId) return false;
            const s = await handleCreateSession(connId);
            if (!s || !s.sessionId) return false;
            // 새 세션은 다음 폴링에야 목록에 오른다. 지금 묶어야 하므로 목록을 먼저 당겨 온다.
            try { await dispatch(fetchSessions()); } catch (_e) { /* 폴링이 곧 맞춘다 */ }
            // 방금 만든 세션이라 목록 검사(assertBindable)를 건너뛴다 — 묶는 범위는 다른 경로와 같다(프로젝트 단위)
            bindProjectSessionUnchecked(activeFileId, s.sessionId);
            return true;
        }
        if (a.type === 'new_tab') {
            const newFile = createNewFile(activeProjectId); // 프로젝트의 세션을 물려받는다
            setOpenFiles(prev => [newFile, ...prev]);
            setActiveFileId(newFile.id);
            return true;
        }
        return false;
    }, [quickConnection, handleCreateSession, dispatch, activeFileId, createNewFile, activeProjectId, bindProjectSessionUnchecked]);

    // 지금 앱 화면 요약을 만드는 함수 — 렌더마다 최신으로 바꿔 끼운다(아래 단추 줄 근처). 턴을 보낼 때 한 번 부른다
    const workspaceSnapRef = useRef(null);
    const assistantContext = useMemo(() => ({
        // 지금 앱 화면(2026-10-20) — 앞에 보이는 화면·열린 작업 화면·편집기 SQL·활성 결과 요약. 행 값은 싣지 않는다(서버 workspaceBlock 이 다시 자른다)
        getWorkspace: () => (workspaceSnapRef.current ? workspaceSnapRef.current() : null),
        sessionId: activeFile?.sessionId || null,
        connectionId: activeTabConnection?.id || null,
        connectionName: activeTabConnection?.name || null,
        // Quick Start 1단계용 — 세션 없이 연결만 골라 둔 상태도 "연결돼 있다" 로 본다
        quickConnectionName: quickConnection?.name || null,
        dbType: activeTabConnection?.dbType || null,
        // 기본 스키마 — 편집기의 이름 풀이와 같은 값(editorConnProps). 예전에는 이 칸이 없어 에이전트에게 늘 빈 값이 갔다(점검 2026-10-20)
        schema: activeTabConnection ? (activeTabConnection.defaultSchema || activeTabConnection.user || null) : null,
        getSelection: () => {
            const view = sqlEditorRef.current;
            if (!view || !view.state) return '';
            const { from, to } = view.state.selection.main;
            return from === to ? '' : view.state.sliceDoc(from, to);
        },
    }), [activeFile?.sessionId, activeTabConnection, quickConnection]);

    let statusLineProps = {
        executionTime: null, // 총 실행 시간 (totalExecutionTime)
        currentFetchTime: null, // 현재 fetch 시간 추가
        clientExecutionTime: null, // 클라이언트 시간
        message: null,
        rowCount: 0,
        isLoading: false,
        hasMoreData: false,
    };

    if (activeQueryResultTab) {
        statusLineProps.executionTime = activeQueryResultTab.executionTime; // 총 실행 시간
        statusLineProps.currentFetchTime = activeQueryResultTab.currentFetchTime; // 현재 fetch 시간
        statusLineProps.clientExecutionTime = activeQueryResultTab.clientExecutionTime; // 클라이언트 시간
        statusLineProps.message = activeQueryResultTab.message;

        if (Array.isArray(activeQueryResultTab.data) &&
            activeQueryResultTab.data.length >= 0 &&
            activeQueryResultTab.data.every(item => typeof item === 'object' && item !== null && !item.hasOwnProperty('message'))) {
            statusLineProps.rowCount = activeQueryResultTab.data.length;
        }
        statusLineProps.isLoading = activeQueryResultTab.isLoadingMore && activeQueryResultTab.data && activeQueryResultTab.data.length > 0;
        statusLineProps.hasMoreData = activeQueryResultTab.hasMore;
    }

    // 활성 에디터에 연결된 결과탭들 — SQLEditor 내부에서 statement.sourceSql 매칭으로
    // 각 쿼리 줄 옆에 점프 아이콘을 표시. project 필터 영향 안 받게 activeFileId 직접 사용.
    // useMemo 로 reference 안정화 — 매 render 마다 새 array 가 되면 SQLEditor 의 useEffect 가
    // 폴링 tick 마다 발화해서 view.dispatch 반복 → CodeMirror measure loop 경고 발생.
    const linkedResultTabsForActiveEditor = useMemo(
        () => activeFileId
            ? queryResultTabs.filter(t => t.editorTabId === activeFileId)
            : [],
        [activeFileId, queryResultTabs]
    );
    const handleActivateResultTab = useCallback((tabId) => {
        if (!tabId) return;
        setActiveQueryResultTabId(tabId);
        setActiveMainTab('queryResults');
    }, []);


    const handleCloseQueryResultTab = useCallback((tabIdToClose) => {
        setQueryResultTabs(prevTabs => {
            const tabToClose = prevTabs.find(t => t.id === tabIdToClose);
            
            // 고정된 탭은 닫을 수 없음
            if (tabToClose && tabToClose.isPinned) {
                return prevTabs;
            }
            
            const tabToCloseIndex = prevTabs.findIndex(t => t.id === tabIdToClose);
            if (tabToCloseIndex === -1) return prevTabs; // 닫을 탭이 없으면 변경 없음

            const newTabs = prevTabs.filter(tab => tab.id !== tabIdToClose);

            if (newTabs.length === 0) {
                setActiveQueryResultTabId(null); // 모든 탭이 닫히면 활성 탭 없음
                // 필요하다면 여기서 기본 빈 탭을 다시 만들 수도 있음
                // setNextQueryResultTabNum(1); // 새 탭 번호 초기화
            } else {
                // 닫힌 탭이 현재 활성 탭이었는지 확인
                if (activeQueryResultTabId === tabIdToClose) {
                    // 새로운 활성 탭 결정: 닫힌 탭의 이전 탭 또는 다음 탭
                    let newActiveId = null;
                    if (tabToCloseIndex > 0) { // 이전 탭이 있다면
                        newActiveId = prevTabs[tabToCloseIndex - 1].id;
                    } else if (newTabs.length > 0) { // 이전 탭이 없고, 남은 탭 중 첫 번째 탭
                        newActiveId = newTabs[0].id;
                    }
                    setActiveQueryResultTabId(newActiveId);
                }
                // 닫힌 탭이 활성 탭이 아니었다면 activeQueryResultTabId는 변경하지 않음
            }
            return newTabs;
        });
    }, [activeQueryResultTabId]);

    const handlePinQueryResultTab = useCallback((tabIdToPin) => {
        setQueryResultTabs(prevTabs => {
            const tabToPin = prevTabs.find(t => t.id === tabIdToPin);
            if (!tabToPin) return prevTabs;

            // 이미 고정된 탭의 개수 확인
            const pinnedTabs = prevTabs.filter(t => t.isPinned);
            
            if (tabToPin.isPinned) {
                // 고정 해제: isPinned를 false로 변경
                return prevTabs.map(t => 
                    t.id === tabIdToPin 
                        ? { ...t, isPinned: false }
                        : t
                );
            } else {
                // 고정된 탭이 이미 3개라면 가장 오래된 고정 탭을 해제
                if (pinnedTabs.length >= 3) {
                    // 첫 번째 고정 탭을 고정 해제
                    const oldestPinnedTab = pinnedTabs[0];
                    return prevTabs.map(t => {
                        if (t.id === oldestPinnedTab.id) {
                            return { ...t, isPinned: false };
                        } else if (t.id === tabIdToPin) {
                            return { ...t, isPinned: true };
                        } else {
                            return t;
                        }
                    });
                } else {
                    // 고정: isPinned를 true로 변경
                    return prevTabs.map(t => 
                        t.id === tabIdToPin 
                            ? { ...t, isPinned: true }
                            : t
                    );
                }
            }
        });
    }, []);

    const handleCloseExecutionPlanTab = useCallback((tabIdToClose) => {
        setExecutionPlanTabs(prevTabs => {
            const tabToCloseIndex = prevTabs.findIndex(t => t.id === tabIdToClose);
            if (tabToCloseIndex === -1) return prevTabs;

            const newTabs = prevTabs.filter(tab => tab.id !== tabIdToClose);

            if (newTabs.length === 0) {
                setActiveExecutionPlanTabId(null);
                // setNextExecutionPlanTabNum(1); // 순번 초기화하지 않음
            } else {
                if (activeExecutionPlanTabId === tabIdToClose) {
                    let newActiveId = null;
                    if (tabToCloseIndex > 0) {
                        newActiveId = prevTabs[tabToCloseIndex - 1].id;
                    } else if (newTabs.length > 0) {
                        newActiveId = newTabs[0].id;
                    }
                    setActiveExecutionPlanTabId(newActiveId);
                }
            }
            return newTabs;
        });
    }, [activeExecutionPlanTabId]);

    // 챌린지 관련 핸들러 함수들
    // 같은 챌린지 창을 두 메뉴가 연다 — 'query' 는 전부, 'tuning' 은 튜닝 문항(003·004)이 든 챌린지만
    const [challengeMode, setChallengeMode] = useState(() => savedLayout.challengeMode);
    // 쿼리챌린지 · 튜닝챌린지는 **각자의 작업 탭**이다(2026-11-04 사용자 요청 — 예전에는 SQL 편집 탭 안에서 편집기 자리를 대신했고
    // 한쪽을 열면 다른 쪽이 바뀌었다). 둘 다 열어 둘 수 있고 탭의 ✕ 로 닫는다(components/quiz/ChallengeWindow).
    // 이미 열려 있으면 open 이 바뀌지 않으니 그 탭을 앞으로 부른다(창 제목 = 메뉴 문구)
    const [challengeOpen, setChallengeOpen] = useState(() => ({
        query: !!(savedLayout.windows.challengeQuery && savedLayout.windows.challengeQuery.open),
        tuning: !!(savedLayout.windows.challengeTuning && savedLayout.windows.challengeTuning.open),
    }));
    const openChallengeTab = useCallback((kind) => {
        setChallengeMode(kind);
        setChallengeOpen(prev => (prev[kind] ? prev : { ...prev, [kind]: true }));
        if (workspace) setTimeout(() => workspace.activateByTitle(t(kind === 'tuning' ? 'menu:challenge.tuning' : 'menu:challenge.query')), 0);
    }, [workspace, t]);
    const closeChallengeTab = useCallback((kind) => setChallengeOpen(prev => ({ ...prev, [kind]: false })), []);
    const handleChallengeJoin = useCallback(() => {
        // 임시패스워드 상태에서는 접근 차단
        if (isTemporaryPassword) {
            toast.warning(t('common:error.tempPassword'));
            return;
        }
        openChallengeTab('query');
    }, [isTemporaryPassword, openChallengeTab, t]);

    /**
     * 에이전트가 채운 챌린지 초안(SSE challenge_draft, 2026-10-06) — 그 종류의 챌린지 창을 열고 「새 챌린지」 편집기를
     * 초안으로 채워 연다. 편집기는 챌린지 창 안에 있다(가져오기 경로·출처 전환이 거기 있다). 창이 받아 열면
     * 곧바로 비운다 — 남겨 두면 창을 다시 열 때마다 같은 초안이 또 뜬다.
     */
    const [challengeDraft, setChallengeDraft] = useState(null);
    const handleOpenChallengeDraft = useCallback((draft) => {
        if (!draft) return;
        setChallengeDraft({ ...draft, nonce: Date.now() });
        openChallengeTab(draft.kind === 'tuning' ? 'tuning' : 'query');
    }, [openChallengeTab]);
    const handleChallengeDraftConsumed = useCallback(() => setChallengeDraft(null), []);

    // 튜닝챌린지 — 같은 챌린지 창을 튜닝 문항(003·004)이 든 챌린지만 보이게 연다
    const handleChallengeTuning = useCallback(() => {
        if (isTemporaryPassword) {
            toast.warning(t('common:error.tempPassword'));
            return;
        }
        openChallengeTab('tuning');
    }, [isTemporaryPassword, openChallengeTab, t]);

    const handleChallengeStatus = useCallback(() => {
        // 임시패스워드 상태에서는 접근 차단
        if (isTemporaryPassword) {
            toast.warning(t('common:error.tempPassword'));
            return;
        }
        
        openChallengeTab('query');
    }, [isTemporaryPassword, openChallengeTab, t]);

    // 사이드패널 상태
    const [externalActiveTab, setExternalActiveTab] = useState(() => savedLayout.sidePanelView);

    // 작업 화면 배치를 저장한다 — 바뀔 때마다. 무엇을 넣는지는 utils/workspaceLayout 의 목록과 같아야 한다
    useEffect(() => {
        saveLayout({
            activeFileId,
            sidePanelOpen: isSidePanelOpen,
            sidePanelView: isSidePanelOpen ? (externalActiveTab || 'history') : (externalActiveTab || null),
            challengeMode,
            // AI 에이전트 펼침 — 웹 기본 펼침이 생긴 판부터의 이름(utils/workspaceLayout). AI 없는 판은 쓰지 않는다(늘 닫혀 있어 「닫음」 으로 저장된다)
            ...(AI_ENABLED ? { assistantPanel: assistantOpen ? 'open' : 'closed' } : {}),
            analysisMode,
            editorSplit: isEditorSplit,
            resultSplit: isResultSplit,
            collapseMode: splitCollapseMode,
            splitSize: Number.isFinite(splitSize) ? splitSize : null,
            explorerCollapsed,
            // 작업 창 — 열림은 App 이, 보던 위치(id·단계)는 창이 알려 준 것
            windows: {
                cases: { ...winView.cases, open: caseListOpen },
                sqlTuning: { ...winView.sqlTuning, open: sqlTuningOpen },
                sqlReview: { ...winView.sqlReview, open: sqlReviewOpen },
                sqlAuthor: { ...winView.sqlAuthor, open: sqlAuthorOpen },
                sqlQuality: { ...winView.sqlQuality, open: sqlQualityOpen },
                schemaCompare: { ...winView.schemaCompare, open: schemaCompareOpen },
                sqlCollect: { ...winView.sqlCollect, open: sqlCollectOpen },
                guides: { ...winView.guides, open: designGuidesOpen },
                tableAnalysis: { open: !!tableAnalysisMode, id: null, step: tableAnalysisMode || null },
                sqlBuilder: { open: sqlBuilderOpen, id: null, step: null },
                challengeQuery: { open: !!challengeOpen.query, id: null, step: null },
                challengeTuning: { open: !!challengeOpen.tuning, id: null, step: null },
            },
        });
    }, [activeFileId, isSidePanelOpen, externalActiveTab, challengeMode, assistantOpen, analysisMode, isEditorSplit, isResultSplit, splitCollapseMode, splitSize, explorerCollapsed,
        winView, caseListOpen, sqlTuningOpen, sqlReviewOpen, sqlAuthorOpen, sqlQualityOpen, schemaCompareOpen, sqlCollectOpen, designGuidesOpen, tableAnalysisMode, sqlBuilderOpen, challengeOpen]);
    const [externalActiveChallengeSubTab] = useState(null);

    /**
     * 툴바 버튼 — 지금 보고 있는 것을 다시 누르면 닫는다(토글).
     *
     * **`externalActiveTab` 선언보다 뒤에 있어야 한다.** 의존성 배열은 훅을 만들 때 바로
     * 평가되므로, 위쪽에 두면 아직 선언되지 않은 값을 읽어 TDZ 로 화면이 통째로 죽는다.
     */
    const toggleSidePanelView = useCallback((view) => {
        // 툴바의 「쿼리 챌린지」 는 쿼리챌린지다 — 튜닝챌린지가 열려 있을 때 누르면 닫지 말고 쿼리챌린지로 바꾼다
        // (같은 패널이라 예전엔 「이미 열림」 으로 보고 닫았다, 2026-10-05)
        if (view === 'challenge' && challengeMode !== 'query') {
            setChallengeMode('query');
            openSidePanel(view);
            return;
        }
        if (isSidePanelOpen && externalActiveTab === view) {
            // 걷어 둔 화면이면 닫지 말고 다시 보인다
            if (coverHidden) { setCoverHidden(false); return; }
            setIsSidePanelOpen(false);
            return;
        }
        openSidePanel(view);
    }, [isSidePanelOpen, externalActiveTab, openSidePanel, challengeMode, coverHidden]);
    // 툴바가 눌린 모양으로 보일 패널 — 튜닝챌린지는 툴바에 버튼이 없어 「쿼리 챌린지」 가 눌려 보이면 안 된다
    const sidePanelViewShown = isSidePanelOpen
        ? ((externalActiveTab === 'challenge' && challengeMode === 'tuning') ? 'challenge-tuning' : (externalActiveTab || 'history'))
        : null;

    if (!connectionsFromStore || connectionsFromStore.length === 0) {
        // 스토어에서 connections를 아직 못 가져왔거나 비어있을 경우 (초기 로딩 등)
        // App.js 최하단의 로딩 조건 전에 이 조건이 먼저 걸릴 수 있음.
        // schemaReducer의 initialState에 connections가 있으므로, 이 경우는 거의 발생하지 않을 수 있음.
        // return <div className="app-container">Loading connections...</div>;
    }

    if (!activeFile && openFiles.length === 0) {
        return <div className="app-container">Loading or initializing...</div>;
    }
    
    // 활성 탭이 이 프로젝트에 없을 때(프로젝트를 바꾼 직후 한 장면) — **이 프로젝트의 첫 탭**으로. 모든 탭의 첫 탭(openFiles[0])을 띄우면
    // 다른 프로젝트·개별 스크립트의 탭이 잠깐 편집기에 올라와 섞였다(2026-10-26 신고). 이 프로젝트에 탭이 없으면 빈 탭이 곧 생긴다
    let currentActiveFile = activeFile || visibleFiles[0];
    if (!currentActiveFile && openFiles.length > 0) {
        currentActiveFile = openFiles[0];
    }
    if (!currentActiveFile && openFiles.length === 0) { 
        return <div className="app-container">No active file. Please open or create a file.</div>;
    }
    if (!currentActiveFile) { 
        return <div className="app-container">Initializing editor...</div>;
    }

    // Show login page if not authenticated (스탠드얼론 모드에서는 바이패스)
    // 로그인이 **만료**된 것이면 작업 화면을 내리지 않는다 — 아래에서 그 위에 로그인 창(SessionExpiredGate)을 덮는다
    if (!isAuthenticated && !IS_STANDALONE && !sessionExpired) {
        return (
            <div className="app-container">
                <ToastContainer position="top-center" autoClose={3000} hideProgressBar newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme={darkMode ? 'dark' : 'light'} />
                <QbaseMaintenanceBanner />
                <LoginPage />
            </div>
        );
    }

    // ============================================================
    // 에디터 액션 중앙 Registry — 메뉴와 툴바 액션 그룹이 공유
    // key: 안정적 식별자 (localStorage·Header 메뉴·Action 그룹 공통)
    // ============================================================
    // 찾기는 툴바 검색 창 하나로 한다 — 편집기 Ctrl+F · 메뉴 편집 › 찾기 · 툴바 버튼이 모두 여기로 온다.
    // autoFocus 는 처음 그려질 때만 동작하므로 직접 포커스한다. 선택한 글자가 있으면 검색어로 쓴다.
    // 바꾸기 — CodeMirror 검색 패널(정규식·단어 단위 포함). 편집기 Ctrl+H 와 같은 함수다
    const handleEditorReplace = () => openReplacePanel(sqlEditorRef.current);
    const handleEditorFind = (selectedText) => {
        setEditorSearch(p => ({ ...p, open: true }));
        if (typeof selectedText === 'string' && selectedText) handleEditorSearch(selectedText);
        requestAnimationFrame(() => {
            const el = editorSearchInputRef.current;
            if (el) { el.focus(); el.select(); }
        });
    };
    const ACTION_REGISTRY = {
        // 편집 메뉴
        undo:            { label: t('common:ui.undo'),   shortcut: 'Ctrl+Z',        icon: FiRotateCcw, onClick: handleEditorUndo,          iconText: null,   toggle: false },
        redo:            { label: t('common:ui.redo'),   shortcut: 'Ctrl+Y',        icon: FiRotateCw,  onClick: handleEditorRedo,          iconText: null,   toggle: false },
        copy:            { label: t('common:ui.copy'),        shortcut: 'Ctrl+C',        icon: FiCopy,      onClick: handleEditorCopy,          iconText: null,   toggle: false },
        cut:             { label: t('common:ui.cut'),    shortcut: 'Ctrl+X',        icon: FiScissors,  onClick: handleEditorCut,           iconText: null,   toggle: false },
        paste:           { label: t('common:ui.paste'),    shortcut: 'Ctrl+V',        icon: FiClipboard, onClick: handleEditorPaste,         iconText: null,   toggle: false },
        find:            { label: t('common:ui.find'),        shortcut: 'Ctrl+F',        icon: FiSearch,    onClick: handleEditorFind,          iconText: null,   toggle: false },
        // 실행 메뉴
        executecurrent:  { label: t('common:ui.runCurrent'), shortcut: 'Ctrl+Enter', icon: FiPlay,         onClick: handleExecuteCurrent,         iconText: null, toggle: false },
        executeall:      { label: t('common:ui.runAll'),   shortcut: 'F5',            icon: FiPlayCircle,   onClick: handleExecuteAll,             iconText: null, toggle: false },
        viewhistory:     { label: t('common:ui.showHistory'), shortcut: null,          icon: FiClock,        onClick: handleViewExecutionHistory,   iconText: null, toggle: false },
        runplan:         { label: t('common:ui.showPlan'), shortcut: 'Ctrl+E',      icon: FiBarChart2,    onClick: executeSQLEditorPlan,         iconText: null, toggle: false },
        viewschema:      { label: t('common:ui.viewSchema'), shortcut: 'F4',       icon: FiDatabase,     onClick: () => handleViewSchemaInfoRequest(), iconText: null, toggle: false },
        commit:          { label: t('common:sess.commit'),   shortcut: null,       icon: FiCheckCircle,  onClick: handleCommit,                 iconText: null, toggle: false },
        rollback:        { label: t('common:sess.rollback'), shortcut: null,       icon: FiXCircle,      onClick: handleRollback,               iconText: null, toggle: false },
        // 에디터 내부 액션 (컨텍스트 메뉴 기반)
        format:          { label: t('common:ui.format'),        shortcut: null,   icon: FiAlignLeft, onClick: handleEditorFormat,        iconText: null,   toggle: false },
        uppercase:       { label: t('common:ui.toUpper'), shortcut: null,            icon: null,        onClick: handleEditorUppercase,     iconText: 'AA',   toggle: false },
        lowercase:       { label: t('common:ui.toLower'), shortcut: null,            icon: null,        onClick: handleEditorLowercase,     iconText: 'aa',   toggle: false },
        toggleComment:   { label: t('common:ui.toggleComment'),   shortcut: 'Ctrl+/',        icon: FiHash,      onClick: handleEditorToggleComment, iconText: null,   toggle: false },
        // 켜고 끄는 옵션(컬럼 선택 등)은 여기 두지 않는다 — 툴바 체크박스 + 메뉴 보기 › 가 맡는다
        // (docs/ui-command-placement.md §2). 예전 저장본에 남은 'columnSelection' id 는 지우지 않고
        // renderActionGroup 이 그릴 수 없는 id 로 건너뛴다 — 체크박스 옆에 같은 버튼이 두 번 보이던 원인.
    };
    {
        const keys = Object.keys(ACTION_REGISTRY);
        if (keys.join('|') !== actionKeysRef.current.join('|')) actionKeysRef.current = keys;
    }

    // 액션 그룹 렌더링 — pinnedActions 를 separator 로 서브그룹 분할
    // Fragment 만 반환. 상위 컨테이너(.editor-action-group) 안에서 호출해야 함
    // windows: true — 즐겨찾기한 "창을 여는 메뉴"(Header 가 window 로 표시)만 **아이콘+레이블**로 돌려준다.
    // 오른편 도구 창 묶음에 놓인다(사용자 요청 2026-09-13). 기본은 그 밖의 편집·실행 액션(왼쪽, 아이콘만).
    const renderActionGroup = ({ windows = false } = {}) => {
        const groups = [];
        let current = [];
        // 이 빌드·이 모드에서 그릴 수 없는 id(스탠드얼론 전용 메뉴, 새 빌드가 붙인 것 등)는 건너뛴다 —
        // 저장본에서는 지우지 않는다(되돌아가면 다시 보여야 한다). 빈 묶음·겹친 구분선도 이걸로 막힌다.
        const isWindowKey = (key) => !ACTION_REGISTRY[key] && !!menuActions[key]?.window;
        if (windows) {
            return pinnedActions.filter(key => key !== 'separator' && isWindowKey(key)).map(key => {
                const m = menuActions[key];
                return (
                    <button key={key} className="detach-inline-btn" onClick={() => runMenuAction(key)} title={m.name}>
                        {React.isValidElement(m.icon) ? m.icon : null} {m.name}
                    </button>
                );
            });
        }
        // 고정 배치(단추 줄의 패널 단추 등)는 예전에 붙여 둔 것이 남아 있어도 여기 다시 그리지 않는다
        pinnedActions.filter(key => key === 'separator' || ((ACTION_REGISTRY[key] || menuActions[key]) && !isWindowKey(key) && !isFixedAction(key))).forEach(key => {
            if (key === 'separator') {
                if (current.length) { groups.push(current); current = []; }
            } else {
                current.push(key);
            }
        });
        if (current.length) groups.push(current);

        const renderButton = (key) => {
            const a = ACTION_REGISTRY[key];
            if (!a) {
                // 메뉴에만 있는 기능 — Header 가 게시한 표시명·아이콘으로 그리고, 실행은 메뉴 클릭과 같은 경로로 간다
                const m = menuActions[key];
                if (!m) return null;
                return (
                    <button
                        key={key}
                        className="editor-action-btn"
                        onClick={() => runMenuAction(key)}
                        title={m.name}
                    >
                        {React.isValidElement(m.icon) ? React.cloneElement(m.icon, { size: 14 }) : null}
                    </button>
                );
            }
            const active = a.toggle && a.isActive && a.isActive();
            const shortcutLabel = a.shortcut ? ` (${a.shortcut})` : '';
            const Icon = a.icon;
            return (
                <button
                    key={key}
                    className={`editor-action-btn${active ? ' active' : ''}`}
                    onClick={a.onClick}
                    title={`${a.label}${shortcutLabel}`}
                >
                    {a.iconText ? <span className="editor-action-text">{a.iconText}</span> : Icon ? <Icon size={14} /> : null}
                </button>
            );
        };

        return groups.map((group, gIdx) => (
            <React.Fragment key={`ag-${gIdx}`}>
                {gIdx > 0 && <span className="editor-action-sep-v" />}
                <div className="editor-action-subgroup">
                    {group.map(renderButton)}
                </div>
            </React.Fragment>
        ));
    };

    // ── SQL 편집 화면의 조각(2026-10-19) — 편집기·결과를 붙여 두든(VerticalSplit) · 한쪽만 분리하든(flex) · 새 창으로 분리하든
    //    (DetachablePanel) **같은 조각**을 그린다. 세 벌로 베껴 두었더니 한 곳만 고치는 일이 반복됐다(검색 칸 모양·세션 선택기 여백이 이미 갈라져 있었다).
    //    편집기 툴바 — 오른쪽 패널(이력·데이터 분석·AI 에이전트)과 **같은 가로줄에 두지 않는다**(사용자 요청 2026-09-13).
    //    결과를 분리해도 같은 줄이다(2026-10-05) — 결과창이 이 창에 없으므로 「결과창 닫기」 만 뺀다.
    // 편집기 자리를 대신하는 화면(2026-10-19 사용자 요청) — 쿼리 챌린지가 **편집기+결과 전체**를 대신한다(SQL 실행계획 분석은 2026-10-26 편집기 실행계획에 합쳐 없앴다).
    // 편집기는 마운트된 채 숨긴다(내용·커서·「편집기에서 가져오기」 가 그대로 돈다). 실행이력은 오른쪽 패널로 남는다.
    // 편집기를 새 창으로 분리했으면 예전처럼 오른쪽 패널이다(편집기 칸이 이 창에 없다)
    // 데이터 분석은 SQL 실행이력처럼 오른쪽에 펼친다(2026-10-19 사용자 요청 — 편집기 자리를 대신하던 것을 되돌림)
    // 챌린지는 작업 탭이 됐다(2026-11-04) — 편집기 자리를 대신하는 화면은 이제 없다(아래 조건들이 읽는 이름만 남긴다)
    const challengeCovering = false;
    const sideRailOpen = isSidePanelOpen && (isEditorDetached || !externalActiveTab || externalActiveTab === 'history');
    const renderSideView = (view, headerControls = null, hideHeader = false) => (
        <HistoryAndChallengeTabs
            history={history}
            onHistoryClick={handleHistoryClick}
            clearHistory={clearHistoryCallback}
            deleteHistoryItem={deleteHistoryCallback}
            view={view}
            onClose={() => setIsSidePanelOpen(false)}
            externalActiveChallengeSubTab={externalActiveChallengeSubTab}
            challengeMode={challengeMode}
            challengeDraft={challengeDraft}
            onChallengeDraftConsumed={handleChallengeDraftConsumed}
            onShowChallengePlans={handleChallengePlans}
            headerControls={headerControls}
            hideHeader={hideHeader}
        />
    );
    // 「SQL 편집」 단추 — 편집기 칸을 대신하던 화면(데이터 분석·실행계획 분석·쿼리 챌린지)을 걷고 편집기로 돌아간다(2026-10-19 신고:
    // 분석 모드에서 눌러도 화면이 그대로였다). 새 창으로 분리해 둔 화면은 그대로 둔다(이미 편집기가 보인다). 실행이력 패널은 편집기 옆이라 두고
    // 닫지 않고 걷어 둔다(coverHidden) — 그 화면의 단추로 다시 돌아온다(2026-10-26 신고: 누르면 실행계획 분석이 통째로 사라졌다)
    const backToEditor = () => {
        if (challengeCovering) setCoverHidden(true);
    };
    const renderEditorToolbar = ({ popup = false } = {}) => (
        <div className="editor-action-group" style={popup
            ? { padding: '4px 50px 4px 8px', flexShrink: 0, borderBottom: '1px solid var(--color-border, #e0e0e0)', background: 'var(--color-bg-secondary, #f5f5f5)' }
            : { padding: '4px 8px 4px 6px', flexShrink: 0, background: 'var(--color-bg)', borderBottom: '1px solid var(--color-border, #e0e0e0)' }}>
            {/* **편집기 쪽** — 검색부터 창 분리까지는 편집기의 것이다. 편집기 영역이 다른 화면으로 바뀌면 이 칸 하나를
                숨겨 함께 사라지게 한다(사용자 요구 2026-10-05). 줄 배치는 그대로 두려고 display: contents */}
            <div className="editor-toolbar-editor-part">
            {/* 프로젝트 선택 — 검색 왼편(2026-10-22 사용자 요청, 예전에는 파일 탭 줄 맨 왼쪽) */}
            <ProjectPicker inline projects={projects} activeProjectId={activeProjectId}
                onSwitchProject={handleSwitchProject} onCloseProject={handleCloseProject} />
            <div className="editor-action-subgroup">
                {editorSearch.open ? (
                    <div className="editor-search-bar">
                        <FiSearch size={12} />
                        <input
                            className="editor-search-input"
                            type="text"
                            value={editorSearch.text}
                            ref={editorSearchInputRef}
                            onChange={(e) => handleEditorSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleEditorSearchNav(e.shiftKey ? 'prev' : 'next');
                                if (e.key === 'Escape') setEditorSearch(p => ({ ...p, open: false, text: '' }));
                                // 검색 창 안에서 Ctrl+F — 브라우저 찾기 대신 검색어 전체 선택
                                if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') { e.preventDefault(); e.target.select(); }
                            }}
                            placeholder={t('common:ui.searchPlaceholder')}
                            autoFocus
                        />
                        {editorSearch.text && <span className="editor-search-count">{editorSearch.matchCount > 0 ? `${editorSearch.matchIndex + 1}/${editorSearch.matchCount}` : '0'}</span>}
                        <button className="editor-search-nav" onClick={() => handleEditorSearchNav('prev')} title={t('common:ui.prev')}><FiChevronUp size={12} /></button>
                        <button className="editor-search-nav" onClick={() => handleEditorSearchNav('next')} title={t('common:ui.next')}><FiChevronDown size={12} /></button>
                        <button className="editor-search-nav" onClick={() => setEditorSearch(p => ({ ...p, open: false, text: '' }))} title={t('common:ui.closeEsc')}><FiX size={12} /></button>
                    </div>
                ) : (
                    <button className="detach-inline-btn" onClick={() => handleEditorFind()} title={t('common:ui.searchCtrlF')}>
                        <FiSearch /> {t('common:tb.search')}
                    </button>
                )}
            </div>
            <span className="editor-action-sep-v" />
            {/* 에디터 액션 아이콘 그룹 (pinned) — 기본 접힘, 앞의 단추로 펼친다 */}
            {renderActionGroup().length > 0 && (
                <button type="button" className={`editor-action-btn editor-action-fold${actionGroupOpen ? ' open' : ''}`} onClick={toggleActionGroupOpen}
                    title={actionGroupOpen ? t('common:tb.foldTools') : t('common:tb.unfoldTools')} aria-expanded={actionGroupOpen}>
                    {actionGroupOpen ? <FiChevronLeft size={14} /> : <FiMoreHorizontal size={14} />}
                </button>
            )}
            {actionGroupOpen && <span className="editor-action-fold-body">{renderActionGroup()}</span>}
            {/* 편집기 옵션 — 편집기에 작용하므로 편집 아이콘 옆(왼쪽)에 둔다(사용자 요청 2026-09-13).
                즐겨찾기한 편집 아이콘이 없으면 앞의 구분선이 이미 있으므로 하나 더 긋지 않는다. */}
            {renderActionGroup().length > 0 && <span className="editor-action-sep-v" />}
            <TxnChip session={activeTabSession} onCommit={handleCommit} onRollback={handleRollback} busy={txnBusy} />
            <EditorOptionChecks editorOptions={editorOptions} setEditorOptions={setEditorOptions} isEditorSplit={isEditorSplit} onToggleSplit={() => setIsEditorSplit(p => !p)}
                collapseMode={popup ? undefined : splitCollapseMode} onToggleCollapse={(popup || isResultDetached) ? undefined : toggleCollapseBottom}
                isEditorDetached={isEditorDetached} onToggleDetach={() => setIsEditorDetached(p => !p)} />
            {/* 세션 선택 — 이 줄의 오른쪽 끝(2026-10-22 사용자 요청, 예전에는 파일 탭 줄 오른쪽) */}
            <div className="editor-action-subgroup right-align">
                <SessionPicker
                    activeFile={openFiles.find(f => f.id === activeFileId) || null}
                    onBindSession={handleBindSessionToTab}
                />
            </div>
            </div>
            {/* 분리한 편집기 창에는 작업 탭 줄이 없으므로 앱 쪽 단추들도 이 줄에 둔다 */}
            {popup && renderToolbarRight()}
        </div>
    );

    // 앱 쪽 단추(분석모드 · AI 에이전트 · 오른쪽 패널 · 즐겨찾기한 도구 창) — 작업 탭 줄의 오른쪽(docs/ui-command-placement.md).
    // 이력·분석 패널은 SQL 편집의 것이라 누르면 SQL 편집 탭을 앞으로 부른다
    // 작업 탭이 결과 영역을 함께 쓰는가 — 결과를 새 창으로 분리했거나 그 화면을 분리했으면 아니다
    const dockResult = !!(activeDockView && activeDockView.withResult && !activeDockView.popped) && !isResultDetached;
    const challengeTitle = challengeMode === 'tuning' ? t('menu:challenge.tuning') : t('common:hct.challenge');
    const toSqlTab = (fn) => (...args) => { if (workspace && workspace.activeId !== SQL_TAB) workspace.activate(SQL_TAB); return fn(...args); };
    // 단추 줄(「SQL 편집」 오른편, 2026-10-19 사용자 요청) — **즐겨찾기거나 켜져(열려) 있는 것만** 선다.
    // 패널 단추(분석 모드 · AI 에이전트 · 실행계획 분석 · 실행이력 · 쿼리 챌린지)는 고정 배치 즐겨찾기(기본 켬, utils/actionFavorites),
    // 도구·창 단추는 메뉴 즐겨찾기. 창이 열려 있으면 그 자리에 그 화면의 탭(눌림·✕)이 선다 — title 이 창 제목과 같아야 한다(메뉴와 같은 번역 키)
    workspaceSnapRef.current = () => {
        const shownScreen = !sqlTabActive ? (activeDockView ? activeDockView.title : '')
            : (challengeCovering ? challengeTitle : t('common:ws.sqlEdit'));
        const sidePanels = [
            ...(sideRailOpen ? [t('common:hct.history')] : []),
        ];
        const r = activeQueryResultTab;
        const oc = caseListOpen ? openCaseRef.current : null;
        return {
            screen: shownScreen,
            // 튜닝 사례 창에서 고른 사례 — 에이전트가 「지금 열린 사례」 를 알게(그림 넣기 등). 내용은 싣지 않는다
            openCase: oc && oc.id ? { id: oc.id, title: oc.title || '', mine: !!oc.mine } : null,
            openScreens: [...(workspace ? workspace.views.map(v => v.title) : []), ...sidePanels],
            editor: activeFile ? { tab: activeFile.name || '', sql: typeof activeFile.content === 'string' ? activeFile.content : '' } : null,
            result: r ? {
                name: r.name || '', query: r.query || '',
                columns: Array.isArray(r.columns) ? r.columns.map(c => (c && typeof c === 'object' ? (c.name || '') : String(c || ''))) : [],
                rowCount: Array.isArray(r.data) ? r.data.length : null,
                hasMore: !!r.hasMore, executing: !!r.isExecuting, error: r.error ? String(r.error) : '',
            } : null,
        };
    };
    const topButtons = () => {
        const fav = (key) => isFavoriteAction(key);
        const btn = (key, label, icon, onClick, active) => (
            <button key={key} type="button" className={`detach-inline-btn${active ? ' active' : ''}`} onClick={onClick} title={label} aria-pressed={active ? true : undefined}>
                {icon} {label}
            </button>
        );
        const list = [];
        // 눌린 모양은 **지금 보이는 화면**에만(2026-10-19 신고 — 분석 모드를 눌러도 앞 화면 단추가 눌린 채였다).
        // 편집기 자리의 화면은 SQL 편집 탭이 앞일 때만 보인다(새 창으로 분리해 둔 것은 그 창에 보이므로 눌린 채)
        const shown = (popKey) => sqlTabActive || !!poppedPane[popKey];
        // 켜져 있는데 다른 탭에 가려 안 보이는 화면을 누르면 끄지 말고 보여 준다(SQL 편집 탭을 앞으로)
        const showOrToggle = (isOnNow, fn) => (...args) => {
            if (isOnNow && !sqlTabActive && workspace) { workspace.activate(SQL_TAB); return undefined; }
            return toSqlTab(fn)(...args);
        };
        if (fav('view-analysis') || analysisMode) {
            list.push({ key: 'view-analysis', title: t('menu:edit.analysis'), icon: <FiPieChart />, open: analysisMode, node: btn('view-analysis', t('menu:edit.analysis'), <FiPieChart />, openDataAnalysis, false) });
        }
        [['viewhistory', 'history', 'common:hct.history', <FiClock />]].forEach(([key, view, labelKey, icon]) => {
            const on = sidePanelViewShown === view;
            // 걷어 둔 화면(coverHidden)은 열려 있되 눌린 모양이 아니다 — 누르면 다시 보인다(toggleSidePanelView)
            const covers = view === 'challenge';
            const pressed = on && shown(view) && !(covers && coverHidden && !poppedPane[view]);
            if (fav(key) || on) list.push({ key, open: on, node: btn(key, t(labelKey), icon, () => showOrToggle(on && !(covers && coverHidden), toggleSidePanelView)(view), pressed) });
        });
        // 쿼리챌린지 · 튜닝챌린지 — 작업 탭이다(2026-11-04). 단추의 title 이 창 제목과 같아 열리면 그 자리에 탭(✕ 포함)이 선다.
        // 쿼리챌린지 단추는 고정 배치 즐겨찾기(기본 켬)라 닫혀 있어도 오른쪽 묶음에 서고, 튜닝챌린지는 열렸을 때만 선다
        if (fav('challengejoin') || challengeOpen.query) {
            const title = t('menu:challenge.query');
            list.push({ key: 'challengejoin', title, icon: <FiAward />, open: !!challengeOpen.query, node: btn('challengejoin', title, <FiAward />, handleChallengeJoin, false) });
        }
        if (challengeOpen.tuning) {
            const title = t('menu:challenge.tuning');
            list.push({ key: 'tuningchallenge', title, icon: <FiZap />, open: true, node: btn('tuningchallenge', title, <FiZap />, handleChallengeTuning, false) });
        }
        // 도구 창(튜닝 사례 · 인덱스 설계)과 즐겨찾기한 창 메뉴 — 메뉴가 게시한 이름·아이콘으로, 실행은 메뉴와 같은 길
        const menuBtn = (key) => {
            const m = menuActions[key];
            if (!m) return;
            const icon = React.isValidElement(m.icon) ? m.icon : null;
            list.push({ key, title: m.name, icon, node: btn(key, m.name, icon, () => runMenuAction(key), false) });
        };
        if (AI_ENABLED && fav('tool-tuning-cases')) menuBtn('tool-tuning-cases');
        if (AI_ENABLED && fav('tool-index-design')) menuBtn('tool-index-design');
        pinnedActions.filter(key => key !== 'separator' && !ACTION_REGISTRY[key] && menuActions[key]?.window).forEach(menuBtn);
        return list;
    };
    const renderToolbarRight = () => (
        <EditorToolbarRight
            analysisMode={analysisMode}
            assistantOpen={assistantOpen}
            onToggleAnalysis={openDataAnalysis}
            onToggleAssistant={AI_ENABLED ? toggleAssistantPanel : undefined}
            sidePanelView={sidePanelViewShown}
            // 편집기 툴바의 「쿼리 챌린지」 는 작업 탭을 연다(2026-11-04) — 실행이력은 그대로 오른쪽 패널
            onToggleSidePanelView={(v) => (v === 'challenge' ? handleChallengeJoin() : toSqlTab(toggleSidePanelView)(v))}
            onOpenCaseList={() => setCaseListOpen(true)}
            onOpenTableAnalysis={openTableAnalysis}
            isToolVisible={(id) => expertMenus && !hiddenFixedActions.includes(id)}
            windowActions={renderActionGroup({ windows: true })}
        />
    );

    // 파일 탭 줄(편집기 위) — 프로젝트 선택·세션 선택은 위 툴바 줄로 옮겼다(2026-10-22)
    const renderFileTabsRow = () => (
        <div className="file-tabs-container" style={{ flexShrink: 0, borderBottom: '1px solid var(--color-border, #e0e0e0)' }}>
            <FileTabs
                openFiles={visibleFiles}
                activeFileId={activeFileId}
                onTabClick={handleTabClick}
                onTabClose={handleTabClose}
                onNewTab={handleNewTab}
                showNewTabButton={!hiddenFixedActions.includes('new')}
                onTabRename={handleTabRename}
                onCloseTabs={handleCloseTabs}
                onTabReorder={handleTabReorder}
                onDuplicateTab={duplicateTab}
                onReopenClosed={reopenClosedTab}
                canReopen={closedTabCount > 0}
                tabRows={[1, 2, 3].includes(editorOptions.tabRows) ? editorOptions.tabRows : 1}
                onTabRowsChange={(n) => setEditorOptions(o => ({ ...o, tabRows: n }))}
            />
        </div>
    );

    // 편집기(+ 나란히 보기의 읽기 전용 사본). key 는 자리마다 달라 자리를 옮기면 편집기를 새로 만든다(예전과 같다).
    const renderSqlEditor = (keyPrefix, cloneKeyPrefix) => (
        <SplitView isSplit={isEditorSplit} renderClone={() => currentActiveFile && (
            <SQLEditor
                key={`${cloneKeyPrefix}${currentActiveFile.id}`}
                query={currentActiveFile.content}
                setQuery={() => {}} onExecute={() => {}} onExecuteWithContext={() => {}}
                onExecutePlan={() => {}} onViewSchemaInfoByEditor={() => {}}
                schemas={currentSchemas} {...editorConnProps} onFocus={() => {}} onBlur={() => {}} onNewTab={() => {}}
                bindVariables={{}} tableComments={tableComments} columnComments={columnComments}
                highlightRanges={cloneHighlightRanges} readOnly={true}
                columnSelection={editorOptions.columnSelection}
                autocomplete={editorOptions.autocomplete !== false}
                showComments={editorOptions.showComments !== false}
            />
        )}>
        {currentActiveFile && (
            <SQLEditor
                key={`${keyPrefix}${currentActiveFile.id}`}
                query={currentActiveFile.content}
                setQuery={updateActiveFileContent}
                readOnly={!activeFile}
                onExecute={executeSQLEditorQuery}
                onExecuteWithContext={handleExecuteCurrentWithContext}
                onExecutePlan={executeSQLEditorPlan}
                onViewSchemaInfoByEditor={handleViewSchemaInfoRequest}
                schemas={currentSchemas} {...editorConnProps}
                editorRef={sqlEditorRef}
                onFocus={handleEditorFocus}
                onBlur={handleEditorBlur}
                onNewTab={handleNewTab}
                onFind={handleEditorFind}
                watermark={editorWatermark}
                onFindNav={handleEditorSearchNav}
                bindVariables={currentActiveFile.bindVariables || {}}
                onEditBindVariables={handleEditBindVariables}
                tableComments={tableComments}
                columnComments={columnComments}
                highlightRanges={editorHighlightRanges}
                columnSelection={editorOptions.columnSelection}
                autocomplete={editorOptions.autocomplete !== false}
                showComments={editorOptions.showComments !== false}
                linkedResultTabs={linkedResultTabsForActiveEditor}
                onActivateResultTab={handleActivateResultTab}
            />
        )}
        </SplitView>
    );

    // 결과 영역 — 도구 줄 · 결과/계획/오브젝트 탭 · 상태 줄
    const renderResultArea = (style) => (
        <div className="result-and-pagination-area" style={style}>
            <div className="result-toolbar">
                <div style={{ display: 'flex', gap: 8, marginRight: 'auto' }} title={t('common:ui.resultTabFilter')}>
                    <label className="compact-view-toggle"><input type="checkbox" checked={tabKindFilter.result} onChange={e => setTabKindFilter(p => ({ ...p, result: e.target.checked }))} />{t('common:ui.tabQuery')}</label>
                    <label className="compact-view-toggle"><input type="checkbox" checked={tabKindFilter.plan} onChange={e => setTabKindFilter(p => ({ ...p, plan: e.target.checked }))} />{t('common:ui.tabPlan')}</label>
                    <label className="compact-view-toggle"><input type="checkbox" checked={tabKindFilter.tableInfo} onChange={e => setTabKindFilter(p => ({ ...p, tableInfo: e.target.checked }))} />{t('common:ui.tabObject')}</label>
                </div>
                <label className="compact-view-toggle" title={t('common:ui.compactMode')}>
                    <input
                        type="checkbox"
                        checked={editorOptions.compactGrid}
                        onChange={(e) => setEditorOptions(p => ({ ...p, compactGrid: e.target.checked }))}
                    />
                    {t('common:tb.compactView')}
                </label>
                {/* 사용자 주석 — 영역명과 같은 컬럼 오른쪽에 주석 열(설정 › 사용자 주석에서 정의·연결 매핑, 2026-10-29) */}
                <label className="compact-view-toggle" title={t('common:ui.userCommentsHint')}>
                    <input
                        type="checkbox"
                        checked={!!editorOptions.userComments}
                        onChange={(e) => setEditorOptions(p => ({ ...p, userComments: e.target.checked }))}
                    />
                    {t('common:ui.userComments')}
                </label>
                {/* 실행계획 탭의 오른쪽 「오브젝트 상세」 — 압축 보기와 같은 자리·같은 모양의 토글이다 */}
                <label className="compact-view-toggle" title={t('common:ui.planObjectDetailHint')}>
                    <input
                        type="checkbox"
                        checked={editorOptions.planObjectDetail}
                        onChange={(e) => setEditorOptions(p => ({ ...p, planObjectDetail: e.target.checked }))}
                    />
                    {t('common:ui.planObjectDetail')}
                </label>
                {/* 수직 분할 · 창 분리 — 체크박스(2026-10-05) */}
                <ResultLayoutChecks isResultSplit={isResultSplit} onToggleSplit={() => setIsResultSplit(p => !p)} isResultDetached={isResultDetached} onToggleDetach={() => setIsResultDetached(p => !p)} />
                {/* 결과 데이터 내보내기 — 화면에 로드된 행이 아니라 쿼리를 재실행해 전체 결과를 스트리밍한다 */}
                <button className="detach-inline-btn" onClick={() => setIsExportModalOpen(true)} disabled={!activeQueryResultTab} title={t('common:ui.exportCsv')}>
                    <FiDownload /> {t('common:tb.export')}
                </button>
                {/* CSV 가져오기 — 기존 테이블에 INSERT. 쓰기 작업이라 서버가 읽기전용 연결·권한 정책으로 이중 차단한다 */}
                <button className="detach-inline-btn" onClick={() => setIsImportModalOpen(true)} disabled={!activeConnectionId} title={t('common:csv.importTitle')}>
                    <FiUpload /> {t('common:tb.import')}
                </button>
            </div>
            <MainDisplayTabs
                activeMainTab={activeMainTab}
                setActiveMainTab={setActiveMainTab}
                queryResultTabs={queryResultTabs}
                activeQueryResultTabId={activeQueryResultTabId}
                setActiveQueryResultTabId={setActiveQueryResultTabId}
                onCloseQueryResultTab={handleCloseQueryResultTab}
                onPinQueryResultTab={handlePinQueryResultTab}
                tableInfoTabs={tableInfoTabs}
                activeTableInfoTabId={activeTableInfoTabId}
                setActiveTableInfoTabId={setActiveTableInfoTabId}
                onCloseTableInfoTab={handleCloseTableInfoTab}
                executionPlanTabs={executionPlanTabs}
                activeExecutionPlanTabId={activeExecutionPlanTabId}
                setActiveExecutionPlanTabId={setActiveExecutionPlanTabId}
                onCloseExecutionPlanTab={handleCloseExecutionPlanTab}
                onFetchMoreData={fetchMoreDataForTab}
                onFetchAll={handleFetchAll}
                onExportCSV={handleExportCSV}
                onStopQuery={handleStopQuery} onMonitorQuery={handleMonitorQuery} onActualPlan={handleActualPlan} onMonitorReport={handleMonitorReport} sessionsList={sessionsList}
                columnComments={columnComments}
                onPlanNodeClick={handlePlanNodeClick}
                onShowTableDDL={handleShowTableDDL}
                statsConnectionId={activePlanConnectionId}
                activePlanNodeId={activePlanNodeId}
                isResultSplit={isResultSplit}
                isCompactGrid={editorOptions.compactGrid}
                showUserComments={!!editorOptions.userComments}
                showPlanObjectDetail={editorOptions.planObjectDetail}
                onRequestObjectDetail={openPlanObjectDetail}
                onSavePlanToArchive={expertMenus ? handleSavePlanToArchive : undefined}
                onPlanToReview={expertMenus ? handlePlanToReview : undefined}
                onPlanMonitorReport={handlePlanMonitorReport}
                onExportPlanFile={handleExportPlanFile}
                // 실행계획 옵션 — 고르면 「실행」 설정에 저장되고 다음 Ctrl+E 부터 따른다(2026-10-26)
                planOptions={execSettings}
                onPlanOptionsChange={setExecSettings}
                onRerunPlan={(tab) => handleExecutePlan(tab.query)}
                onAiTunePlan={AI_ENABLED && expertMenus ? (tab) => startSqlTuningAuto({ sql: tab.query, binds: flattenEditorBinds(activeFileRef.current && activeFileRef.current.bindVariables) }) : undefined}
                gridFormat={gridFormat}
                onPasteToEditor={handlePasteToEditor}
                tabKindFilter={tabKindFilter}
            />
            <StatusLine
                executionTime={statusLineProps.executionTime}
                currentFetchTime={statusLineProps.currentFetchTime}
                clientExecutionTime={statusLineProps.clientExecutionTime}
                message={statusLineProps.message}
                rowCount={statusLineProps.rowCount}
                isLoading={statusLineProps.isLoading}
                hasMoreData={statusLineProps.hasMoreData}
            />
        </div>
    );

    return (
        <div className="app-container">
            <ToastContainer position="top-center" autoClose={3000} hideProgressBar newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme={darkMode ? 'dark' : 'light'} />
            {sessionExpired && !isAuthenticated && !IS_STANDALONE ? <SessionExpiredGate /> : null}
            {!IS_STANDALONE ? <QbaseMaintenanceBanner /> : null}
            {verifyState && (
                <VerifySessionPopover state={verifyState} onClose={() => setVerifyState(null)} />
            )}
            <Header
                onSaveFile={handleSaveFile}
                onOpenFile={handleOpenFile}
                onNewFile={handleNewTab}
                onSaveProject={handleSaveProject}
                onSaveProjectAs={handleSaveProjectAs}
                onOpenSampleProject={handleOpenSampleProject}
                currentProjectName={currentProjectName}
                onGlobalSearch={() => setShowGlobalSearch(true)}
                onExecuteAll={handleExecuteAll}
                onExecuteCurrent={handleExecuteCurrent}
                onEditorFind={handleEditorFind}
                onEditorReplace={handleEditorReplace}
                onEditorFormat={handleEditorFormat}
                onEditorUppercase={handleEditorUppercase}
                onEditorLowercase={handleEditorLowercase}
                onEditorToggleComment={handleEditorToggleComment}
                viewState={viewState}
                onToggleView={handleToggleView}
                onOpenCaseList={() => setCaseListOpen(true)}
                onOpenTableAnalysis={openTableAnalysis}
                onOpenDesignGuides={() => { setDesignGuideKind('index'); setDesignGuidesOpen(true); }}
                onOpenPlanArchive={() => setPlanArchiveOpen(true)}
                onOpenSqlTuning={() => setSqlTuningOpen(true)}
                onOpenSqlReview={() => setSqlReviewOpen(true)}
                onOpenSqlAuthor={() => setSqlAuthorOpen(true)}
                onOpenSqlQuality={() => setSqlQualityOpen(true)}
                onOpenSchemaCompare={() => setSchemaCompareOpen(true)}
                onOpenSqlCollect={() => setSqlCollectOpen(true)}
                onOpenSqlBuilder={openSqlBuilder}
                onOpenDataAnalysis={openDataAnalysis}
                onOpenOpsLog={() => setOpsLogOpen(true)}
                onOpenExperience={() => setExperienceOpen(true)}
                onExecutePlan={executeSQLEditorPlan}
                onViewExecutionHistory={handleViewExecutionHistory}
                onViewSchemaInfo={handleViewSchemaInfoRequest}
                onCommit={handleCommit}
                onRollback={handleRollback}
                isEditorFocused={isEditorFocused}
                onChallengeJoin={handleChallengeJoin}
                onChallengeTuning={handleChallengeTuning}
                onChallengeStatus={handleChallengeStatus}
                screenZoom={screenZoom}
                onZoomChange={handleZoomChange}
                onOpenConnectionModal={() => setIsConnectionModalOpen(true)}
                isFavoriteAction={isFavoriteAction}
                onToggleFavorite={handleToggleFavorite}
                appActionKeys={actionKeysRef.current}
                darkMode={darkMode}
                onToggleDarkMode={() => setDarkMode(p => !p)}
                onOpenSettings={openSettings}
                onAskAssistant={AI_ENABLED ? askAssistant : undefined}
                onOpenSqlPerfInEditor={handleOpenSqlPerfInEditor}
                activeConnectionId={activeConnectionId}
                activeConnectionName={activeConnectionName}
                activeSession={activeTabSession}
                activeSessionConnection={activeTabConnection}
                hasActiveTab={!!activeFile}
                unsavedTabCount={(openFiles || []).filter(f => f.isDirty).length}
            />
            <ExportDataModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                resultTab={activeQueryResultTab}
                connectionName={activeConnectionName}
            />
            <ImportDataModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                connectionId={activeConnectionId}
                connectionName={activeConnectionName}
            />
            <SettingsModal
                isOpen={showSettings}
                initialCategory={settingsCategory}
                onClose={() => setShowSettings(false)}
                onApplyNow={handleApplySettingsNow}
                darkMode={darkMode}
                onToggleDarkMode={() => setDarkMode(p => !p)}
                editorOptions={editorOptions}
                setEditorOptions={setEditorOptions}
                zoneZoom={zoneZoom}
                setZoneZoom={setZoneZoom}
                tabKindFilter={tabKindFilter}
                setTabKindFilter={setTabKindFilter}
                gridFormat={gridFormat}
                setGridFormat={setGridFormat}
                execSettings={execSettings}
                setExecSettings={setExecSettings}
                sqlFormat={sqlFormat}
                setSqlFormat={setSqlFormat}
                assistantSettings={assistantSettings}
                setAssistantSettings={setAssistantSettings}
                isStandalone={IS_STANDALONE}
                screenZoom={screenZoom}
                onZoomChange={handleZoomChange}
                onResetFavorites={handleResetFavorites}
                toolbarActions={Object.entries(ACTION_REGISTRY).map(([key, action]) => ({ key, label: action.label }))}
                pinnedToolbarActions={pinnedActions}
                onTogglePinnedAction={handleToggleActionPin}
            />
            <ConnectionModal
                isOpen={isConnectionModalOpen}
                onClose={() => { setIsConnectionModalOpen(false); setEditConnectionId(null); }}
                onConnected={handleConnectionEstablished}
                onSaved={handleConnectionsSaved}
                activeConnectionId={activeConnectionId}
                initialSelectedId={editConnectionId}
                apiBaseUrl={API_BASE_URL}
            />
            <BuiltinConnectionDialog
                open={!!builtinEdit}
                connection={builtinEdit}
                onClose={() => setBuiltinEdit(null)}
                onSaved={(saved) => {
                    if (!saved || !saved.id) return;
                    // 목록은 서버가 원본이다 — 고친 칸만 바꿔 넣고 스키마 트리(children)는 그대로 둔다
                    dispatch({ type: 'SET_CONNECTIONS', payload: (connectionsFromStore || []).map(c => (c.id === saved.id ? { ...c, name: saved.name, description: saved.description, readOnly: saved.readOnly, connType: saved.connType } : c)) });
                }}
            />
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelected}
                multiple
                accept={`.sql,.txt,.livesql,${PROJECT_EXTS.join(',')}`}
            />
            <GlobalSearchModal
                isOpen={showGlobalSearch}
                onClose={() => setShowGlobalSearch(false)}
                openFiles={openFiles}
                projects={projects}
                onNavigate={handleGlobalSearchNavigate}
            />
            <div className="content-area" ref={contentAreaRef}>
                <div
                    className={`schema-explorer-area${isExplorerCollapsed ? ' collapsed' : ''}`}
                    style={{ '--font-size-tree': `${zoneZoom.explorer}px` }}
                >
                    {/* 접히면 아이콘 띠만 보이고 탐색기는 **마운트된 채 숨는다**(펼친 노드·검색어 유지) */}
                    {isExplorerCollapsed && (
                        <ExplorerRail
                            onExpand={expandExplorer}
                            autoCollapsed={!explorerCollapsed && explorerAutoCollapsed}
                            onOpenConnections={hiddenFixedActions.includes('manage-connections') ? undefined : () => { setEditConnectionId(null); setIsConnectionModalOpen(true); }}
                        />
                    )}
                    <div className="schema-explorer-body" hidden={isExplorerCollapsed}>
                    <SchemaExplorer
                        onCollapse={collapseExplorer}
                        onNodeActivate={handleSchemaNodeActivate}
                        onERDClick={handleERDClick}
                        initialData={explorerTreeData}
                        onOpenConnectionModal={() => { setEditConnectionId(null); setIsConnectionModalOpen(true); }}
                        showConnectButton={!hiddenFixedActions.includes('manage-connections')}
                        onEditConnection={(connData) => { if (connData && connData.builtin) { setBuiltinEdit(connData); return; } setEditConnectionId(connData?.id || null); setIsConnectionModalOpen(true); }}
                        onCreateSession={handleCreateSession}
                        onCancelSession={handleCancelSession}
                        onCloseSession={handleCloseSession}
                        onVerifySession={handleVerifySession}
                        dbmsByType={dbmsByType}
                        onDisconnect={async (connData) => {
                            const ok = await confirm({
                                title: t('common:ui.disconnect'),
                                message: t('common:conn.disconnectConfirm', { name: connData.name }),
                                confirmText: t('common:ui.release'),
                                variant: 'danger',
                            });
                            if (ok) {
                                dispatch({ type: 'DISCONNECT_CONNECTION', payload: connData.id });
                                clearColumnCache(connData.id);
                                if (activeConnectionId === connData.id) {
                                    setActiveConnectionId(null);
                                    setActiveConnectionName('');
                                    activeConnectionIdRef.current = null;
                                    setActiveConnectionIdForSchema(null);
                                }
                                toast.info(t('common:conn.disconnected', { name: connData.name }));
                            }
                        }}
                        onReconnect={async (connData) => {
                            dispatch({ type: 'RECONNECT_CONNECTION', payload: connData.id });
                            setActiveConnectionId(connData.id);
                            setActiveConnectionName(connData.name);
                            activeConnectionIdRef.current = connData.id;
                            setActiveConnectionIdForSchema(connData.id);
                            // 실제 결과 (스키마 조회 성공 여부) 를 기다려서 toast 분기.
                            const result = await dispatch(fetchSchemasForConnection(connData.id, connData.dbType || 'oracle'));
                            if (result && result.success) {
                                toast.success(t('common:conn.reconnected', { name: connData.name, count: result.schemaCount || 0 }));
                            } else {
                                const errMsg = (result && result.error) || t('common:conn.schemaLoadFailed');
                                // backend 에 연결 정의 자체가 없으면 redux 의 stale 항목 — 사용자에게 명확히 안내.
                                if (/Connection config not found/i.test(errMsg)) {
                                    const ok = await confirm({
                                        title: t('common:conn.staleDefinition'),
                                        message: t('common:conn.staleConfirm', { name: connData.name, id: connData.id }),
                                        confirmText: t('common:ui.delete'),
                                        variant: 'danger',
                                    });
                                    if (ok) {
                                        dispatch({ type: 'REMOVE_CONNECTION', payload: connData.id });
                                        toast.info(t('common:conn.cleanedUp', { name: connData.name }));
                                    } else {
                                        toast.warning(t('common:conn.reconnectUnknown', { name: connData.name }));
                                    }
                                } else {
                                    toast.error(t('common:conn.reconnectFailed', { name: connData.name, message: errMsg }));
                                }
                            }
                        }}
                        onRefreshSchemas={async (connData) => {
                            const result = await dispatch(refreshSchemasForConnection(connData.id, connData.dbType || 'oracle'));
                            if (result && result.success) {
                                toast.success(t('common:conn.schemaRefreshed', { name: connData.name, count: result.schemaCount || 0 }));
                            } else if (result && result.error) {
                                toast.error(t('common:conn.schemaRefreshFailed', { message: result.error }));
                            }
                        }}
                        onRemoveConnection={async (connData) => {
                            const ok = await confirm({
                                title: t('common:ui.deleteConn'),
                                message: t('common:conn.deleteConfirm', { name: connData.name }),
                                confirmText: t('common:ui.delete'),
                                variant: 'danger',
                            });
                            if (ok) {
                                dispatch({ type: 'REMOVE_CONNECTION', payload: connData.id });
                                if (activeConnectionId === connData.id) {
                                    setActiveConnectionId(null);
                                    setActiveConnectionName('');
                                    activeConnectionIdRef.current = null;
                                    setActiveConnectionIdForSchema(null);
                                }
                                toast.info(t('common:conn.deleted', { name: connData.name }));
                            }
                        }}
                    />
                    {/* 책갈피 탐색기 — 편집기의 `--@ 이름` 줄. 누르면 그 탭·그 줄로(전역 검색과 같은 이동) */}
                    <BookmarkExplorer
                        groups={bookmarkGroups}
                        onJump={(g, bm) => handleGlobalSearchNavigate({ projectId: g.projectId, fileId: g.fileId, lineNumber: bm.line })}
                    />
                    </div>
                </div>
                <div className="main-work-area ws-split">
                <div className="ws-main-col">
                    {/* file-tabs-container 는 sql-editor-pane 내부, 검색/아이콘 toolbar 아래로 이동됨. */}
                    {/* 편집기 툴바 — 오른쪽 패널(이력·데이터 분석·AI 에이전트)과 **같은 가로줄에 두지 않는다.**
                        편집기 칸 안에 있을 땐 패널이 열리면 폭이 줄어 아이콘이 밀렸다(사용자 요청 2026-09-13).
                        패널은 이 줄 아래에서 펼쳐진다. 편집기를 분리하면 툴바는 새 창(DetachablePanel)에 있으므로 여기 그리지 않는다.
                        **결과를 분리해도 같은 줄이다**(2026-10-05) — 예전엔 축소판 줄을 따로 그려 검색·편집 아이콘·컬럼 선택·주석 보기가
                        통째로 사라졌다. 결과창이 이 창에 없으므로 「결과창 닫기」 만 뺀다. */}
                    {/* 작업 탭 줄 — 「SQL 편집」 + 메뉴에서 연 화면들(2026-10-19) · 오른쪽은 앱 쪽 단추 */}
                    {/* 단추 줄 — 「SQL 편집」 이 늘 왼쪽 끝, 오른편으로 즐겨찾기거나 열려 있는 것만(2026-10-19) */}
                    <div className="ws-topbar">
                        {/* AI 에이전트 단추는 늘 맨 오른쪽 끝(2026-10-19) — 즐겨찾기거나 패널이 열려 있을 때 */}
                        <WorkspaceTabs buttons={topButtons()} onSqlEdit={backToEditor} sqlEditShown={!challengeCovering} trailing={AI_ENABLED && (isFavoriteAction('view-assistant') || assistantOpen) ? (
                            <button type="button" className={`detach-inline-btn${assistantOpen ? ' active' : ''}`} onClick={toggleAssistantPanel}
                                title={t('common:ui.assistantToggle')} aria-pressed={assistantOpen}>
                                <FiMessageSquare /> {t('common:tb.assistant')}
                                {/* 접힌 동안의 상태 — 일하는 중(깜빡임) · 새 답(점) */}
                                {!assistantOpen && agentLive.busy ? <span className="ws-agent-dot busy" title={t('common:ws.agentBusy')} aria-label={t('common:ws.agentBusy')} /> : null}
                                {!assistantOpen && !agentLive.busy && agentUnread ? <span className="ws-agent-dot unread" title={t('common:ws.agentUnread')} aria-label={t('common:ws.agentUnread')} /> : null}
                                {!assistantOpen && observations.unseen > 0 ? <span className="ws-agent-obs" title={t('common:ws.agentObs', { n: observations.unseen })}>{observations.unseen > 99 ? '99+' : observations.unseen}</span> : null}
                            </button>
                        ) : null} />
                    </div>
                    {/* 편집기 툴바 — SQL 편집 탭의 것이다 */}
                    {/* 편집기를 다른 화면이 대신하고 있으면(데이터 분석·실행계획 분석·쿼리 챌린지) 편집기 툴바도 숨긴다 */}
                    {!isEditorDetached && sqlTabActive && !challengeCovering && renderEditorToolbar()}
                    <div className={`split-pane-container${sqlTabActive ? '' : ' ws-other-active'}${challengeCovering ? ' challenge-full' : ''}`} style={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, position: 'relative' }}>
                      {/* 메뉴에서 연 화면들의 칸 — 앞에 온 탭이 SQL 편집이 아니면 편집기·패널 자리를 채운다.
                          결과 영역을 함께 쓰는 화면이면 아래에 붙인다. 칸(WorkspaceDockArea)의 자리는 늘 같아야 한다 — 바뀌면 칸이 새로 만들어진다 */}
                      {/* SQL 편집이 앞이면 크기를 지닌 채 숨긴다(.ws-dock-column-hidden, 2026-10-22) — display:none 이면 돌아올 때 그래프가 모두 다시 쟀다 */}
                      <div className={sqlTabActive ? 'ws-dock-column ws-dock-column-hidden' : 'ws-dock-column'} aria-hidden={sqlTabActive || undefined}>
                        <VerticalSplit
                            size={dockResult ? (dockSplitSize === null ? undefined : dockSplitSize) : '100%'}
                            defaultSize="60%"
                            onChange={setDockSplitSize}
                            pane1Style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}
                            pane2Style={{ display: dockResult ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden', minHeight: 0, minWidth: 0 }}
                            resizerStyle={dockResult ? undefined : { display: 'none' }}
                        >
                            <WorkspaceDockArea />
                            {dockResult ? renderResultArea(resultZoneVars) : <div />}
                        </VerticalSplit>
                      </div>
                      {/* SplitPane 이 absolute 로 ancestor 의 position:relative 까지 거슬러 채우므로,
                          좌측 wrapper 에도 position:relative 를 강제해 SplitPane 의 absolute 영역을 가둔다.
                          이게 없으면 SplitPane 이 split-pane-container 전체를 채워 우측 AnalysisPanel 을 가림. */}
                      {/* 편집기+결과 칸 — 최소 폭(App.css .editor-result-column, utils/explorerLayout EDITOR_MIN_WIDTH)을 지킨다.
                          모자라면 패널 쪽이 줄어든다(패널의 flex-shrink/min-width) — 예전엔 이 칸이 0 까지 눌렸다 */}
                      <div className="editor-result-column">
                        {/* 에디터 팝아웃 패널 (탭 포함) */}
                        <DetachablePanel
                            title={t('common:ui.sqlEditor')}
                            isDetached={isEditorDetached}
                            onDetach={() => setIsEditorDetached(true)}
                            onAttach={() => setIsEditorDetached(false)}
                            defaultSize={{ width: 1000, height: 600 }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                {renderEditorToolbar({ popup: true })}
                                {renderFileTabsRow()}
                                <div className="sql-editor-pane" style={{ ...editorZoneVars, flex: 1 }}>
                                    {renderSqlEditor('popup_', 'clone_popup_')}
                                </div>
                            </div>
                        </DetachablePanel>

                        {/* 결과 팝아웃 패널 */}
                        <DetachablePanel
                            title={t('common:ui.queryResult')}
                            isDetached={isResultDetached}
                            onDetach={() => setIsResultDetached(true)}
                            onAttach={() => setIsResultDetached(false)}
                            defaultSize={{ width: 1000, height: 600 }}
                        >
                            {renderResultArea({ ...resultZoneVars, height: '100%', display: 'flex', flexDirection: 'column' })}
                        </DetachablePanel>

                        {/* 에디터/결과 중 하나라도 분리되면 SplitPane 대신 flex 레이아웃 */}
                        {(isEditorDetached || isResultDetached) ? (
                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                {isEditorDetached ? (
                                    <div className="detach-rejoin-bar" onClick={() => setIsEditorDetached(false)}>
                                        <FiExternalLink /> {t('common:tb.editorDetached')}
                                    </div>
                                ) : (
                                    <div className="sql-editor-pane" style={{ ...editorZoneVars, flex: isResultDetached ? 1 : '0 0 60%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                        {renderFileTabsRow()}
                                        {renderSqlEditor('', 'clone_')}
                                    </div>
                                )}
                                {isResultDetached ? (
                                    <div className="detach-rejoin-bar" onClick={() => setIsResultDetached(false)}>
                                        <FiExternalLink /> {t('common:tb.resultDetached')}
                                    </div>
                                ) : (
                                    renderResultArea({ ...resultZoneVars, flex: isEditorDetached ? 1 : '0 0 40%', display: 'flex', flexDirection: 'column', minHeight: 0 })
                                )}
                            </div>
                        ) : (
                            <VerticalSplit
                                size={
                                    splitCollapseMode === 'bottom' ? '100%'
                                    : (splitSize === null ? undefined : splitSize)
                                }
                                defaultSize={editorHeight}
                                onChange={handleEditorResize}
                                pane1Style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}
                                pane2Style={{
                                    display: splitCollapseMode === 'bottom' ? 'none' : 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                    minHeight: 0,
                                    minWidth: 0,    /* 결과 그리드의 가로 스크롤 동작 보장 — 자식이 부모 폭을 뚫지 않도록 */
                                }}
                                resizerStyle={
                                    splitCollapseMode === 'bottom'
                                        ? { display: 'none' }
                                        : undefined
                                }
                            >
                                <div className="sql-editor-pane" style={editorZoneVars}>
                                    {renderFileTabsRow()}
                                    {renderSqlEditor('', 'clone2_')}
                                </div>
                                {dockResult ? <div /> : renderResultArea(resultZoneVars)}
                            </VerticalSplit>
                        )}
                      </div>
                      {/* 이력·챌린지·튜닝 — **AI 에이전트 왼쪽**. 편집기와 우측 레일 사이에 온다.
                          **조건부 렌더를 쓰지 않는다** — DOM 에서 사라지면 transition 이 돌 틈이 없어
                          그냥 툭 사라진다. 늘 그려 두고 폭만 접는다. 기본 화면(이력)은 props 로 받은
                          목록을 그릴 뿐이라 닫혀 있어도 비용이 없다(챌린지는 열 때 마운트된다).

                          **여닫는 길은 둘뿐이다**(사용자 요청 2026-09-20): 편집기 툴바의 세 버튼으로
                          열고, 제목줄의 ✕ 로 닫는다. 예전에는 패널 가장자리에 펼치기/접기 화살표가
                          하나 더 있었는데, 버튼이 무엇을 여는지 말해 주는 지금은 "여는 곳"이 둘이라
                          헷갈리기만 했다. */}
                      <div className={`side-panel-area ${sideRailOpen ? 'open' : 'closed'}${sideRailOpen && externalActiveTab && externalActiveTab !== 'history' ? ' wide' : ''}`}>
                          {/* 안쪽 폭을 고정한다 — 접힐 때 내용이 다시 배치되면 글자가 출렁인다. 클립만 시킨다.
                              편집기 칸을 대신하는 화면(실행계획 분석·챌린지)은 여기 그리지 않는다 — 두 곳에 마운트되면 챌린지가 두 벌 돈다 */}
                          <div className="side-panel-inner">
                              {renderSideView(sideRailOpen ? (externalActiveTab || 'history') : 'history')}
                          </div>
                      </div>
                    </div>
                </div>
                {/* AI 에이전트 칸 — 단추 줄·편집기 툴바 높이까지 올라온다(2026-10-19 사용자 요청).
                    폭은 패널이 정한다(패널의 여백 미닫이가 그대로 칸을 열고 닫는다) */}
                {AI_ENABLED && (
                <div className={`ws-agent-col${assistantOpen ? ' open' : ''}`}>
                      {/* AI 에이전트 — 같은 우측 레일. 자연어 → 되묻기 → 검증된 SQL.
                          LLM 호출은 사용자 본인 계정(스탠드얼론=PC 로그인, 웹=개인 토큰)으로 나간다. */}
                      <AssistantPanel
                        open={assistantOpen}
                        onClose={() => setAssistantOpen(false)}
                        settings={assistantSettings}
                        onSkillLevelChange={(skillLevel) => setAssistantSettings(s => ({ ...s, skillLevel }))}
                        onTuningLevelChange={(tuningLevel) => setAssistantSettings(s => ({ ...s, tuningLevel }))}
                        prefill={assistantPrefill}
                        onQuickAction={handleQuickAction}
                        activeContext={assistantContext}
                        onInsertSql={handleInsertAssistantSql}
                        onRunSql={handleExecute}
                        onExplainSql={handleExecutePlan}
                        onOpenSettings={openSettings}
                        onOpenCaseDraft={handleOpenCaseDraft}
                        onOpenChallengeDraft={handleOpenChallengeDraft}
                        onSqlTuningEvent={handleSqlTuningEvent}
                        onOpenScreen={handleOpenScreen}
                        onAgentPlanCapture={handleAgentPlanCapture}
                        onAgentPlanCompare={handleAgentPlanCompare}
                        onAgentTurnStart={handleAgentTurnStart}
                      />
                </div>
                )}
                </div>
            </div>
            {/* AI 에이전트 도구 창 3종 — 튜닝 사례·테이블 분석은 편집기 도구모음, 사용·에러 로그는 메뉴 도움말 › 에서 연다. 패널이 닫혀 있어도 열린다.
                LLM 을 부르는 두 창에는 **패널 배지와 같은 제공자**를 넘긴다(과금 귀속이 갈리면 안 된다) —
                배지의 account.provider 도 이 설정값을 그대로 되돌려 받은 것이다. */}
            <OpsLogModal open={opsLogOpen} onClose={() => setOpsLogOpen(false)} />
            {/* 제안의 「열기」 — 기준을 바꾸는 것은 각 편집 화면에서 사람이 한다. 창이 겹치지 않게 진화 기록은 닫는다 */}
            <ExecMonitorModal open={!!execMonitorTarget} target={execMonitorTarget} onClose={() => setExecMonitorTarget(null)} />
            <SqlMonitorReportModal open={!!monitorReportData} data={monitorReportData} onClose={() => setMonitorReportData(null)} />
            <ExperienceModal open={experienceOpen} onClose={() => setExperienceOpen(false)}
                onOpenSqlTuningJob={(jobId) => { setExperienceOpen(false); setSqlTuningFocus({ jobId: String(jobId), step: 'report', open: true, nonce: Date.now() }); }}
                onOpenTuningGuides={AI_ENABLED ? () => { setExperienceOpen(false); openTuningGuides(); } : undefined}
                onOpenCases={AI_ENABLED ? () => { setExperienceOpen(false); setCaseListOpen(true); } : undefined} />

            {/* 튜닝 사례·테이블 분석은 AI 에이전트 기능이다 — AI 제외 빌드에는 없다(config/features.js) */}
            {AI_ENABLED && (
                <TableAnalysisModal
                    open={!!tableAnalysisMode}
                    mode={tableAnalysisMode || 'index'}
                    connectionId={assistantContext.connectionId}
                    preset={tableAnalysisPreset}
                    provider={assistantSettings.provider || undefined}
                    onClose={() => setTableAnalysisMode(null)}
                />
            )}
            {/* AI 가드레일 — 확인 전·결정 필요로 멈춘 AI 요청을 받아 창을 띄우고 다시 보낸다, 메뉴 도움말 › AI 가드레일 */}
            <GuardrailHost connections={connectionsFromStore} />

            {/* 보관함은 **AI 제외 빌드에도 있다** — 실행계획 저장은 AI 와 무관한 기능이다.
                (아래 사례 창과 달리 AI_ENABLED 밖에 둔다) */}
            {/* 쿼리챌린지 · 튜닝챌린지 — 각자의 작업 탭(2026-11-04). 초안은 그 종류의 탭에만 건넨다 */}
            {['query', 'tuning'].map(kind => (
                <ChallengeWindow key={kind} mode={kind} open={!!challengeOpen[kind]} onClose={() => closeChallengeTab(kind)}
                    draft={challengeDraft && ((challengeDraft.kind === 'tuning') === (kind === 'tuning')) ? challengeDraft : null}
                    onDraftConsumed={handleChallengeDraftConsumed} onShowPlans={handleChallengePlans} />
            ))}
            <SqlTuningModal
                open={sqlTuningOpen}
                focus={sqlTuningFocus}
                restore={savedLayout.windows.sqlTuning}
                onView={onSqlTuningView}
                onClose={() => setSqlTuningOpen(false)}
                connections={connectionsFromStore}
                defaultConnectionId={editorConnection ? editorConnection.id : undefined}
                getEditorSql={getEditorSqlForTuning}
                onOpenPlans={handleChallengePlans}
                aiEnabled={AI_ENABLED}
                aiProvider={assistantSettings.provider || undefined}
                onAskAssistant={AI_ENABLED ? askAssistant : undefined}
                // 튜닝 결과를 사례로 — 사례 등록 화면은 AI 빌드에만 있다(보관함의 「튜닝 사례로 등록」 과 같은 규칙)
                onRegisterCase={AI_ENABLED ? (seed) => handleOpenCaseDraft({ ...seed, nonce: Date.now() }) : undefined}
                onOpenGuides={AI_ENABLED ? openTuningGuides : undefined}
            />
            <SqlReviewModal
                open={sqlReviewOpen}
                focus={sqlReviewFocus}
                restore={savedLayout.windows.sqlReview}
                onView={onSqlReviewView}
                onClose={() => setSqlReviewOpen(false)}
                aiEnabled={AI_ENABLED}
                aiProvider={assistantSettings.provider || undefined}
                seed={sqlReviewSeed}
                onSeedUsed={() => setSqlReviewSeed(null)}
                onOpenGuides={AI_ENABLED ? openTuningGuides : undefined}
                // 결과를 사례로 — 사례 등록 화면은 AI 빌드에만 있다. 검증되지 않았으므로 초안으로 넘어간다(서버 caseSeed)
                onRegisterCase={AI_ENABLED ? (seed) => handleOpenCaseDraft({ ...seed, nonce: Date.now() }) : undefined}
            />
            <SqlAuthorModal
                open={sqlAuthorOpen}
                restore={savedLayout.windows.sqlAuthor}
                onView={onSqlAuthorView}
                onClose={() => setSqlAuthorOpen(false)}
                connections={connectionsFromStore}
                defaultConnectionId={sqlTabConnProps.connectionId || undefined}
                getEditorSql={getEditorSqlForTuning}
                aiEnabled={AI_ENABLED}
                aiProvider={assistantSettings.provider || undefined}
                // 빌더로 넘긴 SQL 은 편집기의 어느 자리에서 온 것이 아니다 — 「편집기에 반영」 은 커서 위치에 넣는다
                onOpenBuilder={(sql) => { sqlBuilderOrigin.current = null; setSqlBuilderSeed({ sql, nonce: Date.now() }); setSqlBuilderOpen(true); }}
                onOpenInEditor={openSqlBuilderInNewTab}
                onRunInResult={runSqlInResult}
                focus={sqlAuthorFocus}
            />
            <SqlQualityModal
                open={sqlQualityOpen}
                focus={sqlQualityFocus}
                restore={savedLayout.windows.sqlQuality}
                onView={onSqlQualityView}
                onClose={() => setSqlQualityOpen(false)}
                connections={connectionsFromStore}
                defaultConnectionId={editorConnection ? editorConnection.id : undefined}
                aiEnabled={AI_ENABLED}
                aiProvider={assistantSettings.provider || undefined}
                onOpenGuides={AI_ENABLED ? openStandardGuides : undefined}
                onOpenInEditor={openSqlBuilderInNewTab}
                onOpenBuilder={(sql) => { sqlBuilderOrigin.current = null; setSqlBuilderSeed({ sql, nonce: Date.now() }); setSqlBuilderOpen(true); }}
            />
            <SqlCollectModal
                open={sqlCollectOpen}
                restore={savedLayout.windows.sqlCollect}
                onView={onSqlCollectView}
                onClose={() => setSqlCollectOpen(false)}
                connections={connectionsFromStore}
                defaultConnectionId={editorConnection ? editorConnection.id : undefined}
                onOpenInEditor={openSqlBuilderInNewTab}
            />
            <SchemaCompareModal
                open={schemaCompareOpen}
                restore={savedLayout.windows.schemaCompare}
                onView={onSchemaCompareView}
                onClose={() => setSchemaCompareOpen(false)}
                connections={connectionsFromStore}
                defaultConnectionId={editorConnection ? editorConnection.id : undefined}
                onOpenInEditor={openSqlBuilderInNewTab}
            />
            <DataAnalysisModal
                open={!!analysisMode}
                onClose={() => setAnalysisMode(false)}
                connections={connectionsFromStore}
                connectionId={sqlTabConnProps.connectionId}
                dbType={sqlTabConnProps.dbType}
                resultSource={analysisResultSource}
                onInsert={insertAnalysisSql}
                onNewTab={openSqlBuilderInNewTab}
            />
            <SqlBuilderModal
                open={sqlBuilderOpen}
                onClose={() => setSqlBuilderOpen(false)}
                seed={sqlBuilderSeed}
                onSeedUsed={() => setSqlBuilderSeed(null)}
                onImport={readEditorForBuilder}
                onApply={applySqlBuilder}
                onNewTab={openSqlBuilderInNewTab}
                schemas={currentSchemas}
                connections={connectionsFromStore}
                connectionId={sqlTabConnProps.connectionId}
                dbType={sqlTabConnProps.dbType}
            />
            <PlanArchiveModal
                open={planArchiveOpen}
                onClose={() => setPlanArchiveOpen(false)}
                onOpenInTab={handleOpenArchivedPlan}
                {...(AI_ENABLED ? { onRegisterCase: handleRegisterPlanCase } : {})}
            />

            {AI_ENABLED && (
                <TuningCaseModal
                    open={caseListOpen}
                    restore={savedLayout.windows.cases}
                    onView={onCasesView}
                    seed={caseSeed}
                    onSeedUsed={() => setCaseSeed(null)}
                    onSelectedChange={handleCaseSelected}
                    onCaseSaved={handleCaseSavedFromSeed}
                    onClose={() => setCaseListOpen(false)}
                    onOpenGuides={openTuningGuides}
                    provider={assistantSettings.provider || undefined}
                />
            )}

            {/* 사례·SQL 튜닝·진단 리포트 창에서도 열리므로 **그 창들 뒤에** 둔다(z-index 가 같아 나중에 그린 것이 위다).
                설계 가이드 관리(메뉴 DB 전문가 › 오라클 전문가 › 설계 가이드) — 설계 창 안의 「가이드 관리」 와 같은 창 */}
            {AI_ENABLED && (
                <DesignGuideModal
                    open={designGuidesOpen}
                    initialKind={designGuideKind}
                    restore={savedLayout.windows.guides}
                    onView={onGuidesView}
                    provider={assistantSettings.provider || undefined}
                    onClose={() => setDesignGuidesOpen(false)}
                />
            )}

            <BindVariablesModal
                isOpen={isBindModalOpen}
                statements={bindModalData.statements}
                onSubmit={handleBindModalSubmit}
                onClose={handleBindModalClose}
                isSpecificStatement={!!bindModalData.isSpecificStatement}
                specificStatementNumber={bindModalData.specificStatementNumber || null}
                editOnly={!!bindModalData.editOnly}
            />
            
            {/* 패스워드 변경 모달 */}
            <PasswordChangeModal
                isOpen={showPasswordChangeModal}
                onClose={() => setShowPasswordChangeModal(false)}
            />

            {/* ERD 모달 */}
            <ERDModal
                isOpen={isERDModalOpen}
                onClose={handleERDModalClose}
                schemaName={selectedERDSchema}
                connectionData={currentConnectionData}
            />
            <footer className="app-footer">
                {/* 제품 이름(2026-11-04) — AI 가 빠진 판(스탠드얼론 noai)에는 「with AI Agent」 를 붙이지 않는다 */}
                <span>{AI_ENABLED ? 'LiveSQL with AI Agent' : 'LiveSQL'} v{buildInfo.version}</span>
                <span className="app-footer-sep">|</span>
                <span>MIT License</span>
                <span className="app-footer-sep">|</span>
                <span>Build: {buildInfo.buildTime}</span>
            </footer>
        </div>
    );
};

export default App; 